import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { FontFamily } from '@tiptap/extension-font-family';
import { Node as TiptapNode, Extension, mergeAttributes } from '@tiptap/core';
import TextAlign from '@tiptap/extension-text-align';
import { Plugin, PluginKey, TextSelection } from 'prosemirror-state';
import { splitBlock, createParagraphNear } from 'prosemirror-commands';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { Slice, Fragment } from 'prosemirror-model';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import { HardBreak } from '@tiptap/extension-hard-break';
import SmartVariableExtension, { findVariableDecorationAtPos } from './SmartVariableExtension';
import GenderAgreementExtension from './GenderAgreementExtension';
import FontSizeExtension from './FontSizeExtension';
import TabExtension from './TabExtension';
import PageBreakExtension from './PageBreakExtension';
import PageSpacerExtension from './PageSpacerExtension';
import ParagraphSpacingExtension from './ParagraphSpacingExtension';
import LineHeightExtension from './LineHeightExtension';
import BackgroundPagesOverlay from './BackgroundPagesOverlay';
// import AutoPaginationExtension from './AutoPaginationExtension'; // ОТКЛЮЧЕНО: используем упрощенный алгоритм
// Убираем визуализацию страниц в режимах редактирования
import mammoth from 'mammoth';
import { processDocxTables } from '../../utils/tableProcessor';
import { processDocxStyles } from '../../utils/docxStyleProcessor';
import { processAdvancedDocx } from '../../utils/advancedDocxProcessor';
import { apiService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import '../Common/CardActions.css';
import './TiptapEditor.css';

const TableCellWithVerticalAlign = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      verticalAlign: {
        default: 'top',
        parseHTML: element => element.style?.verticalAlign || 'top',
        renderHTML: attributes => ({
          style: `vertical-align: ${attributes.verticalAlign || 'top'};`
        })
      }
    };
  }
});

const TableWithDisplaySettings = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      tableBorderWidth: {
        default: null,
        parseHTML: (element) => {
          const value = element?.style?.getPropertyValue('--table-border-width') || '';
          return String(value || '').trim() || null;
        },
      },
      tableRowMinHeight: {
        default: null,
        parseHTML: (element) => {
          const value = element?.style?.getPropertyValue('--table-row-min-height') || '';
          return String(value || '').trim() || null;
        },
      },
      tableCellPadding: {
        default: null,
        parseHTML: (element) => {
          const value = element?.style?.getPropertyValue('--table-cell-padding') || '';
          return String(value || '').trim() || null;
        },
      },
      tableMarginVert: {
        default: null,
        parseHTML: (element) => {
          const value = element?.style?.getPropertyValue('--table-margin-vert') || '';
          return String(value || '').trim() || null;
        },
      },
    };
  },
  renderHTML({ HTMLAttributes }) {
    const attrs = { ...(HTMLAttributes || {}) };
    const borderWidth = String(attrs.tableBorderWidth || '').trim();
    const rowMinHeight = String(attrs.tableRowMinHeight || '').trim();
    const cellPadding = String(attrs.tableCellPadding || '').trim();
    const tableMarginVert = String(attrs.tableMarginVert || '').trim();
    delete attrs.tableBorderWidth;
    delete attrs.tableRowMinHeight;
    delete attrs.tableCellPadding;
    delete attrs.tableMarginVert;

    const existingStyle = String(attrs.style || '').trim().replace(/;+\s*$/, '');
    const styleParts = [];
    if (existingStyle) styleParts.push(existingStyle);
    if (borderWidth) styleParts.push(`--table-border-width: ${borderWidth}`);
    if (rowMinHeight) styleParts.push(`--table-row-min-height: ${rowMinHeight}`);
    if (cellPadding) styleParts.push(`--table-cell-padding: ${cellPadding}`);
    if (tableMarginVert) styleParts.push(`--table-margin-vert: ${tableMarginVert}`);
    if (styleParts.length > 0) {
      attrs.style = `${styleParts.join('; ')};`;
    }

    return ['table', mergeAttributes(this.options.HTMLAttributes, attrs), ['tbody', 0]];
  },
});

// Диагностика/версионирование фронта для локальных проверок (в т.ч. проблем со скроллом в модалке).
// Позволяет убедиться, что в браузере действительно подхватилась актуальная сборка.
if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-underscore-dangle
  window.__AOT_TIPTAP_EDITOR_BUILD__ = '2026-01-12T00:00:00Z+6205a8f';
}

const MARGINS_TAG_REGEX = /<div[^>]*data-a4-margins=(['"])([\s\S]*?)\1[^>]*><\/div>/i;

const encodeMarginsTag = (m) => `<div data-a4-margins='${JSON.stringify(m)}'></div>`;

const extractMargins = (html) => {
  if (!html) return null;
  const match = html.match(MARGINS_TAG_REGEX);
  if (!match) return null;
  try { return JSON.parse(match[2]); } catch (_) { return null; }
};

const stripMarginsTag = (html) => (html || '').replace(MARGINS_TAG_REGEX, '');

const PX_PER_MM = 96 / 25.4;
const MAX_MARGIN_MM = 60;
const mmToPx = (mm) => {
  const n = Number(mm);
  if (!Number.isFinite(n)) return 0;
  return Math.round(Math.max(0, n) * PX_PER_MM);
};
const pxToMm = (px) => {
  const n = Number(px);
  if (!Number.isFinite(n)) return 0;
  return Number((Math.max(0, n) / PX_PER_MM).toFixed(1));
};
const formatMmInput = (px) => String(pxToMm(px)).replace(/\.0$/, '');
const parseMmInput = (raw) => {
  const normalized = String(raw ?? '').trim().replace(',', '.');
  if (!normalized) return null;
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  return Math.min(MAX_MARGIN_MM, Math.max(0, value));
};
const DEFAULT_MARGINS_MM = { top: 20, right: 15, bottom: 20, left: 30 };
const DEFAULT_MARGINS_PX = {
  top: mmToPx(DEFAULT_MARGINS_MM.top),
  right: mmToPx(DEFAULT_MARGINS_MM.right),
  bottom: mmToPx(DEFAULT_MARGINS_MM.bottom),
  left: mmToPx(DEFAULT_MARGINS_MM.left),
};
const normalizeMargins = (margins, fallback = DEFAULT_MARGINS_PX) => {
  const keys = ['top', 'right', 'bottom', 'left'];
  const src = margins && typeof margins === 'object' ? margins : {};
  return keys.reduce((acc, key) => {
    const value = Number(src[key]);
    acc[key] = Number.isFinite(value) && value >= 0 ? Math.round(value) : fallback[key];
    return acc;
  }, {});
};
const hasValidMargins = (margins) => {
  if (!margins || typeof margins !== 'object') return false;
  return ['top', 'right', 'bottom', 'left'].every((key) => {
    const value = Number(margins[key]);
    return Number.isFinite(value) && value >= 0;
  });
};
const areMarginsEqual = (a, b) => (
  Number(a?.top) === Number(b?.top)
  && Number(a?.right) === Number(b?.right)
  && Number(a?.bottom) === Number(b?.bottom)
  && Number(a?.left) === Number(b?.left)
);

// Узлы для условных блоков (визуализация без показа [[if]])
const conditionalMarkersPluginKey = new PluginKey('conditionalMarkers');
const newlineMarkersPluginKey = new PluginKey('newlineMarkers');

const isWhitespaceOnly = (text) => String(text || '').replace(/[\s\u00A0\u200B\uFEFF]/g, '') === '';

const isVisuallyEmptyParagraphNode = (node) => {
  if (!node || node.type?.name !== 'paragraph') return false;
  if (!node.content || node.content.childCount === 0) return true;

  for (let i = 0; i < node.content.childCount; i += 1) {
    const child = node.content.child(i);
    if (!child) continue;

    if (child.isText) {
      if (!isWhitespaceOnly(child.text || '')) return false;
      continue;
    }

    if (child.type?.name === 'hardBreak') continue;
    return false;
  }

  return true;
};

const buildNewlineMarkers = (doc) => {
  const decorations = [];
  doc.descendants((node, pos) => {
    if (isVisuallyEmptyParagraphNode(node)) {
      decorations.push(
        Decoration.node(pos, pos + node.nodeSize, {
          class: 'show-hidden-newline-paragraph',
        })
      );
      decorations.push(
        Decoration.widget(
          pos + 1,
          () => {
            const span = document.createElement('span');
            span.className = 'invisible-character newline-paragraph-character';
            span.textContent = '↵';
            span.setAttribute('aria-hidden', 'true');
            return span;
          },
          { side: -1, ignoreSelection: true }
        )
      );
    }

    if (!node?.isText || !node.text || node.text.indexOf('\n') === -1) return;
    const text = node.text;
    for (let i = 0; i < text.length; i += 1) {
      if (text[i] !== '\n') continue;
      decorations.push(
        Decoration.widget(
          pos + i + 1,
          () => {
            const span = document.createElement('span');
            span.className = 'invisible-character newline-character';
            span.textContent = '↵';
            span.setAttribute('aria-hidden', 'true');
            return span;
          },
          { side: -1, ignoreSelection: true }
        )
      );
    }
  });
  return DecorationSet.create(doc, decorations);
};

const NewlineMarkersExtension = Extension.create({
  name: 'newlineMarkers',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: newlineMarkersPluginKey,
        state: {
          init: (_, state) => buildNewlineMarkers(state.doc),
          apply: (tr) => buildNewlineMarkers(tr.doc),
        },
        props: {
          decorations(state) {
            return newlineMarkersPluginKey.getState(state) || DecorationSet.empty;
          },
        },
      }),
    ];
  },
});

const stripConditionalsFromFragment = (fragment) => {
  const out = [];
  fragment.forEach((child) => {
    const typeName = child?.type?.name;
    if (typeName === 'conditionalBlock' || typeName === 'conditionalSection') {
      const inner = stripConditionalsFromFragment(child.content || Fragment.empty);
      inner.forEach((n) => out.push(n));
      return;
    }

    if (!child.content || child.content.size === 0) {
      out.push(child);
      return;
    }

    const next = stripConditionalsFromFragment(child.content);
    out.push(child.copy(next));
  });
  return Fragment.fromArray(out);
};

const stripConditionalsFromSlice = (slice) => {
  if (!slice) return slice;
  try {
    const next = stripConditionalsFromFragment(slice.content || Fragment.empty);
    return Slice.maxOpen(next, true);
  } catch (_) {
    return slice;
  }
};

// Для inline IF защищаем пробелы на краях выделенного фрагмента:
// обычные пробелы временно превращаем в NBSP, чтобы они не терялись после save/reopen.
const protectFragmentEdgeSpaces = (fragment, schema) => {
  if (!fragment || !schema?.text) return fragment;
  const children = [];
  fragment.forEach((child) => children.push(child));
  if (!children.length) return fragment;

  let firstTextIdx = -1;
  let lastTextIdx = -1;
  for (let i = 0; i < children.length; i += 1) {
    if (children[i]?.isText) {
      firstTextIdx = i;
      break;
    }
  }
  for (let i = children.length - 1; i >= 0; i -= 1) {
    if (children[i]?.isText) {
      lastTextIdx = i;
      break;
    }
  }
  if (firstTextIdx === -1) return fragment;

  const convertLeading = (text) => String(text || '').replace(/^ +/, (m) => '\u00A0'.repeat(m.length));
  const convertTrailing = (text) => String(text || '').replace(/ +$/, (m) => '\u00A0'.repeat(m.length));

  const patchTextNode = (idx, patchFn) => {
    const node = children[idx];
    if (!node?.isText) return;
    const nextText = patchFn(String(node.text || ''));
    if (nextText === String(node.text || '')) return;
    children[idx] = schema.text(nextText, node.marks || []);
  };

  patchTextNode(firstTextIdx, convertLeading);
  patchTextNode(lastTextIdx, convertTrailing);

  return Fragment.fromArray(children);
};

const buildConditionalDecorations = (doc, hoveredGroupId, selectedGroupId, previewRange, blockedRange) => {
  const decorations = [];

  doc.descendants((node, pos) => {
    if (node.type.name !== 'conditionalBlock' && node.type.name !== 'conditionalSection') return;

    const from = pos;
    const to = pos + node.nodeSize;
    const effectiveGroupId = String(node.attrs?.groupId || `__pos_${pos}`);

    // Базовая подсветка блока — всегда видна (не влияет на форматирование).
    decorations.push(Decoration.node(from, to, { class: 'conditional-block--always' }));

    // Подсветка выбранного блока (клик по бейджу/выбор в списке)
    // Если есть previewRange (перетаскивание границ) — подсветку рисуем по диапазону, а не по узлам.
    if (!previewRange && selectedGroupId && effectiveGroupId === String(selectedGroupId)) {
      decorations.push(Decoration.node(from, to, { class: 'conditional-block--selected' }));
    }

    // Подсветка диапазона по hover — без постоянного оверлея и без смещения текста
    if (hoveredGroupId && effectiveGroupId === String(hoveredGroupId)) {
      decorations.push(Decoration.node(from, to, { class: 'conditional-block--hover' }));
    }
  });

  if (previewRange && typeof previewRange.from === 'number' && typeof previewRange.to === 'number') {
    const a = Math.min(previewRange.from, previewRange.to);
    const b = Math.max(previewRange.from, previewRange.to);
    if (a < b) {
      // Во время ресайза границ подсвечиваем именно диапазон.
      // ВАЖНО: отдельный класс, т.к. .conditional-block--selected стилизуется как class на узле.
      decorations.push(Decoration.inline(a, b, { class: 'conditional-range--selected' }));
    }
  }
  if (blockedRange && typeof blockedRange.from === 'number' && typeof blockedRange.to === 'number') {
    const a = Math.min(blockedRange.from, blockedRange.to);
    const b = Math.max(blockedRange.from, blockedRange.to);
    if (a < b) {
      decorations.push(Decoration.node(a, b, { class: 'conditional-block--blocked' }));
    }
  }

  return DecorationSet.create(doc, decorations);
};

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

// Для drag-границ IF обычный posAtCoords может промахиваться рядом с пробелами.
// Берём базовую позицию и уточняем её по ближайшим соседним coordsAtPos.
const resolveConditionalEdgePosFromCoords = (view, clientX, clientY, side, minPos, maxPos) => {
  if (!view || !view.state || !view.state.doc) return null;
  const hit = view.posAtCoords({ left: clientX, top: clientY });
  if (!hit || typeof hit.pos !== 'number') return null;

  const min = Number.isFinite(minPos) ? minPos : 1;
  const max = Number.isFinite(maxPos) ? maxPos : (view.state.doc.content.size - 1);
  const base = clamp(hit.pos, min, max);

  let bestPos = base;
  let bestDist = Number.POSITIVE_INFINITY;
  for (let p = Math.max(min, base - 3); p <= Math.min(max, base + 3); p += 1) {
    let rect;
    try {
      rect = view.coordsAtPos(p);
    } catch (_) {
      continue;
    }
    const x = side === 'start' ? rect.left : rect.right;
    const dist = Math.abs(x - clientX);
    if (dist < bestDist) {
      bestDist = dist;
      bestPos = p;
      continue;
    }
    if (dist === bestDist) {
      if (side === 'end' && p < bestPos) bestPos = p;
      if (side === 'start' && p > bestPos) bestPos = p;
    }
  }

  return bestPos;
};

// Запрещаем ставить границы условного блока внутри переменной {{...}}.
// "Мягкий" снэп: прилипание только если граница близко к краю переменной.
const snapConditionalEdgeOutsideVariable = (state, pos) => {
  const hit = findVariableDecorationAtPos(state, pos);
  if (!hit) return pos;
  // Выбираем ближайший край, чтобы не было мелькания "туда-сюда" при движении курсора внутри переменной.
  const dFrom = Math.abs(pos - hit.from);
  const dTo = Math.abs(hit.to - pos);
  const nearest = dFrom <= dTo ? hit.from : hit.to;
  const dist = Math.min(dFrom, dTo);
  return dist <= 2 ? nearest : pos;
};

// Жёсткий снэп: если внутри переменной, фиксируем на ближайший край.
const snapConditionalEdgeOutsideVariableHard = (state, pos) => {
  const hit = findVariableDecorationAtPos(state, pos);
  if (!hit) return pos;
  const dFrom = Math.abs(pos - hit.from);
  const dTo = Math.abs(hit.to - pos);
  return dFrom <= dTo ? hit.from : hit.to;
};

// Если граница попадает внутрь ЧУЖОГО условного блока (не текущей группы),
// запрещаем "резать" его: мягко прилипнем к ближайшему внешнему краю узла.
const snapConditionalEdgeOutsideForeignConditionals = (state, pos, currentGroupId) => {
  if (!state || !state.doc) return pos;
  const gid = String(currentGroupId || '');
  try {
    const $p = state.doc.resolve(pos);
    for (let d = $p.depth; d > 0; d--) {
      const n = $p.node(d);
      const name = n?.type?.name;
      if (name !== 'conditionalBlock' && name !== 'conditionalSection') continue;

      const nodePos = $p.before(d);
      const nodeAfter = $p.after(d);
      const nid = String(n?.attrs?.groupId || '');
      const eff = nid ? nid : `__pos_${nodePos}`;
      if (String(eff) === gid) continue;

      // Только если мы реально ВНУТРИ контента узла (не на границе)
      const start = $p.start(d);
      const end = $p.end(d);
      if (pos <= start || pos >= end) continue;

      const dL = Math.abs(pos - nodePos);
      const dR = Math.abs(nodeAfter - pos);
      const nearest = dL <= dR ? nodePos : nodeAfter;
      const dist = Math.min(dL, dR);
      return dist <= 2 ? nearest : pos;
    }
  } catch (_) { }
  return pos;
};

const findForeignConditionalNearPos = (state, pos, currentGroupId, threshold = 6) => {
  if (!state || !state.doc) return null;
  const gid = String(currentGroupId || '');
  let best = null;
  state.doc.descendants((node, nodePos) => {
    if (node.type?.name !== 'conditionalBlock' && node.type?.name !== 'conditionalSection') return;
    const eff = String(node.attrs?.groupId || `__pos_${nodePos}`);
    if (eff === gid) return;
    const start = nodePos;
    const end = nodePos + node.nodeSize;
    const d = Math.min(Math.abs(pos - start), Math.abs(end - pos));
    if (d > threshold) return;
    if (!best || d < best.dist) {
      best = { dist: d, from: start, to: end };
    }
  });
  return best ? { from: best.from, to: best.to } : null;
};

// Inline-условие (внутри строки)
const ConditionalBlock = TiptapNode.create({
  name: 'conditionalBlock',
  group: 'inline',
  inline: true,
  content: 'inline*',
  atom: false,
  selectable: true,
  addOptions() {
    return {
      interactive: true,
    };
  },
  addAttributes() {
    return {
      condition: {
        default: '',
        parseHTML: element => element.getAttribute('data-if') || '',
        renderHTML: attributes => ({
          'data-if': attributes.condition || '',
        }),
      },
      groupId: {
        default: '',
        parseHTML: element => element.getAttribute('data-cgid') || '',
        renderHTML: attributes => (attributes.groupId ? { 'data-cgid': attributes.groupId } : {}),
      },
    };
  },
  parseHTML() {
    return [
      {
        // Новое имя класса
        tag: 'span.conditional-inline',
        contentElement: 'span.conditional-body',
        preserveWhitespace: 'full',
      },
      {
        // Совместимость со старыми данными
        tag: 'span.conditional-block',
        contentElement: 'span.conditional-body',
        preserveWhitespace: 'full',
      },
    ];
  },
  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      {
        ...HTMLAttributes,
        class: 'conditional-inline',
        'data-if': String(node?.attrs?.condition || ''),
        ...(node?.attrs?.groupId ? { 'data-cgid': String(node.attrs.groupId) } : {}),
      },
      ['span', { class: 'conditional-body', 'data-cond-body': 'true' }, 0],
    ];
  },
  addCommands() {
    return {
      setConditionalBlock:
        (condition) =>
          ({ commands }) => {
            return commands.insertContent({
              type: this.name,
              attrs: { condition },
              content: [],
            });
          },
    };
  },
  addProseMirrorPlugins() {
    if (!this.options.interactive) return [];
    // Важно: используем decorations вместо абсолютного оверлея,
    // чтобы маркеры прокручивались вместе с текстом и не смещали содержимое.
    return [
      new Plugin({
        key: conditionalMarkersPluginKey,
        state: {
          init: () => ({ hoveredGroupId: null, selectedGroupId: null, previewRange: null, blockedRange: null }),
          apply(tr, value) {
            const meta = tr.getMeta(conditionalMarkersPluginKey);
            if (meta?.type === 'hover') {
              return { ...value, hoveredGroupId: meta.groupId || null };
            }
            if (meta?.type === 'select') {
              return { ...value, selectedGroupId: meta.groupId || null };
            }
            if (meta?.type === 'previewRange') {
              return { ...value, previewRange: meta.range || null };
            }
            if (meta?.type === 'blockedRange') {
              return { ...value, blockedRange: meta.range || null };
            }
            // backward-compat: clear = clear hover
            if (meta?.type === 'clear' || meta?.type === 'clearHover') {
              return { ...value, hoveredGroupId: null };
            }
            if (meta?.type === 'clearSelect') {
              return { ...value, selectedGroupId: null, previewRange: null };
            }
            if (meta?.type === 'clearPreviewRange') {
              return { ...value, previewRange: null };
            }
            if (meta?.type === 'clearBlockedRange') {
              return { ...value, blockedRange: null };
            }
            return value;
          },
        },
        props: {
          transformCopied(slice) {
            return stripConditionalsFromSlice(slice);
          },
          transformPasted(slice) {
            return stripConditionalsFromSlice(slice);
          },
          handleTextInput(view, _from, _to, text) {
            // Если курсор стоит в самом конце условного inline-блока, то набор текста
            // должен происходить уже ПОСЛЕ блока (чтобы блок не "разрастался").
            const { state } = view;
            const sel = state.selection;
            if (!sel || !sel.empty) return false;
            const $from = sel.$from;

            // Находим глубину, на которой находится conditionalBlock
            let condDepth = null;
            for (let d = $from.depth; d > 0; d--) {
              if ($from.node(d)?.type?.name === 'conditionalBlock') {
                condDepth = d;
                break;
              }
            }
            if (condDepth === null) return false;

            const startInside = $from.start(condDepth);
            const endInside = $from.end(condDepth);
            if ($from.pos !== endInside && $from.pos !== startInside) return false;

            // Курсор на правой границе: печатаем после блока.
            if ($from.pos === endInside) {
              const afterNode = $from.after(condDepth);
              const tr = state.tr.insertText(text, afterNode, afterNode);
              tr.setSelection(TextSelection.create(tr.doc, afterNode + text.length));
              view.dispatch(tr);
              return true;
            }

            // Курсор на левой границе: печатаем перед блоком.
            const beforeNode = $from.before(condDepth);
            const tr = state.tr.insertText(text, beforeNode, beforeNode);
            tr.setSelection(TextSelection.create(tr.doc, beforeNode + text.length));
            view.dispatch(tr);
            return true;
          },
          handleKeyDown(view, event) {
            // Если курсор внутри условного inline-блока и нажата Enter —
            // гарантируем создание нового абзаца.
            if (!event || event.key !== 'Enter') return false;
            const { state } = view;
            const sel = state.selection;
            if (!sel || !sel.empty) return false;
            const $from = sel.$from;

            let condDepth = null;
            for (let d = $from.depth; d > 0; d--) {
              if ($from.node(d)?.type?.name === 'conditionalBlock') {
                condDepth = d;
                break;
              }
            }
            if (condDepth === null) return false;

            const endInside = $from.end(condDepth);
            let applied = false;

            // 1) Внутри текста IF пробуем обычный splitBlock (разделить текущий абзац по курсору).
            if ($from.pos !== endInside) {
              applied = splitBlock(view.state, view.dispatch) || createParagraphNear(view.state, view.dispatch);
            } else {
              // 2) На правой границе IF сначала выходим за границу блока,
              // затем создаём новый абзац.
              const afterNode = $from.after(condDepth);
              const moveTr = state.tr.setSelection(TextSelection.create(state.doc, afterNode));
              view.dispatch(moveTr);
              applied = splitBlock(view.state, view.dispatch) || createParagraphNear(view.state, view.dispatch);
            }
            if (!applied) return false;

            event.preventDefault();
            event.stopPropagation();
            if (event.stopImmediatePropagation) event.stopImmediatePropagation();
            return true;
          },
          decorations(state) {
            const pluginState = conditionalMarkersPluginKey.getState(state);
            return buildConditionalDecorations(
              state.doc,
              pluginState?.hoveredGroupId || null,
              pluginState?.selectedGroupId || null,
              pluginState?.previewRange || null,
              pluginState?.blockedRange || null
            );
          },
        },
      }),
    ];
  },
});

// Блочное условие (может оборачивать несколько абзацев/блоков)
const ConditionalSection = TiptapNode.create({
  name: 'conditionalSection',
  group: 'block',
  inline: false,
  content: 'block+',
  atom: false,
  selectable: true,
  addAttributes() {
    return {
      condition: {
        default: '',
        parseHTML: element => element.getAttribute('data-if') || '',
        renderHTML: attributes => ({
          'data-if': attributes.condition || '',
        }),
      },
      groupId: {
        default: '',
        parseHTML: element => element.getAttribute('data-cgid') || '',
        renderHTML: attributes => (attributes.groupId ? { 'data-cgid': attributes.groupId } : {}),
      },
    };
  },
  parseHTML() {
    return [
      { tag: 'div.conditional-section', contentElement: 'div.conditional-body', preserveWhitespace: 'full' },
      // Совместимость на случай старых данных/ручных вставок
      { tag: 'div.conditional-block', contentElement: 'div.conditional-body', preserveWhitespace: 'full' },
    ];
  },
  renderHTML({ node, HTMLAttributes }) {
    return [
      'div',
      {
        ...HTMLAttributes,
        class: 'conditional-section',
        'data-if': String(node?.attrs?.condition || ''),
        ...(node?.attrs?.groupId ? { 'data-cgid': String(node.attrs.groupId) } : {}),
      },
      ['div', { class: 'conditional-body', 'data-cond-body': 'true' }, 0],
    ];
  },
});

// Блочная секция цикла (for ... in ...)
const LoopSection = TiptapNode.create({
  name: 'loopSection',
  group: 'block',
  inline: false,
  content: 'block+',
  atom: false,
  selectable: true,
  addAttributes() {
    return {
      loop: {
        default: '',
        parseHTML: element => element.getAttribute('data-for') || '',
        renderHTML: attributes => ({
          'data-for': attributes.loop || '',
        }),
      },
    };
  },
  parseHTML() {
    return [
      { tag: 'div.loop-section', contentElement: 'div.loop-body' },
    ];
  },
  renderHTML({ node, HTMLAttributes }) {
    return [
      'div',
      {
        ...HTMLAttributes,
        class: 'loop-section',
        'data-for': String(node?.attrs?.loop || ''),
      },
      ['div', { class: 'loop-header', contenteditable: 'false' }, `Цикл: ${String(node?.attrs?.loop || '')}`],
      ['div', { class: 'loop-body', 'data-loop-body': 'true' }, 0],
    ];
  },
});

// Условные блоки: рендер в понятный вид и обратная сериализация
// Важно: поддерживаем вложенные [[if]]...[[endif]] (в противном случае маркеры остаются видимыми в тексте).
const renderConditionalBlocks = (html) => {
  if (!html) return html;
  const protectInlineConditionalEdgeSpaces = (value) => {
    const srcValue = String(value || '');
    if (!srcValue) return srcValue;
    const lead = srcValue.match(/^ +/)?.[0] || '';
    const trail = srcValue.match(/ +$/)?.[0] || '';
    if (!lead && !trail) return srcValue;
    const mid = srcValue.slice(lead.length, srcValue.length - trail.length);
    return `${'\u00A0'.repeat(lead.length)}${mid}${'\u00A0'.repeat(trail.length)}`;
  };
  const src = String(html);
  const lower = src.toLowerCase();
  const closeToken = '[[endif]]';

  const findMatchingEndIf = (fromIdx) => {
    let depth = 1;
    let i = fromIdx;
    while (i < src.length) {
      const nextIf = lower.indexOf('[[if', i);
      const nextEnd = lower.indexOf(closeToken, i);
      if (nextEnd === -1) return -1;
      if (nextIf !== -1 && nextIf < nextEnd) {
        const close = src.indexOf(']]', nextIf);
        if (close === -1) return -1;
        depth += 1;
        i = close + 2;
        continue;
      }
      depth -= 1;
      if (depth === 0) return nextEnd;
      i = nextEnd + closeToken.length;
    }
    return -1;
  };

  let out = '';
  let cursor = 0;
  while (true) {
    const ifIdx = lower.indexOf('[[if', cursor);
    if (ifIdx === -1) break;
    const headerEnd = src.indexOf(']]', ifIdx);
    if (headerEnd === -1) break;
    const condRaw = src.slice(ifIdx + 4, headerEnd).trim();
    const afterIf = headerEnd + 2;
    const endIdx = findMatchingEndIf(afterIf);
    if (endIdx === -1) break;

    const body = src.slice(afterIf, endIdx);
    const renderedBody = renderConditionalBlocks(body);

    const parts = String(condRaw || '').trim().split('@@');
    const cleanCond = String(parts[0] || '').trim();
    const groupId = String(parts[1] || '').trim();
    const gidAttr = groupId ? ` data-cgid="${groupId}"` : '';

    // Если внутри есть блочные теги — используем блочную обёртку, иначе inline.
    const isBlockBody = /<(p|div|h[1-6]|ul|ol|li|table|thead|tbody|tr|td|th|blockquote|pre)\b/i.test(renderedBody);
    const wrapped = isBlockBody
      ? `<div class="conditional-section" data-if="${cleanCond}"${gidAttr}><div class="conditional-body">${renderedBody}</div></div>`
      : `<span class="conditional-inline" data-if="${cleanCond}"${gidAttr}><span class="conditional-body">${protectInlineConditionalEdgeSpaces(renderedBody)}</span></span>`;

    out += src.slice(cursor, ifIdx) + wrapped;
    cursor = endIdx + closeToken.length;
  }
  out += src.slice(cursor);
  return out;
};

const LOOP_LIST_OPTIONS = [
  { value: 'pledge_single_creditor_groups', label: 'Группы залоговых лотов с одним кредитором' },
  { value: 'pledge_single_creditor_solo_lots', label: 'Залоговые лоты с одним кредитором (по одному)' },
  { value: 'pledge_multi_creditor_entries', label: 'Лоты с несколькими кредиторами (по кредитору)' },
];

const DEFAULT_LOOP_LIST = LOOP_LIST_OPTIONS[0]?.value || '';

const parseLoopExpression = (expr) => {
  const raw = String(expr || '').trim();
  const match = raw.match(/^\s*([a-zA-Z_][\w]*)\s+in\s+([a-zA-Z0-9_.]+)\s*$/);
  if (!match) return { item: '', list: '' };
  return { item: match[1], list: match[2] };
};

const getLoopLabel = (expr) => {
  const { list } = parseLoopExpression(expr);
  const found = LOOP_LIST_OPTIONS.find((opt) => opt.value === list);
  return found?.label || '';
};

const renderLoopBlocks = (html) => {
  if (!html) return html;
  const src = String(html);
  const lower = src.toLowerCase();
  const closeToken = '[[endfor]]';

  const findMatchingEndFor = (fromIdx) => {
    let depth = 1;
    let i = fromIdx;
    while (i < src.length) {
      const nextFor = lower.indexOf('[[for', i);
      const nextEnd = lower.indexOf(closeToken, i);
      if (nextEnd === -1) return -1;
      if (nextFor !== -1 && nextFor < nextEnd) {
        const close = src.indexOf(']]', nextFor);
        if (close === -1) return -1;
        depth += 1;
        i = close + 2;
        continue;
      }
      depth -= 1;
      if (depth === 0) return nextEnd;
      i = nextEnd + closeToken.length;
    }
    return -1;
  };

  let out = '';
  let cursor = 0;
  while (true) {
    const forIdx = lower.indexOf('[[for', cursor);
    if (forIdx === -1) break;
    const headerEnd = src.indexOf(']]', forIdx);
    if (headerEnd === -1) break;
    const loopRaw = src.slice(forIdx + 5, headerEnd).trim();
    const afterFor = headerEnd + 2;
    const endIdx = findMatchingEndFor(afterFor);
    if (endIdx === -1) break;

    const body = src.slice(afterFor, endIdx);
    const renderedBody = renderConditionalBlocks(renderLoopBlocks(body));

    const label = getLoopLabel(loopRaw);
    const headerText = label ? `Цикл: ${label}` : `Цикл: ${loopRaw}`;
    const wrapped =
      `<div class="loop-section" data-for="${loopRaw}"${label ? ` data-loop-label="${label}"` : ''}>` +
      `<div class="loop-header" contenteditable="false">${headerText}</div>` +
      `<div class="loop-body">${renderedBody}</div>` +
      `</div>`;

    out += src.slice(cursor, forIdx) + wrapped;
    cursor = endIdx + closeToken.length;
  }
  out += src.slice(cursor);
  return out;
};

const serializeConditionalBlocks = (html) => {
  if (!html) return html;
  const restoreInlineConditionalEdgeSpaces = (value) => {
    const srcValue = String(value || '');
    if (!srcValue) return srcValue;
    return srcValue
      .replace(/^\u00A0+/, (m) => ' '.repeat(m.length))
      .replace(/\u00A0+$/, (m) => ' '.repeat(m.length));
  };
  return html
    // Блочная версия
    .replace(/<div[^>]*class="[^"]*(conditional-section|conditional-block)[^"]*"[^>]*data-if="([^"]+)"[^>]*?(?:data-cgid="([^"]*)")?[^>]*>\s*<div[^>]*class="[^"]*conditional-body[^"]*"[^>]*>([\s\S]*?)<\/div>\s*<\/div>/gi,
      (m, _cls, cond, gid, body) => `[[if ${cond}${gid ? `@@${gid}` : ''}]]${body}[[endif]]`)
    // Inline версия (новая и старая)
    .replace(/<span[^>]*class="[^"]*(conditional-inline|conditional-block)[^"]*"[^>]*data-if="([^"]+)"[^>]*?(?:data-cgid="([^"]*)")?[^>]*>([\s\u00A0\u200B\uFEFF]*)<span[^>]*class="[^"]*conditional-body[^"]*"[^>]*>([\s\S]*?)<\/span>([\s\u00A0\u200B\uFEFF]*)<\/span>/gi,
      (m, _cls, cond, gid, leadingWs, body, trailingWs) => {
        const safeLeading = String(leadingWs || '').replace(/\u00A0/g, ' ');
        const safeBody = restoreInlineConditionalEdgeSpaces(body);
        const safeTrailing = String(trailingWs || '').replace(/\u00A0/g, ' ');
        return `[[if ${cond}${gid ? `@@${gid}` : ''}]]${safeLeading}${safeBody}${safeTrailing}[[endif]]`;
      })
    // Старый вариант с badge оставляем на случай старых данных
    .replace(/<span[^>]*class="[^"]*conditional-block[^"]*"[^>]*data-if="([^"]+)"[^>]*>([\s\u00A0\u200B\uFEFF]*)<span[^>]*class="[^"]*conditional-badge[^"]*"[^>]*>.*?<\/span>([\s\u00A0\u200B\uFEFF]*)<span[^>]*class="[^"]*conditional-body[^"]*"[^>]*>([\s\S]*?)<\/span>([\s\u00A0\u200B\uFEFF]*)<\/span>/gi,
      (m, cond, beforeBadgeWs, afterBadgeWs, body, trailingWs) => `[[if ${cond}]]${beforeBadgeWs || ''}${afterBadgeWs || ''}${body}${trailingWs || ''}[[endif]]`);
};

const serializeLoopBlocks = (html) => {
  if (!html) return html;
  return html
    .replace(/<div[^>]*class="[^"]*loop-section[^"]*"[^>]*data-for="([^"]+)"[^>]*>\s*(?:<div[^>]*class="[^"]*loop-header[^"]*"[^>]*>[\s\S]*?<\/div>\s*)?<div[^>]*class="[^"]*loop-body[^"]*"[^>]*>([\s\S]*?)<\/div>\s*<\/div>/gi,
      (m, loopExpr, body) => `[[for ${loopExpr}]]${body}[[endfor]]`);
};

// Нормализуем края HTML-документа:
// 1) убираем краевые пробельные текстовые узлы,
// 2) убираем краевые пустые <p>.
// Это предотвращает появление "лишних" ¶ сверху/снизу после save -> reopen.
const normalizeEdgeWhitespaceAndEmptyParagraphs = (html) => {
  if (!html || typeof document === 'undefined') return html;

  const root = document.createElement('div');
  root.innerHTML = String(html);

  const isWhitespaceText = (text) => String(text || '').replace(/[\s\u00A0\u200B\uFEFF]/g, '') === '';

  const isTrimmableParagraph = (el) => {
    if (!el || el.nodeType !== 1 || el.tagName !== 'P') return false;

    for (const node of Array.from(el.childNodes || [])) {
      if (node.nodeType === 3) {
        if (!isWhitespaceText(node.nodeValue)) return false;
        continue;
      }
      if (node.nodeType !== 1) continue;
      const child = node;
      if (child.tagName === 'BR') continue;
      return false;
    }

    return isWhitespaceText(el.textContent || '');
  };

  const isRemovableEdgeNode = (node) => {
    if (!node) return false;
    if (node.nodeType === 8) return true; // comment
    if (node.nodeType === 3) return isWhitespaceText(node.nodeValue); // text
    return false;
  };

  while (isRemovableEdgeNode(root.firstChild)) {
    root.removeChild(root.firstChild);
  }
  while (isRemovableEdgeNode(root.lastChild)) {
    root.removeChild(root.lastChild);
  }

  while (isTrimmableParagraph(root.firstElementChild)) {
    root.removeChild(root.firstElementChild);
    while (isRemovableEdgeNode(root.firstChild)) {
      root.removeChild(root.firstChild);
    }
  }
  while (isTrimmableParagraph(root.lastElementChild)) {
    root.removeChild(root.lastElementChild);
    while (isRemovableEdgeNode(root.lastChild)) {
      root.removeChild(root.lastChild);
    }
  }

  const out = root.innerHTML;
  return out || '<p></p>';
};

// ===== Редактор условий (user-friendly) =====
const CONDITION_VARIABLES = [
  {
    code: 'debtor_type_code',
    label: 'Тип должника',
    type: 'enum',
    values: [
      { value: 'legal', label: 'Юридическое лицо' },
      { value: 'individual', label: 'Физическое лицо' },
      { value: 'entrepreneur', label: 'ИП' },
    ],
  },
  {
    code: 'debtor_gender',
    label: 'Пол должника',
    type: 'enum',
    values: [
      { value: 'female', label: 'Женский' },
      { value: 'male', label: 'Мужской' },
      { value: 'neuter', label: 'Средний / организация' },
      { value: 'unknown', label: 'Не определен' },
    ],
  },
  {
    code: 'debtor_manager_gender',
    label: 'Пол арбитражного управляющего',
    type: 'enum',
    values: [
      { value: 'female', label: 'Женский' },
      { value: 'male', label: 'Мужской' },
      { value: 'neuter', label: 'Средний / организация' },
      { value: 'unknown', label: 'Не определен' },
    ],
  },
  {
    code: 'debtor_procedure_code',
    label: 'Процедура',
    type: 'enum',
    values: [
      { value: 'observation', label: 'наблюдения' },
      { value: 'competition', label: 'конкурсное производство' },
      { value: 'financial_rehabilitation', label: 'финансовое оздоровление' },
      { value: 'external_management', label: 'внешнее управление' },
      { value: 'citizen_debt_restructuring', label: 'реструктуризация долгов гражданина' },
      { value: 'citizen_property_realization', label: 'реализация имущества гражданина' },
    ],
  },
  {
    code: 'debtor_manager_role',
    label: 'Тип управляющего',
    type: 'enum',
    values: [
      { value: 'временный', label: 'Временный управляющий' },
      { value: 'конкурсный', label: 'Конкурсный управляющий' },
      { value: 'административный', label: 'Административный управляющий' },
      { value: 'внешний', label: 'Внешний управляющий' },
      { value: 'финансовый', label: 'Финансовый управляющий' },
    ],
  },
  {
    code: 'debtor_manager_act_basis_code',
    label: 'Назначение АУ',
    type: 'enum',
    values: [
      { value: 'same', label: 'АУ утверждён тем же актом, что и процедура' },
      { value: 'separate', label: 'АУ утверждён другим актом' },
      { value: 'unknown', label: 'Не определено' },
    ],
  },
  {
    code: 'debtor_acts_court_relation_code',
    label: 'Суды по актам',
    type: 'enum',
    values: [
      { value: 'same', label: 'Одинаковые' },
      { value: 'different', label: 'Разные' },
      { value: 'unknown', label: 'Не определено' },
    ],
  },
  {
    code: 'debtor_manager_act_type',
    label: 'Тип судебного акта о назначении управляющего',
    type: 'enum',
    values: [
      { value: 'Решение', label: 'Решение' },
      { value: 'Определение', label: 'Определение' },
      { value: 'Постановление', label: 'Постановление' },
    ],
  },
  {
    code: 'auction_conductor_code',
    label: 'Торги проводит',
    type: 'enum',
    values: [
      { value: 'ot', label: 'ОТ (организатор торгов)' },
      { value: 'au', label: 'АУ (арбитражный управляющий)' },
    ],
  },
  {
    code: 'auction_form_code',
    label: 'Форма торгов',
    type: 'enum',
    values: [
      { value: 'auction', label: 'Аукцион' },
      { value: 'contest', label: 'Конкурс' },
      { value: 'public_offer', label: 'Публичное предложение' },
    ],
  },
  {
    code: 'price_offer_form_code',
    label: 'Форма подачи предложения о цене',
    type: 'enum',
    values: [
      { value: 'open', label: 'Открытая' },
      { value: 'closed', label: 'Закрытая' },
    ],
  },
  {
    code: 'organizer_entity_type_code',
    label: 'Тип организатора торгов',
    type: 'enum',
    values: [
      { value: 'legal', label: 'Юридическое лицо' },
      { value: 'entrepreneur', label: 'ИП' },
      { value: 'individual', label: 'Физическое лицо' },
    ],
  },
  {
    code: 'organizer_type_code',
    label: 'Роль организатора торгов',
    type: 'enum',
    values: [
      { value: 'specialized_org', label: 'ОТ (организатор торгов)' },
      { value: 'arbitrage_manager', label: 'АУ (арбитражный управляющий)' },
    ],
  },
  {
    code: 'inspection_phone',
    label: 'Телефон для ознакомления',
    type: 'text',
  },
  {
    code: 'inspection_email',
    label: 'Email для ознакомления',
    type: 'text',
  },
  {
    code: 'kommersant_party_kind',
    label: 'Коммерсантъ: кто оплачивает / на кого оформляется',
    type: 'enum',
    values: [
      { value: 'debtor', label: 'Должник' },
      { value: 'organizer', label: 'ОТ (организатор торгов)' },
      { value: 'manager', label: 'АУ (арбитражный управляющий)' },
    ],
  },
  {
    code: 'auction_lots_count_category',
    label: 'Количество лотов',
    type: 'enum',
    values: [
      { value: 'single', label: '1 лот' },
      { value: 'multiple', label: '>1 лота' },
      { value: 'none', label: 'Лотов нет' },
    ],
  },
  {
    code: 'auction_lots_count_bucket',
    label: 'Количество лотов (детально)',
    type: 'enum',
    values: [
      { value: 'none', label: '0' },
      { value: 'single', label: '1' },
      { value: 'two', label: '2' },
      { value: 'many', label: '3 и более' },
    ],
  },
  {
    code: 'pledge_lots_count_category',
    label: 'Залоговые лоты',
    type: 'enum',
    values: [
      { value: 'single', label: '1 лот' },
      { value: 'multiple', label: '>1 лота' },
      { value: 'none', label: 'Лотов нет' },
    ],
  },
  {
    code: 'pledge_lots_count_bucket',
    label: 'Залоговые лоты (детально)',
    type: 'enum',
    values: [
      { value: 'none', label: '0' },
      { value: 'single', label: '1' },
      { value: 'two', label: '2' },
      { value: 'many', label: '3 и более' },
    ],
  },
  {
    code: 'pledge_lots_same_creditor_group_present',
    label: 'Есть группа лотов с одним кредитором',
    type: 'enum',
    values: [
      { value: 'yes', label: 'Да' },
      { value: 'no', label: 'Нет' },
    ],
  },
  {
    code: 'pledge_lots_multi_creditor_category',
    label: 'Лоты с несколькими кредиторами',
    type: 'enum',
    values: [
      { value: 'none', label: 'Нет' },
      { value: 'single', label: '1 лот' },
      { value: 'multiple', label: '>1 лота' },
    ],
  },
  {
    code: 'pledge_lots_single_creditor_unique_category',
    label: 'Уникальные кредиторы (по залоговым лотам)',
    type: 'enum',
    values: [
      { value: 'none', label: 'Нет' },
      { value: 'single', label: '1 кредитор' },
      { value: 'multiple', label: 'Более 1' },
    ],
  },
  {
    code: 'property_has_asset_lots',
    label: 'Есть движимое/недвижимое имущество',
    type: 'enum',
    values: [
      { value: 'yes', label: 'Да' },
      { value: 'no', label: 'Нет' },
    ],
  },
  {
    code: 'property_has_debt_lots',
    label: 'Есть дебиторская задолженность',
    type: 'enum',
    values: [
      { value: 'yes', label: 'Да' },
      { value: 'no', label: 'Нет' },
    ],
  },
  {
    code: 'property_has_debt_and_asset_lots',
    label: 'Есть и дебиторская, и имущество',
    type: 'enum',
    values: [
      { value: 'yes', label: 'Да' },
      { value: 'no', label: 'Нет' },
    ],
  },
  // Часто используемые проверки «заполнено/не заполнено»
  { code: 'debtor_kpp', label: 'КПП должника', type: 'text' },
  { code: 'debtor_snils', label: 'СНИЛС должника', type: 'text' },
  { code: 'debtor_email', label: 'Email должника', type: 'text' },
  { code: 'debtor_phone', label: 'Телефон должника', type: 'text' },
  { code: 'organizer_kpp', label: 'КПП организатора торгов', type: 'text' },
  { code: 'organizer_email', label: 'Email организатора торгов', type: 'text' },
  { code: 'organizer_phone', label: 'Телефон организатора торгов', type: 'text' },
  { code: 'organizer_mailing_address', label: 'Почтовый адрес организатора торгов', type: 'text' },
  { code: 'debtor_manager_email', label: 'Email арбитражного управляющего', type: 'text' },
  { code: 'debtor_manager_phone', label: 'Телефон арбитражного управляющего', type: 'text' },
  { code: 'debtor_manager_mailing_address', label: 'Адрес для корреспонденции арбитражного управляющего', type: 'text' },
  { code: 'deposit_account_details', label: 'Реквизиты счета для внесения задатка', type: 'text' },
  { code: 'payment_account_details', label: 'Реквизиты счета для оплаты по договору', type: 'text' },
  { code: 'deposit_account_owner_name', label: 'Наименование владельца счета для внесения задатка', type: 'text' },
  { code: 'payment_account_owner_name', label: 'Наименование владельца счета для оплаты по договору', type: 'text' },
  { code: 'deposit_account_owner_inn', label: 'ИНН владельца счета для внесения задатка', type: 'text' },
  { code: 'payment_account_owner_inn', label: 'ИНН владельца счета для оплаты по договору', type: 'text' },
  {
    code: 'deposit_account_owner_type_code',
    label: 'Тип владельца счета для внесения задатка',
    type: 'enum',
    values: [
      { value: 'debtor', label: 'Должник' },
      { value: 'organizer', label: 'Организатор торгов' },
      { value: 'platform', label: 'ЭТП / площадка' },
    ],
  },
  {
    code: 'payment_account_owner_type_code',
    label: 'Тип владельца счета для оплаты по договору',
    type: 'enum',
    values: [
      { value: 'debtor', label: 'Должник' },
      { value: 'organizer', label: 'Организатор торгов' },
      { value: 'platform', label: 'ЭТП / площадка' },
    ],
  },
  { code: 'deposit_account_owner_type_label', label: 'Тип владельца счета для внесения задатка (текст)', type: 'text' },
  { code: 'payment_account_owner_type_label', label: 'Тип владельца счета для оплаты по договору (текст)', type: 'text' },
  { code: 'debtor_case_act_court', label: 'Суд акта о введении процедуры', type: 'text' },
  { code: 'debtor_manager_act_court', label: 'Суд акта о назначении управляющего', type: 'text' },
  { code: 'debtor_manager_act_date', label: 'Дата акта о назначении управляющего', type: 'date' },
  { code: 'debtor_case_act_date', label: 'Дата акта о введении процедуры', type: 'date' },
];

const sanitizeConditionText = (raw) => String(raw || '').split('@@')[0].trim();

const isEnumValueAllowed = (varCode, value) => {
  const meta = CONDITION_VARIABLES.find((v) => v.code === varCode);
  if (!meta || meta.type !== 'enum') return true;
  const allowed = (meta.values || []).map((v) => String(v.value));
  return allowed.includes(String(value));
};

const getConditionVarMeta = (varCode) => CONDITION_VARIABLES.find((v) => String(v.code) === String(varCode));

const getConditionValueLabel = (varCode, rawValue) => {
  const meta = getConditionVarMeta(varCode);
  if (!meta || meta.type !== 'enum') return null;
  const found = (meta.values || []).find((v) => String(v.value) === String(rawValue));
  return found ? String(found.label || '') : null;
};

const getDefaultEnumValue = (varCode) => {
  const meta = CONDITION_VARIABLES.find((v) => v.code === varCode);
  if (!meta || meta.type !== 'enum') return '';
  return String(meta.values?.[0]?.value || '');
};

const unwrapOuterParens = (s) => {
  const str = String(s || '').trim();
  if (!str.startsWith('(') || !str.endsWith(')')) return str;
  let depth = 0;
  let inSingle = false;
  let inDouble = false;
  for (let i = 0; i < str.length; i++) {
    const ch = str[i];
    const prev = str[i - 1];
    if (!inDouble && ch === "'" && prev !== '\\') inSingle = !inSingle;
    if (!inSingle && ch === '"' && prev !== '\\') inDouble = !inDouble;
    if (inSingle || inDouble) continue;
    if (ch === '(') depth++;
    if (ch === ')') depth--;
    // если внешняя скобка закрылась раньше конца — это не «общая» оболочка
    if (depth === 0 && i < str.length - 1) return str;
  }
  // если баланс ок — снимаем один уровень
  return str.slice(1, -1).trim();
};

const splitTopLevel = (input, op) => {
  const s = String(input || '');
  const token = String(op || '');
  if (!s || !token) return [String(s).trim()].filter(Boolean);
  const parts = [];
  let buf = '';
  let depth = 0;
  let inSingle = false;
  let inDouble = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    const prev = s[i - 1];
    if (!inDouble && ch === "'" && prev !== '\\') inSingle = !inSingle;
    if (!inSingle && ch === '"' && prev !== '\\') inDouble = !inDouble;
    if (!inSingle && !inDouble) {
      if (ch === '(') depth++;
      if (ch === ')') depth = Math.max(0, depth - 1);
    }
    if (!inSingle && !inDouble && depth === 0 && s.slice(i, i + token.length) === token) {
      parts.push(buf.trim());
      buf = '';
      i += token.length - 1;
      continue;
    }
    buf += ch;
  }
  parts.push(buf.trim());
  return parts.filter(Boolean);
};

const parsePredicate = (p) => {
  const raw = String(p || '').trim();
  if (!raw) return null;
  let m = raw.match(/^empty\(\s*([\w.]+)\s*\)$/i);
  if (m) return { kind: 'clause', variable: m[1], op: 'empty', value: '' };
  m = raw.match(/^not_empty\(\s*([\w.]+)\s*\)$/i);
  if (m) return { kind: 'clause', variable: m[1], op: 'not_empty', value: '' };
  m = raw.match(/^contains\(\s*([\w.]+)\s*,\s*['"]([^'"]+)['"]\s*\)$/i);
  if (m) return { kind: 'clause', variable: m[1], op: 'contains', value: m[2] };
  m = raw.match(/^([\w.]+)\s*(==|!=)\s*(['"])([^'"]+)\3$/);
  if (m) return { kind: 'clause', variable: m[1], op: m[2] === '==' ? 'eq' : 'neq', value: m[4] };
  m = raw.match(/^([\w.]+)\s*(==|!=)\s*([\w.]+)$/);
  if (m) return { kind: 'clause', variable: m[1], op: m[2] === '==' ? 'eq_var' : 'neq_var', value: m[3] };
  return null;
};

const normalizeBuilderClauses = (clauses) => {
  const items = Array.isArray(clauses) ? clauses : [];
  const out = [];
  items.forEach((it) => {
    if (it && it.kind === 'group') {
      const inner = Array.isArray(it.clauses) ? it.clauses : [];
      const fixed = inner.map((c) => ({
        kind: 'clause',
        variable: String(c.variable || '').trim(),
        op: String(c.op || '').trim(),
        value: String(c.value || '').trim(),
      })).filter((c) => c.variable && c.op);
      if (fixed.length) out.push({ kind: 'group', join: '||', clauses: fixed });
      return;
    }
    const clause = {
      kind: 'clause',
      variable: String(it?.variable || '').trim(),
      op: String(it?.op || '').trim(),
      value: String(it?.value || '').trim(),
    };
    if (clause.variable && clause.op) out.push(clause);
  });
  return out;
};

const tryParseMixedToBuilder = (rawExpression) => {
  const expression = sanitizeConditionText(rawExpression);
  if (!expression) return null;
  const hasAnd = expression.includes('&&');
  const hasOr = expression.includes('||');
  if (!(hasAnd && hasOr)) return null;

  // Поддерживаем распространённый случай: A && (B || C || D) && E ...
  const andParts = splitTopLevel(expression, '&&');
  if (andParts.length < 2) return null;

  const clauses = [];
  for (const partRaw of andParts) {
    const part = unwrapOuterParens(partRaw);
    const orParts = splitTopLevel(part, '||');
    if (orParts.length > 1) {
      const inner = orParts.map(parsePredicate).filter(Boolean);
      if (inner.length !== orParts.length) return null;
      clauses.push({ kind: 'group', join: '||', clauses: inner });
      continue;
    }
    const pred = parsePredicate(part);
    if (!pred) return null;
    clauses.push(pred);
  }
  return { mode: 'builder', raw: expression, join: '&&', clauses };
};

const tryParseOrWithAndToBuilder = (rawExpression) => {
  const expression = sanitizeConditionText(rawExpression);
  if (!expression) return null;
  const hasAnd = expression.includes('&&');
  const hasOr = expression.includes('||');
  if (!(hasAnd && hasOr)) return null;

  // Поддерживаем распространённый случай: A || (B && C && ...)
  // В визуальном редакторе представляем эквивалентно:
  // (A || B) && (A || C) && ...
  const orParts = splitTopLevel(expression, '||');
  if (orParts.length !== 2) return null;

  const leftRaw = unwrapOuterParens(orParts[0]);
  const rightRaw = unwrapOuterParens(orParts[1]);
  const leftPred = parsePredicate(leftRaw);
  const rightPred = parsePredicate(rightRaw);

  const buildFrom = (singlePred, andExprRaw) => {
    if (!singlePred) return null;
    const andParts = splitTopLevel(andExprRaw, '&&');
    if (andParts.length < 2) return null;
    const andPreds = andParts.map((p) => parsePredicate(unwrapOuterParens(p))).filter(Boolean);
    if (andPreds.length !== andParts.length) return null;
    const clauses = andPreds.map((p) => ({ kind: 'group', join: '||', clauses: [singlePred, p] }));
    return { mode: 'builder', raw: expression, join: '&&', clauses };
  };

  return buildFrom(leftPred, rightRaw) || buildFrom(rightPred, leftRaw);
};

const MAX_CNF_CLAUSES = 32;
const MAX_CNF_DISJUNCTION_SIZE = 8;

const tryParseAnyLogicalToBuilder = (rawExpression) => {
  const expression = sanitizeConditionText(rawExpression);
  if (!expression) return null;

  const parseExpr = (exprRaw) => {
    const expr = unwrapOuterParens(String(exprRaw || '').trim());
    if (!expr) return null;

    const orParts = splitTopLevel(expr, '||');
    if (orParts.length > 1) {
      const children = orParts.map((p) => parseExpr(p)).filter(Boolean);
      if (children.length !== orParts.length) return null;
      return { type: 'or', children };
    }

    const andParts = splitTopLevel(expr, '&&');
    if (andParts.length > 1) {
      const children = andParts.map((p) => parseExpr(p)).filter(Boolean);
      if (children.length !== andParts.length) return null;
      return { type: 'and', children };
    }

    const pred = parsePredicate(expr);
    if (!pred) return null;
    return { type: 'pred', pred };
  };

  const node = parseExpr(expression);
  if (!node) return null;

  const predKey = (p) => `${String(p?.variable || '').trim()}|${String(p?.op || '').trim()}|${String(p?.value || '').trim()}`;

  const cnfOf = (n) => {
    if (!n) return null;
    if (n.type === 'pred') return [[n.pred]];
    if (n.type === 'and') {
      let out = [];
      for (const ch of (n.children || [])) {
        const cnf = cnfOf(ch);
        if (!cnf) return null;
        out = out.concat(cnf);
        if (out.length > MAX_CNF_CLAUSES) return null;
      }
      return out;
    }
    if (n.type === 'or') {
      const children = n.children || [];
      if (!children.length) return null;
      let out = cnfOf(children[0]);
      if (!out) return null;
      for (let i = 1; i < children.length; i += 1) {
        const right = cnfOf(children[i]);
        if (!right) return null;
        const next = [];
        for (const lDisj of out) {
          for (const rDisj of right) {
            const merged = [];
            const seen = new Set();
            [...(lDisj || []), ...(rDisj || [])].forEach((p) => {
              const k = predKey(p);
              if (!seen.has(k)) {
                seen.add(k);
                merged.push(p);
              }
            });
            if (merged.length > MAX_CNF_DISJUNCTION_SIZE) return null;
            next.push(merged);
            if (next.length > MAX_CNF_CLAUSES) return null;
          }
        }
        out = next;
      }
      return out;
    }
    return null;
  };

  const cnf = cnfOf(node);
  if (!cnf || !cnf.length || cnf.length > MAX_CNF_CLAUSES) return null;

  const clauses = cnf.map((disj) => {
    const items = (disj || []).filter(Boolean);
    if (items.length <= 1) return items[0] || null;
    return { kind: 'group', join: '||', clauses: items };
  }).filter(Boolean);

  if (!clauses.length) return null;
  return { mode: 'builder', raw: expression, join: '&&', clauses };
};

const parseConditionToBuilder = (rawExpression) => {
  const expression = sanitizeConditionText(rawExpression);
  const hasAnd = /\s&&\s/.test(expression);
  const hasOr = /\s\|\|\s/.test(expression);
  if (hasAnd && hasOr) {
    const mixed = tryParseMixedToBuilder(expression);
    if (mixed) return mixed;
    const orWithAnd = tryParseOrWithAndToBuilder(expression);
    if (orWithAnd) return orWithAnd;
    const anyLogical = tryParseAnyLogicalToBuilder(expression);
    if (anyLogical) return anyLogical;
    return { mode: 'advanced', raw: expression, join: '&&', clauses: [] };
  }
  const join = hasOr ? '||' : '&&';
  const splitter = join === '||' ? /\s*\|\|\s*/g : /\s*&&\s*/g;
  const parts = expression.split(splitter).map((s) => s.trim()).filter(Boolean);
  const clauses = [];
  for (const p of parts) {
    const pred = parsePredicate(unwrapOuterParens(p));
    if (pred) { clauses.push(pred); continue; }
    return { mode: 'advanced', raw: expression, join, clauses: [] };
  }
  return {
    mode: 'builder',
    raw: expression,
    join,
    clauses: (clauses.length ? clauses : [{ kind: 'clause', variable: 'debtor_type_code', op: 'eq', value: 'legal' }]).map((c) => {
      if (c.kind === 'group') {
        const fixedInner = (c.clauses || []).map((ic) => {
          const op = String(ic.op || 'eq');
          const varCode = String(ic.variable || '');
          const val = String(ic.value || '');
          if ((op === 'eq' || op === 'neq') && !isEnumValueAllowed(varCode, val)) {
            return { ...ic, value: getDefaultEnumValue(varCode) };
          }
          return ic;
        });
        return { ...c, join: '||', clauses: fixedInner };
      }
      const op = String(c.op || 'eq');
      const varCode = String(c.variable || '');
      const val = String(c.value || '');
      if ((op === 'eq' || op === 'neq') && !isEnumValueAllowed(varCode, val)) {
        return { ...c, value: getDefaultEnumValue(varCode) };
      }
      return c;
    }),
		  };
};

const buildConditionFromBuilder = ({ join, clauses }) => {
  const cleanItems = normalizeBuilderClauses(clauses);
  const buildClause = (c) => {
    const variable = String(c.variable || '').trim();
    const op = String(c.op || '').trim();
    const value = String(c.value || '').trim();
    if (!variable || !op) return '';
    if (op === 'empty') return `empty(${variable})`;
    if (op === 'not_empty') return `not_empty(${variable})`;
    if (op === 'contains') return `contains(${variable}, '${value.replace(/'/g, "\\'")}')`;
    if (op === 'eq') return `${variable} == '${value.replace(/'/g, "\\'")}'`;
    if (op === 'neq') return `${variable} != '${value.replace(/'/g, "\\'")}'`;
    if (op === 'eq_var') return `${variable} == ${value}`;
    if (op === 'neq_var') return `${variable} != ${value}`;
    return '';
  };

  const parts = cleanItems.map((it) => {
    if (it.kind === 'group') {
      const inner = (it.clauses || []).map(buildClause).filter(Boolean);
      if (!inner.length) return '';
      return `(${inner.join(' || ')})`;
    }
    return buildClause(it);
  }).filter(Boolean);

  return parts.join(` ${join === '||' ? '||' : '&&'} `).trim();
};

const TiptapEditor = forwardRef(({
  value = '',
  onChange,
  mode = 'edit',
  placeholder = 'Введите текст...',
  availableVariables = {},
  variableGroups = {},
  onVariableClick,
  showVariables = true, // новый параметр для контроля отображения переменных
  showConditions = true,
  bodyGuide = null,
  sectionMode = 'content',
  topInlineSlot = null,
  bottomInlineSlot = null,
  contentTopPaddingPx = null,
  topInlineGapPx = null,
  topInlineContentHeightPx = null,
  onMarginsChange,
  externalMargins = null,
  externalToolbarActive = false,
  externalToolbarState = null,
  onExternalToolbarCommand = null,
  performanceMode = false,
  onDirty = null,
}, ref) => {
  const { user } = useAuth();
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin' || !!user?.is_staff || !!user?.is_superuser;
  const isDebug = React.useCallback(() => {
    try { return localStorage.getItem('aot_debug_pagination') === '1'; }
    catch (_) { return false; }
  }, []);
  const [editorZoom, setEditorZoom] = React.useState(1);
  const topInlineSlotRef = React.useRef(null);
  const [topInlineMeasuredHeight, setTopInlineMeasuredHeight] = React.useState(0);
  const [zoomReady, setZoomReady] = React.useState(false);
  const [fullscreen, setFullscreen] = React.useState(false);
  const [showVariablesLocal, setShowVariablesLocal] = React.useState(false);
  const [showVariableSearch, setShowVariableSearch] = React.useState(false);
  const [variableSearch, setVariableSearch] = React.useState('');
  const variableSearchInputRef = React.useRef(null);
  const wrapperRef = React.useRef(null);
  const mainContainerRef = React.useRef(null);
  const a4ContainerRef = React.useRef(null);
		  const conditionalGutterRef = React.useRef(null);
		  const [conditionalGutterItems, setConditionalGutterItems] = React.useState([]);
		  const [activeConditionalGutter, setActiveConditionalGutter] = React.useState(null);
		  const [selectedConditionalGroupId, setSelectedConditionalGroupId] = React.useState(null);
		  const conditionalPopoverRef = React.useRef(null);
		  const scrollBasesRef = React.useRef(new WeakMap());
		  const restoreConditionalGutterRef = React.useRef(null);
		  const restoreConditionalGutterOnCloseRef = React.useRef(false);
		  const conditionalResizeLayoutRef = React.useRef({ a4Rect: null, scaleX: 1, scaleY: 1 });
		  const [conditionalResizeUI, setConditionalResizeUI] = React.useState(null);
		  const conditionalResizeDragRef = React.useRef(null);
		  const [conditionalResizeHandlesVisible, setConditionalResizeHandlesVisible] = React.useState({ start: false, end: false });
	  const scrollPrimaryRef = React.useRef(null);
  const pendingGutterResetRef = React.useRef(false);
  const updateConditionalGutterRef = React.useRef(null);
  const deferredChangeTimerRef = React.useRef(null);

  const [conditionEditorOpen, setConditionEditorOpen] = React.useState(false);
  const [conditionEditorTitle, setConditionEditorTitle] = React.useState('Условие показа блока');
  const [conditionEditorState, setConditionEditorState] = React.useState(() => parseConditionToBuilder("debtor_type_code == 'legal'"));
  const conditionEditorApplyRef = React.useRef(null);

  const [conditionPresets, setConditionPresets] = React.useState([]);
  const [conditionPresetsLoading, setConditionPresetsLoading] = React.useState(false);
  const [conditionPresetsError, setConditionPresetsError] = React.useState('');
  const [conditionPresetsTab, setConditionPresetsTab] = React.useState('global'); // global | mine
  const [presetDraft, setPresetDraft] = React.useState({ id: null, name: '', is_global: false });
  const [loopEditorOpen, setLoopEditorOpen] = React.useState(false);
  const [loopEditorState, setLoopEditorState] = React.useState({ loop: '', listCode: '', pos: null, actions: null });
  const loopEditorInputRef = React.useRef(null);
  const loopEditorSelectRef = React.useRef(null);
  const [footerVisualSpacer, setFooterVisualSpacer] = React.useState(0);

  const measureSectionContentHeight = React.useCallback(() => {
    try {
      const prose = wrapperRef.current?.querySelector?.('.tiptap-editor .ProseMirror');
      if (!prose) return 0;
      const children = Array.from(prose.children || []);
      if (children.length === 0) {
        const cs = window.getComputedStyle(prose);
        const lineHeight = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) || 0;
        return Math.max(0, lineHeight);
      }
      const first = children[0].getBoundingClientRect();
      const last = children[children.length - 1].getBoundingClientRect();
      return Math.max(0, (last.bottom - first.top));
    } catch (_) {
      return 0;
    }
  }, []);

  const loadConditionPresets = React.useCallback(async () => {
    setConditionPresetsLoading(true);
    setConditionPresetsError('');
    try {
      const res = await apiService.getConditionPresets();
      const data = res?.data;
      const items = Array.isArray(data)
        ? data
        : (Array.isArray(data?.results) ? data.results : []);
      setConditionPresets(items);
    } catch (e) {
      setConditionPresetsError('Не удалось загрузить пресеты условий');
    } finally {
      setConditionPresetsLoading(false);
    }
  }, []);

  const getConditionVariableLabel = React.useCallback((varCode) => {
    const code = String(varCode || '').trim();
    if (!code) return '';
    const meta = getConditionVarMeta(code);
    if (meta?.label) return String(meta.label);
    const fromAll = availableVariables?.[code];
    if (fromAll) return String(fromAll);
    return code;
  }, [availableVariables]);

  const formatConditionClause = React.useCallback((c) => {
    const varCode = String(c?.variable || '').trim();
    const op = String(c?.op || '').trim();
    const valueRaw = String(c?.value || '').trim();
    const varLabel = getConditionVariableLabel(varCode);
    if (!varLabel || !op) return '';
    if (op === 'not_empty') return `${varLabel} = not empty`;
    if (op === 'empty') return `${varLabel} = empty`;
    if (op === 'contains') return `${varLabel} contains «${valueRaw}»`;
    if (op === 'eq' || op === 'neq') {
      const valueLabel = getConditionValueLabel(varCode, valueRaw);
      const shown = valueLabel ? valueLabel : valueRaw;
      return `${varLabel} ${op === 'eq' ? '=' : '!='} ${shown}`;
    }
    if (op === 'eq_var' || op === 'neq_var') {
      const rhsLabel = getConditionVariableLabel(valueRaw);
      return `${varLabel} ${op === 'eq_var' ? '=' : '!='} ${rhsLabel}`;
    }
    return `${varLabel} ${op} ${valueRaw}`.trim();
  }, [getConditionVariableLabel]);

  const formatConditionExpression = React.useCallback((expression, { multiline }) => {
    const raw = sanitizeConditionText(expression);
    if (!raw) return '';

    const parsed = tryParseMixedToBuilder(raw) || parseConditionToBuilder(raw);
    const isMultiline = !!multiline;

    const joinToSymbol = (j) => (String(j) === '||' ? '||' : '&');
    const joinSeparator = (j) => (isMultiline ? ` ${joinToSymbol(j)}\n` : ` ${joinToSymbol(j)} `);

    if (parsed?.mode === 'builder') {
      const items = normalizeBuilderClauses(parsed.clauses);
      const parts = items.map((it) => {
        if (it.kind === 'group') {
          const inner = (it.clauses || []).map(formatConditionClause).filter(Boolean);
          if (!inner.length) return '';
          if (!isMultiline) return `(${inner.join(' || ')})`;
          if (inner.length === 1) return `(${inner[0]})`;
          return `(\n  ${inner.join(' ||\n  ')}\n)`;
        }
        return formatConditionClause(it);
      }).filter(Boolean);
      return parts.join(joinSeparator(parsed.join));
    }

    // Advanced: делаем «кодовый» вид, но с русскими лейблами переменных
    let out = String(raw);
    out = out
      .replace(/\bnot_empty\(\s*([\w.]+)\s*\)/gi, (_m, v) => `${getConditionVariableLabel(v)} = not empty`)
      .replace(/\bempty\(\s*([\w.]+)\s*\)/gi, (_m, v) => `${getConditionVariableLabel(v)} = empty`)
      .replace(/\bcontains\(\s*([\w.]+)\s*,\s*['"]([^'"]+)['"]\s*\)/gi, (_m, v, s) => `${getConditionVariableLabel(v)} contains «${s}»`)
      .replace(/\b([\w.]+)\s*(==|!=)\s*(['"])([^'"]+)\3/g, (_m, v, op, _q, val) => {
        const varLabel = getConditionVariableLabel(v);
        const valueLabel = getConditionValueLabel(v, val);
        const shown = valueLabel ? valueLabel : val;
        return `${varLabel} ${op === '==' ? '=' : '!='} ${shown}`;
      })
      .replace(/\b([\w.]+)\s*(==|!=)\s*([\w.]+)/g, (_m, v, op, rhs) => {
        const varLabel = getConditionVariableLabel(v);
        const rhsLabel = getConditionVariableLabel(rhs);
        return `${varLabel} ${op === '==' ? '=' : '!='} ${rhsLabel}`;
      });

    // Разделители
    if (isMultiline) {
      out = out
        .replace(/\s&&\s/g, ' &\n')
        .replace(/\s\|\|\s/g, ' ||\n');
    } else {
      out = out
        .replace(/\s&&\s/g, ' & ')
        .replace(/\s\|\|\s/g, ' || ');
    }

    // Лёгкая нормализация скобок: если есть группы ИЛИ — попробуем красиво отформатировать
    if (isMultiline && raw.includes('&&') && raw.includes('||')) {
      const mixed = tryParseMixedToBuilder(raw);
      if (mixed) {
        const items = normalizeBuilderClauses(mixed.clauses);
        const parts = items.map((it) => {
          if (it.kind === 'group') {
            const inner = (it.clauses || []).map(formatConditionClause).filter(Boolean);
            if (!inner.length) return '';
            if (inner.length === 1) return `(${inner[0]})`;
            return `(\n  ${inner.join(' ||\n  ')}\n)`;
          }
          return formatConditionClause(it);
        }).filter(Boolean);
        return parts.join(' &\n');
      }
    }

    return out;
  }, [formatConditionClause, getConditionVariableLabel]);

  const formatConditionForTooltip = React.useCallback(
    (expression) => formatConditionExpression(expression, { multiline: false }),
    [formatConditionExpression]
  );

  const formatConditionForPopover = React.useCallback(
    (expression) => formatConditionExpression(expression, { multiline: true }),
    [formatConditionExpression]
  );

  const getConditionEditorExpression = React.useCallback((state) => {
    const s = state || conditionEditorState;
    const raw = sanitizeConditionText(s?.raw || '');
    if (s?.mode === 'advanced') return raw;
    return sanitizeConditionText(buildConditionFromBuilder(s));
  }, [conditionEditorState]);

  const savePresetFromCurrentCondition = React.useCallback(async () => {
    const targetIsGlobal = conditionPresetsTab === 'global';
    if (targetIsGlobal && !isAdmin) {
      alert('Общие пресеты может создавать только администратор');
      return;
    }
    const name = String(presetDraft.name || '').trim();
    if (!name) {
      alert('Введите название пресета');
      return;
    }
    const expression = getConditionEditorExpression(conditionEditorState);
    if (!expression) {
      alert('Условие пустое');
      return;
    }
    try {
      setConditionPresetsError('');
      if (presetDraft.id) {
        await apiService.updateConditionPreset(presetDraft.id, {
          name,
          expression,
          is_global: isAdmin ? !!presetDraft.is_global : false,
          is_active: true,
        });
      } else {
        await apiService.createConditionPreset({
          name,
          expression,
          is_global: targetIsGlobal && isAdmin,
          is_active: true,
        });
      }
      setPresetDraft({ id: null, name: '', is_global: targetIsGlobal && isAdmin });
      await loadConditionPresets();
    } catch (e) {
      const message = e?.response?.data?.detail || e?.response?.data?.error || 'Не удалось сохранить пресет';
      setConditionPresetsError(String(message));
    }
  }, [presetDraft, conditionEditorState, getConditionEditorExpression, loadConditionPresets, conditionPresetsTab, isAdmin]);

  const editPreset = React.useCallback((preset) => {
    if (!preset) return;
    setConditionPresetsTab(preset.is_global ? 'global' : 'mine');
    setConditionEditorState(parseConditionToBuilder(preset.expression || preset.value || ''));
    setPresetDraft({ id: preset.id, name: preset.name || preset.label || '', is_global: !!preset.is_global });
  }, []);

  const deletePreset = React.useCallback(async (preset) => {
    if (!preset?.id) return;
    const ok = window.confirm(`Удалить пресет ${preset.name}?`);
    if (!ok) return;
    try {
      setConditionPresetsError('');
      await apiService.deleteConditionPreset(preset.id);
      await loadConditionPresets();
    } catch (e) {
      const message = e?.response?.data?.detail || e?.response?.data?.error || 'Не удалось удалить пресет';
      setConditionPresetsError(String(message));
    }
  }, [loadConditionPresets]);

  // Блокировка прокрутки основного окна при полноэкранном режиме
  React.useEffect(() => {
    if (fullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [fullscreen]);

  // Определяем, нужно ли показывать переменные - если параметр showVariables false, то не показываем вообще
  React.useEffect(() => {
    if (!showVariables) {
      setShowVariableSearch(false);
      setVariableSearch('');
    }
  }, [showVariables]);
  const shouldShowVariables = showVariables && showVariablesLocal;
  React.useEffect(() => {
    if (!shouldShowVariables) {
      setShowVariableSearch(false);
      setVariableSearch('');
    }
  }, [shouldShowVariables]);
  const enableConditionTools = mode === 'edit' && showConditions && !performanceMode;
  const [, forceToolbarRender] = React.useState(0); // rerender toolbar on selection changes

  const normalizedVariableSearch = React.useMemo(
    () => variableSearch.trim().toLowerCase(),
    [variableSearch]
  );

  const filteredVariableGroups = React.useMemo(() => {
    const entries = Object.entries(variableGroups || {});
    if (!normalizedVariableSearch) {
      return entries.map(([groupName, variables]) => ({
        name: groupName,
        variables
      }));
    }

    return entries
      .map(([groupName, variables]) => {
        const filtered = variables.filter(variable => {
          const description = availableVariables?.[variable] || variable;
          const haystack = `${description} ${variable}`.toLowerCase();
          return haystack.includes(normalizedVariableSearch);
        });
        return filtered.length ? { name: groupName, variables: filtered } : null;
      })
      .filter(Boolean);
  }, [variableGroups, availableVariables, normalizedVariableSearch]);

  const highlightVariableMatch = React.useCallback(
    (text) => {
      if (!normalizedVariableSearch) return text;
      const safeQuery = normalizedVariableSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${safeQuery})`, 'ig');
      const parts = String(text).split(regex);
      return parts.map((part, index) => {
        const isMatch = part.toLowerCase() === normalizedVariableSearch;
        return (
          <span key={index} className={isMatch ? 'highlight-match' : undefined}>
            {part}
          </span>
        );
      });
    },
    [normalizedVariableSearch]
  );

  // Вспомогательные функции для кодирования/декодирования полей в HTML.
  // Внутренне храним поля в px (для существующего рендера/экспорта), в UI показываем мм.
  const [margins, setMargins] = React.useState(() => {
    if (hasValidMargins(externalMargins)) {
      return normalizeMargins(externalMargins, DEFAULT_MARGINS_PX);
    }
    return normalizeMargins(extractMargins(value), DEFAULT_MARGINS_PX);
  });
  const [showMarginsPanel, setShowMarginsPanel] = React.useState(false);
  const [showHiddenChars, setShowHiddenChars] = React.useState(false);
  const [marginInputsMm, setMarginInputsMm] = React.useState(() => ({
    top: formatMmInput(DEFAULT_MARGINS_PX.top),
    right: formatMmInput(DEFAULT_MARGINS_PX.right),
    bottom: formatMmInput(DEFAULT_MARGINS_PX.bottom),
    left: formatMmInput(DEFAULT_MARGINS_PX.left),
  }));

  React.useEffect(() => {
    if (sectionMode !== 'content') return;
    if (hasValidMargins(externalMargins)) return;
    const parsed = extractMargins(value);
    // Не сбрасываем на дефолт, если в текущем HTML временно нет маркера полей.
    // Это позволяет стабильно редактировать значения в тулбаре без «отката».
    if (!parsed || typeof parsed !== 'object') return;
    setMargins((prev) => normalizeMargins(parsed, prev || DEFAULT_MARGINS_PX));
  }, [value, sectionMode, externalMargins]);

  React.useEffect(() => {
    if (!hasValidMargins(externalMargins)) return;
    const nextMargins = normalizeMargins(externalMargins, DEFAULT_MARGINS_PX);
    setMargins((prev) => (areMarginsEqual(prev, nextMargins) ? prev : nextMargins));
  }, [externalMargins]);

  React.useEffect(() => {
    if (!showMarginsPanel) return;
    setMarginInputsMm({
      top: formatMmInput(margins.top),
      right: formatMmInput(margins.right),
      bottom: formatMmInput(margins.bottom),
      left: formatMmInput(margins.left),
    });
  }, [showMarginsPanel, margins.top, margins.right, margins.bottom, margins.left]);

  const handleMarginInputChange = React.useCallback((side, valueInMm) => {
    setMarginInputsMm((prev) => ({ ...(prev || {}), [side]: String(valueInMm ?? '') }));
  }, []);

  const commitMarginInput = React.useCallback((side) => {
    const parsed = parseMmInput(marginInputsMm?.[side]);
    if (parsed === null) {
      setMarginInputsMm((prev) => ({ ...(prev || {}), [side]: formatMmInput(margins?.[side]) }));
      return;
    }
    const nextPx = mmToPx(parsed);
    const nextMargins = { ...(margins || {}), [side]: nextPx };
    setMargins((prev) => ({ ...(prev || {}), [side]: nextPx }));
    // Сообщаем родителю сразу, чтобы сохранение после blur не использовало старые поля.
    if (typeof onMarginsChange === 'function') {
      try { onMarginsChange(nextMargins); } catch (_) { }
    }
    setMarginInputsMm((prev) => ({ ...(prev || {}), [side]: String(parsed).replace(/\.0$/, '') }));
  }, [marginInputsMm, margins, setMargins, onMarginsChange]);

  const getEffectiveMargins = React.useCallback(() => {
    const next = { ...(margins || {}) };
    ['top', 'right', 'bottom', 'left'].forEach((side) => {
      const parsed = parseMmInput(marginInputsMm?.[side]);
      if (parsed !== null) next[side] = mmToPx(parsed);
    });
    return normalizeMargins(next, DEFAULT_MARGINS_PX);
  }, [margins, marginInputsMm]);

  React.useLayoutEffect(() => {
    let raf1 = 0;
    let raf2 = 0;
    const recalc = () => {
      const measured = measureSectionContentHeight();
      if (sectionMode === 'footer') {
        const contentAreaHeight = Math.max(0, 1123 - Number(margins.top || 0) - Number(margins.bottom || 0));
        const spacer = Math.max(0, contentAreaHeight - measured);
        setFooterVisualSpacer(spacer);
      } else {
        setFooterVisualSpacer(0);
      }
    };
    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(recalc);
    });
    return () => {
      if (raf1) window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
    };
  }, [sectionMode, margins.top, margins.bottom, value, measureSectionContentHeight]);

  // Обработка содержимого
  const processContent = useMemo(() => {
    if (!value) return '<p></p>';
    let processed = stripMarginsTag(value);

    // Если это простой текст без HTML, конвертируем в параграфы
    if (!value.includes('<p>') && !value.includes('<br>') && !value.includes('<h')) {
      const paragraphs = value.split('\n').filter(p => p.trim());
      processed = paragraphs.length > 0 ? paragraphs.map(p => `<p>${p}</p>`).join('') : '<p></p>';
    }

    // Рендерим циклы и условные блоки в удобный вид
    processed = renderLoopBlocks(processed);
    processed = renderConditionalBlocks(processed);
    processed = normalizeEdgeWhitespaceAndEmptyParagraphs(processed);

    // В режиме редактирования убираем HTML-подсветку переменных
    if (mode === 'edit') {
      processed = processed.replace(
        /<span class="template-variable">(\{\{[^}]+\}\})<\/span>/g,
        '$1'
      );
    }

    return processed;
  }, [value, mode]);

  // Мемоизируем конфигурацию
  const editorConfig = useMemo(() => ({
    extensions: [
      StarterKit.configure({
        heading: false,
        underline: false, // отключаем встроенный — используем отдельное расширение
        hardBreak: false, // отключаем встроенный, используем свой
      }),
      Underline,
      HardBreak.configure({
        HTMLAttributes: {
          class: 'hard-break',
        },
      }),
      TabExtension,  // Добавляем поддержку табуляции
      Placeholder.configure({
        placeholder: placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
      TextStyle,
      FontFamily.configure({
        types: ['textStyle'],
      }),
      FontSizeExtension.configure({
        types: ['textStyle'],
      }),
      ...(performanceMode ? [] : [NewlineMarkersExtension]),
      LineHeightExtension,
      ParagraphSpacingExtension,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TableWithDisplaySettings.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCellWithVerticalAlign,
      // Разрывы страниц: автоматические «прокладки» + ручные разрывы
      PageSpacerExtension,
      PageBreakExtension,
      ...(performanceMode ? [] : [BackgroundPagesOverlay]),
      ConditionalSection,
      ConditionalBlock.configure({ interactive: enableConditionTools }),
      LoopSection,
      // Добавляем подсветку переменных только в режиме редактирования
      ...(mode === 'edit' && showVariables && !performanceMode ? [
        SmartVariableExtension.configure({ availableVariables }),
        GenderAgreementExtension
      ] : [])
    ],
    content: processContent,
    editable: mode === 'edit',
    immediatelyRender: false
  }), [mode, placeholder, processContent, availableVariables, performanceMode, showVariables, enableConditionTools]);

  const editor = useEditor(editorConfig);

  const syncTableDisplayStyles = React.useCallback(() => {
    if (!editor?.view?.dom || !editor?.state?.doc) return;
    try {
      const tableAttrs = [];
      editor.state.doc.descendants((node) => {
        if (node?.type?.name === 'table') {
          tableAttrs.push({
            borderWidth: String(node.attrs?.tableBorderWidth || '').trim(),
            rowMinHeight: String(node.attrs?.tableRowMinHeight || '').trim(),
            cellPadding: String(node.attrs?.tableCellPadding || '').trim(),
            marginVert: String(node.attrs?.tableMarginVert || '').trim(),
          });
        }
      });

      const tableEls = Array.from(editor.view.dom.querySelectorAll('.ProseMirror table'));
      tableEls.forEach((tableEl, index) => {
        const attrs = tableAttrs[index] || {};
        const bw = String(attrs.borderWidth || '').trim();
        const mh = String(attrs.rowMinHeight || '').trim();
        const cp = String(attrs.cellPadding || '').trim();
        const mv = String(attrs.marginVert || '').trim();
        if (bw) tableEl.style.setProperty('--table-border-width', bw);
        else tableEl.style.removeProperty('--table-border-width');
        if (mh) tableEl.style.setProperty('--table-row-min-height', mh);
        else tableEl.style.removeProperty('--table-row-min-height');
        if (cp) tableEl.style.setProperty('--table-cell-padding', cp);
        else tableEl.style.removeProperty('--table-cell-padding');
        if (mv) tableEl.style.setProperty('--table-margin-vert', mv);
        else tableEl.style.removeProperty('--table-margin-vert');
      });
    } catch (_) { }
  }, [editor]);

  const findLoopAtPos = React.useCallback((state, pos) => {
    if (!state) return null;
    try {
      const $pos = state.doc.resolve(pos);
      for (let d = $pos.depth; d > 0; d -= 1) {
        const node = $pos.node(d);
        if (node?.type?.name === 'loopSection') {
          return { node, pos: $pos.before(d), depth: d };
        }
      }
    } catch (_) { }
    return null;
  }, []);

  const computeLoopActions = React.useCallback((state, info) => {
    if (!state || !info) return { canExpandUp: false, canExpandDown: false, canShrinkUp: false, canShrinkDown: false };
    const $pos = state.doc.resolve(info.pos);
    const parent = $pos.parent;
    const index = $pos.index();
    const canExpandUp = index > 0;
    const canExpandDown = index < parent.childCount - 1;
    const canShrink = info.node.childCount > 1;
    return {
      canExpandUp,
      canExpandDown,
      canShrinkUp: canShrink,
      canShrinkDown: canShrink,
    };
  }, []);

  const openLoopEditorFromPos = React.useCallback((pos) => {
    if (!editor) return;
    const info = findLoopAtPos(editor.state, pos);
    if (!info) {
      setLoopEditorOpen(false);
      setLoopEditorState({ loop: '', listCode: '', pos: null, actions: null });
      return;
    }
    const parsed = parseLoopExpression(info.node?.attrs?.loop || '');
    const listCode = LOOP_LIST_OPTIONS.some((opt) => opt.value === parsed.list) ? parsed.list : '';
    setLoopEditorState({
      loop: info.node?.attrs?.loop || '',
      listCode,
      pos: info.pos,
      actions: computeLoopActions(editor.state, info),
    });
    setLoopEditorOpen(true);
  }, [editor, computeLoopActions, findLoopAtPos]);

  const closeLoopEditor = React.useCallback(() => {
    setLoopEditorOpen(false);
  }, []);

  const updateLoopExpression = React.useCallback((value) => {
    if (!editor) return;
    const info = findLoopAtPos(editor.state, loopEditorState.pos ?? editor.state.selection.from);
    if (!info) return;
    const attrs = { ...(info.node.attrs || {}), loop: value };
    const tr = editor.state.tr.setNodeMarkup(info.pos, undefined, attrs);
    editor.view.dispatch(tr);
    setLoopEditorState((prev) => ({ ...prev, loop: value }));
  }, [editor, loopEditorState.pos, findLoopAtPos]);

  const updateLoopList = React.useCallback((listCode) => {
    if (!editor) return;
    if (!listCode) return;
    const loopExpr = `item in ${listCode}`;
    updateLoopExpression(loopExpr);
    setLoopEditorState((prev) => ({ ...prev, listCode, loop: loopExpr }));
  }, [editor, updateLoopExpression]);

  const moveLoopBoundary = React.useCallback((direction) => {
    if (!editor) return;
    const { state } = editor;
    const info = findLoopAtPos(state, loopEditorState.pos ?? state.selection.from);
    if (!info) return;
    const { node, pos } = info;
    const $pos = state.doc.resolve(pos);
    const parent = $pos.parent;
    const index = $pos.index();
    let tr = state.tr;

    if (direction === 'expandUp') {
      if (index <= 0) return;
      const prev = parent.child(index - 1);
      const from = pos - prev.nodeSize;
      const to = pos;
      const slice = state.doc.slice(from, to);
      tr = tr.delete(from, to);
      const newPos = pos - prev.nodeSize;
      tr = tr.insert(newPos + 1, slice.content);
      tr.setSelection(TextSelection.create(tr.doc, newPos + 1));
      editor.view.dispatch(tr);
      openLoopEditorFromPos(newPos + 1);
      return;
    }

    if (direction === 'expandDown') {
      if (index >= parent.childCount - 1) return;
      const next = parent.child(index + 1);
      const from = pos + node.nodeSize;
      const to = from + next.nodeSize;
      const slice = state.doc.slice(from, to);
      tr = tr.delete(from, to);
      const insertPos = pos + node.nodeSize - 1;
      tr = tr.insert(insertPos, slice.content);
      tr.setSelection(TextSelection.create(tr.doc, insertPos - 1));
      editor.view.dispatch(tr);
      openLoopEditorFromPos(insertPos - 1);
      return;
    }

    if (direction === 'shrinkUp') {
      if (node.childCount <= 1) return;
      const first = node.child(0);
      const from = pos + 1;
      const to = from + first.nodeSize;
      const slice = state.doc.slice(from, to);
      tr = tr.delete(from, to);
      tr = tr.insert(pos, slice.content);
      tr.setSelection(TextSelection.create(tr.doc, pos + slice.content.size));
      editor.view.dispatch(tr);
      openLoopEditorFromPos(pos + slice.content.size);
      return;
    }

    if (direction === 'shrinkDown') {
      if (node.childCount <= 1) return;
      const last = node.child(node.childCount - 1);
      const offset = node.content.size - last.nodeSize;
      const from = pos + 1 + offset;
      const to = from + last.nodeSize;
      const slice = state.doc.slice(from, to);
      tr = tr.delete(from, to);
      const newLoopEnd = pos + node.nodeSize - last.nodeSize;
      tr = tr.insert(newLoopEnd, slice.content);
      tr.setSelection(TextSelection.create(tr.doc, newLoopEnd + 1));
      editor.view.dispatch(tr);
      openLoopEditorFromPos(newLoopEnd + 1);
    }
  }, [editor, loopEditorState.pos, findLoopAtPos, openLoopEditorFromPos]);

  const unwrapLoop = React.useCallback(() => {
    if (!editor) return;
    const info = findLoopAtPos(editor.state, loopEditorState.pos ?? editor.state.selection.from);
    if (!info) return;
    const { node, pos } = info;
    const tr = editor.state.tr.replaceWith(pos, pos + node.nodeSize, node.content);
    editor.view.dispatch(tr);
    closeLoopEditor();
  }, [editor, loopEditorState.pos, findLoopAtPos, closeLoopEditor]);
  const zoomWrapperRef = React.useRef(null);

  // (Отладочная визуализация layout'а удалена — больше не нужна)

  // В стандартном режиме подгоняем МАСШТАБ под ширину области редактирования.
  // Важно: именно масштабирование (zoom), а не изменение ширины страницы — иначе будет «рефлоу»
  // (текст станет помещаться иначе по строкам), что пользователю не подходит.
  React.useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const main = mainContainerRef.current;
    const zoomWrapper = zoomWrapperRef.current;

    if (fullscreen) {
      setEditorZoom(1);
      setZoomReady(true);
      return;
    }
    // При первом открытии модалки editor создаётся асинхронно, и этот эффект должен
    // повторно отработать после появления DOM. Поэтому зависимость включает `editor`.
    // До момента корректного измерения скрываем страницу через zoomReady=false.
    setZoomReady(false);
    if (!wrapper || !main) return;

    const BASE_WIDTH = 794; // ширина A4 в px при 96dpi
    let raf = 0;
    let t1 = 0;
    let t2 = 0;
    let t3 = 0;
    let t4 = 0;
    let settleRaf = 0;
    let settleFrames = 0;

    const measure = () => {
      raf = 0;
      try {
        // Берём "видимую" ширину (getBoundingClientRect), т.к. при модалках/оверлеях
        // размеры в layout-единицах могут отличаться от фактической видимой ширины (особенно при трансформациях).
        const zwRectW = Number(zoomWrapper?.getBoundingClientRect?.().width || 0);
        const mainRectW = Number(main.getBoundingClientRect?.().width || 0);
        // Если zoomWrapper уже измерим — доверяем ему (это "синий" контейнер вокруг страницы).
        // mainRectW иногда может быть больше из-за особенностей раскладки/скроллбаров.
        const baseAvailable = (zwRectW > 0 ? zwRectW : mainRectW);

        // Если модалка ещё не разложилась и ширина подозрительно маленькая — не фиксируем zoom,
        // а продолжаем "догонять" следующие кадры.
        if (baseAvailable < 320) {
          // Важно: transform/transition при открытии модалки не триггерит ResizeObserver.
          // Поэтому добавляем отложенный пересчёт.
          window.setTimeout(schedule, 120);
          setZoomReady(false);
          return;
        }

        // Запас под тень/границы/округления, чтобы не клиппилось по горизонтали
        const available = Math.max(260, baseAvailable - 18);
        const zoom = clamp(available / BASE_WIDTH, 0.72, 1.8);
        setEditorZoom(zoom);
        setZoomReady(true);

        // Диагностика размеров доступна вручную через window.__AOT_dumpTemplateEditorLayout()
      } catch (_) { }
    };

    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(measure);
    };

    const ro = new ResizeObserver(() => schedule());
    try { ro.observe(main); } catch (_) { }
    try { if (zoomWrapper) ro.observe(zoomWrapper); } catch (_) { }
    window.addEventListener('resize', schedule);
    // ВАЖНО: первый расчёт делаем синхронно в useLayoutEffect, чтобы не было "мигания"
    // (первый кадр с zoom=1, затем кадр с рассчитанным zoom).
    measure();
    schedule();
    // Доп. измерения после открытия модалки/рендера боковой панели.
    // В некоторых случаях initial clientWidth == 0/не финальный, и масштаб остаётся 1 до первого "большого" события.
    t1 = window.setTimeout(schedule, 60);
    t2 = window.setTimeout(schedule, 260);
    t3 = window.setTimeout(schedule, 650);
    t4 = window.setTimeout(schedule, 1200);
    // И ещё: несколько кадров подряд после маунта, чтобы "поймать" финальные размеры без DevTools/рефлоу.
    const settle = () => {
      if (settleFrames >= 16) return;
      settleFrames += 1;
      schedule();
      settleRaf = window.requestAnimationFrame(settle);
    };
    settleRaf = window.requestAnimationFrame(settle);
    return () => {
      try { ro.disconnect(); } catch (_) { }
      try { window.removeEventListener('resize', schedule); } catch (_) { }
      if (raf) window.cancelAnimationFrame(raf);
      if (t1) window.clearTimeout(t1);
      if (t2) window.clearTimeout(t2);
      if (t3) window.clearTimeout(t3);
      if (t4) window.clearTimeout(t4);
      if (settleRaf) window.cancelAnimationFrame(settleRaf);
    };
  }, [fullscreen, editor]);

  // Обновляем тулбар при изменении выделения/курсорной позиции
  React.useEffect(() => {
    if (!editor) return;
    const refresh = () => forceToolbarRender(v => v + 1);
    editor.on('selectionUpdate', refresh);
    editor.on('transaction', refresh);
    editor.on('focus', refresh);
    editor.on('blur', refresh);
    refresh();
    return () => {
      editor.off('selectionUpdate', refresh);
      editor.off('transaction', refresh);
      editor.off('focus', refresh);
      editor.off('blur', refresh);
    };
  }, [editor, mode, selectedConditionalGroupId]);

  React.useEffect(() => {
    if (!editor) return;
    const sync = () => {
      window.requestAnimationFrame(() => {
        syncTableDisplayStyles();
      });
    };
    editor.on('update', sync);
    sync();
    return () => {
      editor.off('update', sync);
    };
  }, [editor, syncTableDisplayStyles]);

  // Убрана логика showInvisibles

  // Отдельный useEffect для обработки onUpdate
  React.useEffect(() => {
    if (editor && onChange && mode === 'edit') {
      try { window.__editor = editor; } catch (_) { }
      const emitChange = () => {
        // Прокидываем поля вместе с содержимым как скрытый тег в начале HTML
        let html = editor.getHTML();
        html = serializeLoopBlocks(html);
        html = serializeConditionalBlocks(html);
        html = normalizeEdgeWhitespaceAndEmptyParagraphs(html);
        const contentWithoutTag = stripMarginsTag(html);
        const withTag = encodeMarginsTag(margins) + contentWithoutTag;
        onChange(withTag);
      };
      const clearDeferred = () => {
        if (deferredChangeTimerRef.current) {
          window.clearTimeout(deferredChangeTimerRef.current);
          deferredChangeTimerRef.current = null;
        }
      };
      const flushDeferred = () => {
        clearDeferred();
        emitChange();
      };
      const handleUpdate = () => {
        if (!performanceMode) {
          emitChange();
          return;
        }
        if (typeof onDirty === 'function') {
          try { onDirty(); } catch (_) { }
        }
        clearDeferred();
        deferredChangeTimerRef.current = window.setTimeout(() => {
          deferredChangeTimerRef.current = null;
          emitChange();
        }, 700);
      };

      editor.on('update', handleUpdate);
      editor.on('blur', flushDeferred);

      return () => {
        editor.off('update', handleUpdate);
        editor.off('blur', flushDeferred);
        clearDeferred();
      };
    }
  }, [editor, onChange, onDirty, mode, margins, performanceMode]);

  // Обновляем содержимое редактора только при изменении value из props (не из редактора)
  React.useEffect(() => {
    if (editor && processContent !== undefined) {
      const currentContent = editor.getHTML();
      // Обновляем только если содержимое изменилось извне и редактор не в фокусе
      if (currentContent !== processContent && !editor.isFocused) {
        editor.commands.setContent(processContent, false);
        // ВАЖНО: setContent(..., false) не эмитит update, поэтому пересчёт IF-гуттера не запускается,
        // и бейджи появляются только после первого скролла. Принудительно пересчитываем после paint.
        let raf1 = 0;
        let raf2 = 0;
        let t = 0;
        const run = () => {
          try { updateConditionalGutterRef.current?.(); } catch (_) { }
        };
        raf1 = window.requestAnimationFrame(() => {
          raf2 = window.requestAnimationFrame(run);
        });
        t = window.setTimeout(run, 120);
        return () => {
          if (raf1) window.cancelAnimationFrame(raf1);
          if (raf2) window.cancelAnimationFrame(raf2);
          if (t) window.clearTimeout(t);
        };
      }
    }
  }, [editor, processContent]);

  // Открытие модалки для цикла по клику на заголовок
  React.useEffect(() => {
    if (!editor) return;
    const view = editor.view;
    const onClick = (event) => {
      const header = event.target?.closest?.('.loop-header');
      if (!header) return;
      const loopEl = header.closest('.loop-section');
      if (!loopEl) return;
      event.preventDefault();
      event.stopPropagation();
      const pos = view.posAtDOM(loopEl, 0);
      const selectionPos = Math.max(1, pos + 1);
      const tr = view.state.tr.setSelection(TextSelection.create(view.state.doc, selectionPos));
      view.dispatch(tr);
      openLoopEditorFromPos(selectionPos);
    };
    view.dom.addEventListener('click', onClick);
    return () => {
      view.dom.removeEventListener('click', onClick);
    };
  }, [editor, openLoopEditorFromPos]);

  React.useEffect(() => {
    if (!loopEditorOpen) return;
    const t = window.setTimeout(() => {
      if (loopEditorSelectRef.current) {
        loopEditorSelectRef.current.focus?.();
      } else {
        loopEditorInputRef.current?.focus?.();
        loopEditorInputRef.current?.select?.();
      }
    }, 0);
    return () => window.clearTimeout(t);
  }, [loopEditorOpen]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    window.__AOTLoopEditorOpen = !!loopEditorOpen;
    return () => {
      window.__AOTLoopEditorOpen = false;
    };
  }, [loopEditorOpen]);

  // Закрываем поповер, если курсор вышел из цикла
  React.useEffect(() => {
    if (!editor) return;
    const handleSelection = () => {
      const info = findLoopAtPos(editor.state, editor.state.selection.from);
      if (!info) {
        if (loopEditorOpen) {
          closeLoopEditor();
        }
        return;
      }
      if (loopEditorOpen && loopEditorState.pos !== info.pos) {
        openLoopEditorFromPos(editor.state.selection.from);
      }
    };
    editor.on('selectionUpdate', handleSelection);
    return () => {
      editor.off('selectionUpdate', handleSelection);
    };
  }, [editor, findLoopAtPos, loopEditorOpen, loopEditorState.pos, closeLoopEditor, openLoopEditorFromPos]);

  // Прокидываем в DOM оригинальный HTML предпросмотра (с тем же маркером полей)
  React.useEffect(() => {
    if (!editor) return;
    try {
      const root = editor.view?.dom;
      if (!root) return;
      let serialized = serializeLoopBlocks(processContent || '');
      serialized = serializeConditionalBlocks(serialized);
      const originalHtmlForPreview = encodeMarginsTag(margins) + stripMarginsTag(serialized);
      root.setAttribute('data-preview-html', originalHtmlForPreview);
      // Сообщаем слушателям (оверлею), что данные обновились
      const ev = new CustomEvent('previewHtmlChanged');
      root.dispatchEvent(ev);
    } catch (_) { }
  }, [editor, processContent, margins]);

  // Уведомляем плагины/родителя о смене полей (поля живут в самом шаблоне/документе).
  React.useEffect(() => {
    if (typeof onMarginsChange === 'function' && !hasValidMargins(externalMargins)) {
      try { onMarginsChange(margins); } catch (_) { }
    }
    if (editor) {
      if (isDebug()) console.log('[TiptapEditor] 📡 ОТПРАВКА события pageMarginsChanged с полями:', margins);
      const ev = new CustomEvent('pageMarginsChanged', { detail: { margins } });
      editor.view.dom.dispatchEvent(ev);
      // лог об отправке скрыт по умолчанию
    }

    // ВАЖНО: при изменении полей текста coordsAtPos меняется, но update/selectionUpdate может не происходить.
    // В итоге IF-бейджи/линии визуально «зависают» до первого скролла. Пересчитываем гуттер сразу.
    try {
      const gutterEl = conditionalGutterRef.current;
      if (gutterEl) gutterEl.style.transform = '';
    } catch (_) { }

    let raf1 = 0;
    let raf2 = 0;
    let t1 = 0;
    let t2 = 0;
    const run = () => {
      try { updateConditionalGutterRef.current?.(); } catch (_) { }
    };
    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(run);
    });
    t1 = window.setTimeout(run, 60);
    t2 = window.setTimeout(run, 180);
    try {
      if (document?.fonts?.ready) {
        document.fonts.ready.then(run).catch(() => {});
      }
    } catch (_) { }
    return () => {
      if (raf1) window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
      if (t1) window.clearTimeout(t1);
      if (t2) window.clearTimeout(t2);
    };
  }, [margins, editor, isDebug, onMarginsChange, externalMargins]);

  // Уведомляем PageWrapperExtension об изменении полноэкранного режима
  React.useEffect(() => {
    if (editor) {
      if (isDebug()) console.log('TiptapEditor - fullscreen changed to:', fullscreen);
      // Даем время на применение CSS классов
      setTimeout(() => {
        // Trigger re-evaluation of PageWrapperExtension
        const event = new CustomEvent('fullscreenModeChanged', {
          detail: { fullscreen }
        });
        editor.view.dom.dispatchEvent(event);
        // Просим автопагинацию прислать координаты разрывов (для отрисовки синих пунктиров)
        try {
          editor.view.dom.dispatchEvent(new CustomEvent('forcePagination'));
        } catch (_) { }
      }, 100);
    }
  }, [editor, fullscreen, isDebug]);

  // Исторически здесь подключался отдельный слой разрывов; сейчас он не используется
  React.useEffect(() => {
    if (!editor || !fullscreen) return;
    if (isDebug()) console.log('[TiptapEditor] Hard breaks layer disabled');

    // Очищаем старые жёсткие разделители если они есть
    const root = editor.view?.dom;
    if (root) {
      const oldLayer = root.querySelector('.a4-hard-breaks');
      if (oldLayer) {
        if (isDebug()) console.log('[TiptapEditor] Removing old hard breaks layer');
        oldLayer.remove();
      }
    }

    return () => {
      // Cleanup при размонтировании
      if (root) {
        const layer = root.querySelector('.a4-hard-breaks');
        try { layer && layer.remove(); } catch (_) { }
      }
    };
  }, [editor, fullscreen, isDebug]);

  React.useEffect(() => {
    if (showVariableSearch && variableSearchInputRef.current) {
      variableSearchInputRef.current.focus();
    }
  }, [showVariableSearch]);

  React.useEffect(() => {
    if (!fullscreen) {
      setShowVariableSearch(false);
      setVariableSearch('');
    }
  }, [fullscreen]);

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__AOTFullscreenVariableSearchActive = fullscreen && showVariableSearch;
      window.__AOTFullscreenVariablesVisible = fullscreen && shouldShowVariables;
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.__AOTFullscreenVariableSearchActive = false;
        window.__AOTFullscreenVariablesVisible = false;
      }
    };
  }, [fullscreen, showVariableSearch, shouldShowVariables]);

  React.useEffect(() => {
    const handleExternalClose = () => {
      setShowVariableSearch(false);
      setVariableSearch('');
    };
    document.addEventListener('tiptapCloseFullscreenVariableSearch', handleExternalClose, { capture: true });
    return () => {
      document.removeEventListener('tiptapCloseFullscreenVariableSearch', handleExternalClose, { capture: true });
    };
  }, []);

  // Выход из полноэкранного режима по Esc (перехват с приоритетом)
  React.useEffect(() => {
    const handlePanelClose = () => {
      setShowVariablesLocal(false);
    };
    document.addEventListener('tiptapCloseFullscreenVariablesPanel', handlePanelClose, { capture: true });
    return () => {
      document.removeEventListener('tiptapCloseFullscreenVariablesPanel', handlePanelClose, { capture: true });
    };
  }, []);

  React.useEffect(() => {
    const onKeyCapture = (e) => {
      if (e.key === 'Escape' && fullscreen) {
        if (showVariableSearch) {
          try { e.preventDefault(); } catch (_) { }
          try { e.stopImmediatePropagation(); } catch (_) { }
          try { e.stopPropagation(); } catch (_) { }
          setShowVariableSearch(false);
          setVariableSearch('');
          return false;
        }
        if (shouldShowVariables) {
          try { e.preventDefault(); } catch (_) { }
          try { e.stopImmediatePropagation(); } catch (_) { }
          try { e.stopPropagation(); } catch (_) { }
          setShowVariablesLocal(false);
          return false;
        }
        // Блокируем закрытие окружающих модалок и переводим редактор в обычный режим
        try { e.preventDefault(); } catch (_) { }
        try { e.stopImmediatePropagation(); } catch (_) { }
        try { e.stopPropagation(); } catch (_) { }
        setFullscreen(false);
        return false;
      }
    };
    // capture: true — чтобы перехватить раньше глобальных обработчиков
    document.addEventListener('keydown', onKeyCapture, { capture: true });
    return () => document.removeEventListener('keydown', onKeyCapture, { capture: true });
  }, [fullscreen, showVariableSearch, shouldShowVariables]);

  // Служебное событие — программный выход из полноэкранного режима
  React.useEffect(() => {
    const onRequestExit = () => {
      if (fullscreen) setFullscreen(false);
    };
    document.addEventListener('requestExitFullscreen', onRequestExit, { capture: true });
    return () => document.removeEventListener('requestExitFullscreen', onRequestExit, { capture: true });
  }, [fullscreen]);

  // Убрана кастомная логика - используем стандартную команду

  // Функция для вставки переменной в текущую позицию курсора
  const insertVariable = (variable) => {
    if (mode === 'edit') {
      // Если в полноэкранном режиме и есть активная страница
      if (fullscreen && window._activePageContent) {
        try {
          const selection = window.getSelection();
          let range;

          // Безопасно получаем range
          if (selection.rangeCount > 0) {
            range = selection.getRangeAt(0);
          } else {
            // Создаем новый range в начале активной области
            range = document.createRange();
            const firstNode = window._activePageContent.firstChild || window._activePageContent;
            if (firstNode.nodeType === Node.TEXT_NODE) {
              range.setStart(firstNode, 0);
            } else {
              range.setStart(firstNode, 0);
            }
            range.collapse(true);
          }

          // Создаем элемент переменной с подсветкой (используем только CSS классы)
          const variableSpan = document.createElement('span');
          variableSpan.className = 'template-variable-highlight';
          variableSpan.textContent = variable;

          range.insertNode(variableSpan);

          // Добавляем пробел после переменной
          const spaceNode = document.createTextNode(' ');
          range.setStartAfter(variableSpan);
          range.insertNode(spaceNode);

          // Устанавливаем курсор после пробела
          range.setStartAfter(spaceNode);
          range.setEndAfter(spaceNode);
          selection.removeAllRanges();
          selection.addRange(range);

          // Синхронизируем изменения обратно в ProseMirror
          const allPages = document.querySelectorAll('.page-content');
          let fullContent = '';
          allPages.forEach(page => {
            fullContent += page.innerHTML;
          });
          if (editor) {
            editor.commands.setContent(fullContent, false);
          }
        } catch (e) {
          console.error('Variable insertion error:', e);
          // Fallback - вставляем в конец активной области
          if (window._activePageContent) {
            const variableSpan = document.createElement('span');
            variableSpan.className = 'template-variable-highlight';
            variableSpan.textContent = variable;
            window._activePageContent.appendChild(variableSpan);
            window._activePageContent.appendChild(document.createTextNode(' '));
          }
        }
      } else if (editor) {
        // Обычный режим
        editor.chain().focus().insertContent(variable).run();
      }
    }
  };

  // Обработчик клика по переменной
  const handleVariableClick = (variableName, event) => {
    if (onVariableClick) {
      onVariableClick(variableName, event);
    } else {
      insertVariable(`{{${variableName}}}`);
    }
  };

  // Функция для импорта DOCX файла
  const importDocx = async (file) => {
    if (!file || !editor || mode !== 'edit') return;

    try {
      const arrayBuffer = await file.arrayBuffer();

      // Используем продвинутый процессор DOCX
      let processedHtml = await processAdvancedDocx(arrayBuffer);

      // Применяем дополнительную обработку таблиц
      processedHtml = processDocxTables(processedHtml);


      // Устанавливаем содержимое в редактор
      editor.commands.setContent(processedHtml);

      return processedHtml;
    } catch (error) {
      console.error('Ошибка при импорте DOCX:', error);

      // Fallback на старый метод при ошибке
      return importDocxFallback(file);
    }
  };

  // Fallback функция импорта (старый метод)
  const importDocxFallback = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();

      // Старые опции для mammoth
      const options = {
        convertImage: mammoth.images.imgElement(function (image) {
          return image.read("base64").then(function (imageBuffer) {
            return {
              src: "data:" + image.contentType + ";base64," + imageBuffer
            };
          });
        }),
        styleMap: [
          // Заголовки
          "p[style-name='Heading 1'] => h1",
          "p[style-name='Heading 2'] => h2",
          "p[style-name='Heading 3'] => h3",
          "p[style-name='Title'] => h1",
          "p[style-name='Subtitle'] => h2",
          // Выравнивание
          "p[style-name='Centered'] => p.text-center",
          "p[style-name='Right'] => p.text-right",
          "p[style-name='Justified'] => p.text-justify",
          // Таблицы
          "table => table",
          "tr => tr",
          "td => td",
          "th => th",
          "td[style-name='Table Cell Center'] => td.cell-center",
          "td[style-name='Table Cell Right'] => td.cell-right",
          // Сохраняем все параграфы со стилями
          "p => p:fresh",
          "r => span:fresh"
        ],
        includeDefaultStyleMap: false, // Отключаем стандартные стили для большего контроля
        includeEmbeddedStyleMap: true,
        preserveEmptyParagraphs: true, // Сохраняем пустые параграфы (для отступов)
        transformDocument: mammoth.transforms.paragraph(function (element) {
          // Извлекаем информацию о стилях параграфа
          const styleInfo = [];

          if (element.style) {
            // Добавляем информацию о стиле в класс для последующей обработки
            styleInfo.push(`style-${element.style}`);
          }

          if (element.alignment) {
            styleInfo.push(`align-${element.alignment}`);
          }

          if (styleInfo.length > 0) {
            return {
              ...element,
              styleId: styleInfo.join(' ')
            };
          }

          return element;
        })
      };

      const result = await mammoth.convertToHtml({ arrayBuffer }, options);

      // Логируем исходный HTML от mammoth для отладки

      // Дополнительная обработка HTML для лучшего сохранения форматирования
      let processedHtml = result.value;

      // Применяем обработку стилей (шрифты, размеры, выравнивание)
      processedHtml = processDocxStyles(processedHtml);

      // Сохраняем информацию о ширинах колонок из colgroup (если есть)
      const colgroupMatch = processedHtml.match(/<colgroup[^>]*>.*?<\/colgroup>/gs);
      let columnWidths = [];
      if (colgroupMatch) {
        const colMatches = colgroupMatch[0].matchAll(/<col[^>]*width="([^"]*)"[^>]*>/g);
        for (const match of colMatches) {
          columnWidths.push(match[1]);
        }
      }

      // Улучшенная обработка таблиц с сохранением размеров и выравнивания
      processedHtml = processedHtml.replace(/<table([^>]*)>/g, function (match, attributes) {
        // Базовые стили для всех таблиц
        let tableStyle = 'border-collapse: collapse; margin: 10px 0;';

        // Проверяем, есть ли уже width в атрибутах
        if (!attributes.includes('width') && !attributes.includes('style')) {
          tableStyle += ' width: 100%;';
        }

        // Если есть информация о ширине в атрибутах, используем её
        if (attributes.includes('width=')) {
          const widthMatch = attributes.match(/width="([^"]*)"/);
          if (widthMatch) {
            tableStyle += ` width: ${widthMatch[1]};`;
          }
        }

        return `<table${attributes} border="1" style="${tableStyle}">`;
      });

      // Анализируем структуру таблиц для автоматического определения ширины колонок
      const tables = processedHtml.match(/<table[^>]*>[\s\S]*?<\/table>/g) || [];
      tables.forEach(table => {
        // Подсчитываем количество колонок в первой строке
        const firstRow = table.match(/<tr[^>]*>([\s\S]*?)<\/tr>/);
        if (firstRow) {
          const cells = firstRow[1].match(/<t[dh][^>]*>/g) || [];
          const numCols = cells.length;
          if (numCols > 0) {
            // Автоматически распределяем ширину колонок
            const colWidth = Math.floor(100 / numCols) + '%';
            columnWidths = Array(numCols).fill(colWidth);
          }
        }
      });

      // Счетчик для определения ширины колонок
      let cellIndex = 0;

      // Обработка строк таблицы для подсчета ячеек
      processedHtml = processedHtml.replace(/<tr[^>]*>/g, function (match) {
        cellIndex = 0;
        return match;
      });

      // Обработка ячеек таблицы с сохранением выравнивания
      processedHtml = processedHtml.replace(/<td([^>]*)>([^<]*(?:<(?!\/td>)[^<]*)*)<\/td>/g, function (match, attributes, content) {
        let cellStyle = 'border: 1px solid #ccc; padding: 8px;';

        // Анализируем содержимое для определения выравнивания
        const trimmedContent = content.trim();

        // Эвристика для определения выравнивания по содержимому
        // Если содержимое начинается и заканчивается пробелами - возможно центрирование
        if (content !== trimmedContent && content.startsWith(' ') && content.endsWith(' ')) {
          const leadingSpaces = content.match(/^(\s+)/);
          const trailingSpaces = content.match(/(\s+)$/);
          if (leadingSpaces && trailingSpaces && leadingSpaces[1].length > 2 && trailingSpaces[1].length > 2) {
            cellStyle += ' text-align: center;';
          }
        }

        // Если содержимое - число или дата, часто выравнивается по правому краю
        if (/^\d+([.,]\d+)?$/.test(trimmedContent) || /^\d{1,2}[./-]\d{1,2}[./-]\d{2,4}$/.test(trimmedContent)) {
          if (!cellStyle.includes('text-align')) {
            cellStyle += ' text-align: right;';
          }
        }

        // Проверяем наличие выравнивания в параграфах внутри ячейки
        if (trimmedContent.includes('text-align:')) {
          const alignMatch = trimmedContent.match(/text-align:\s*([^;"]+)/);
          if (alignMatch) {
            cellStyle += ` text-align: ${alignMatch[1]};`;
          }
        }

        // Если есть класс с выравниванием на самой ячейке
        if (attributes) {
          if (attributes.includes('class="cell-center"')) {
            cellStyle += ' text-align: center;';
          } else if (attributes.includes('class="cell-right"')) {
            cellStyle += ' text-align: right;';
          }
        }

        // Если есть класс с выравниванием в содержимом
        if (trimmedContent.includes('class="text-center"')) {
          cellStyle += ' text-align: center;';
        } else if (trimmedContent.includes('class="text-right"')) {
          cellStyle += ' text-align: right;';
        } else if (trimmedContent.includes('class="text-justify"')) {
          cellStyle += ' text-align: justify;';
        }

        // Сохраняем вертикальное выравнивание из стилей ячейки
        const verticalAlignMatch = attributes && attributes.match(/vertical-align:\s*([^;"']+)/i);
        if (verticalAlignMatch) {
          cellStyle += ` vertical-align: ${verticalAlignMatch[1]};`;
        }

        // Проверяем ширину колонки если она указана
        if (columnWidths[cellIndex]) {
          cellStyle += ` width: ${columnWidths[cellIndex]};`;
        } else if (attributes && attributes.includes('width')) {
          const widthMatch = attributes.match(/width="([^"]*)"/);
          if (widthMatch) {
            cellStyle += ` width: ${widthMatch[1]};`;
          }
        }

        // Обрабатываем содержимое ячейки, убирая лишние теги p если нужно
        let processedContent = content;
        if (processedContent.includes('<p') && processedContent.split('<p').length === 2) {
          // Если только один параграф, можем извлечь его стили
          processedContent = processedContent.replace(/<p[^>]*style="([^"]*)"[^>]*>(.*?)<\/p>/g, function (m, style, text) {
            // Применяем стили параграфа к ячейке
            if (style && style.includes('text-align')) {
              const alignMatch = style.match(/text-align:\s*([^;]+)/);
              if (alignMatch && !cellStyle.includes('text-align')) {
                cellStyle += ` text-align: ${alignMatch[1]};`;
              }
            }
            return text;
          });

          // Также проверяем классы в параграфе
          processedContent = processedContent.replace(/<p[^>]*class="([^"]*)"[^>]*>(.*?)<\/p>/g, function (m, className, text) {
            if (className.includes('text-center') && !cellStyle.includes('text-align')) {
              cellStyle += ' text-align: center;';
            } else if (className.includes('text-right') && !cellStyle.includes('text-align')) {
              cellStyle += ' text-align: right;';
            } else if (className.includes('text-justify') && !cellStyle.includes('text-align')) {
              cellStyle += ' text-align: justify;';
            }
            return text;
          });
        }

        cellIndex++;
        return `<td${attributes} style="${cellStyle}">${processedContent}</td>`;
      });

      // Обработка заголовков таблицы с сохранением выравнивания
      processedHtml = processedHtml.replace(/<th([^>]*)>([^<]*(?:<(?!\/th>)[^<]*)*)<\/th>/g, function (match, attributes, content) {
        let headerStyle = 'border: 1px solid #ccc; padding: 8px; background-color: #f5f5f5; font-weight: bold;';

        // Анализируем содержимое для определения выравнивания
        const trimmedContent = content.trim();

        // Проверяем наличие выравнивания
        if (trimmedContent.includes('text-align:')) {
          const alignMatch = trimmedContent.match(/text-align:\s*([^;"]+)/);
          if (alignMatch) {
            headerStyle += ` text-align: ${alignMatch[1]};`;
          }
        } else {
          // По умолчанию заголовки по центру
          headerStyle += ' text-align: center;';
        }

        // Проверяем ширину колонки если она указана
        if (attributes && attributes.includes('width')) {
          const widthMatch = attributes.match(/width="([^"]*)"/);
          if (widthMatch) {
            headerStyle += ` width: ${widthMatch[1]};`;
          }
        }

        return `<th${attributes} style="${headerStyle}">${content}</th>`;
      });

      // Обработка выравнивания текста на основе классов
      processedHtml = processedHtml.replace(/<p class="text-center"([^>]*)>/g, '<p$1 style="text-align: center;">');
      processedHtml = processedHtml.replace(/<p class="text-right"([^>]*)>/g, '<p$1 style="text-align: right;">');
      processedHtml = processedHtml.replace(/<p class="text-justify"([^>]*)>/g, '<p$1 style="text-align: justify;">');

      // Преобразуем табуляции в неразрывные пробелы для визуального отображения
      processedHtml = processedHtml.replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');

      // Сохраняем отступы параграфов - улучшенная обработка
      processedHtml = processedHtml.replace(/<p([^>]*)>/g, function (match, attributes) {
        // Проверяем наличие отступов в атрибутах
        if (attributes && attributes.includes('margin-left')) {
          // Извлекаем значение margin-left
          const marginMatch = attributes.match(/margin-left:\s*([^;]+);?/);
          if (marginMatch) {
            const marginValue = marginMatch[1];
            // Преобразуем в padding-left для лучшей поддержки
            const paddingValue = marginValue.includes('pt') ?
              (parseFloat(marginValue) * 1.3) + 'px' :
              marginValue;
            return `<p${attributes} style="padding-left: ${paddingValue};">`;
          }
        }
        return match;
      });

      // Используем продвинутую обработку таблиц
      processedHtml = processDocxTables(processedHtml);

      // Логируем обработанный HTML для отладки

      // Конвертируем HTML от mammoth в содержимое редактора
      editor.commands.setContent(processedHtml);

      // Выводим предупреждения если есть
      if (result.messages.length > 0) {
        console.warn('Предупреждения при импорте:', result.messages);
      }

      // Возвращаем обработанный HTML для внешних компонентов
      return processedHtml;
    } catch (error) {
      console.error('Ошибка при импорте DOCX:', error);
      throw error; // Перебрасываем ошибку для обработки в TemplateEditor
    }
  };

  // Экспонируем методы через ref
  useImperativeHandle(ref, () => ({
    insertVariable,
    importDocx,
    getContent: () => editor?.getHTML() || '',
    getMargins: () => getEffectiveMargins(),
    getContentWithMargins: () => {
      const effectiveMargins = getEffectiveMargins();
      let html = editor?.getHTML() || processContent || '';
      html = serializeLoopBlocks(html);
      html = serializeConditionalBlocks(html);
      html = normalizeEdgeWhitespaceAndEmptyParagraphs(html);
      return encodeMarginsTag(effectiveMargins) + stripMarginsTag(html);
    },
    setContent: (content) => {
      if (editor) {
        editor.commands.setContent(content);
      }
    },
    scrollToTop: () => {
      const main = mainContainerRef.current;
      if (main) main.scrollTop = 0;
    },
    scrollToBottom: () => {
      const main = mainContainerRef.current;
      if (main) main.scrollTop = Math.max(0, main.scrollHeight - main.clientHeight);
    },
  }), [editor, processContent, getEffectiveMargins, insertVariable]);

  const fontFamilyGroups = [
    {
      key: 'Arial',
      label: 'Arial',
      variants: [
        { key: 'regular', label: 'Regular', value: 'Arial' },
        { key: 'narrow', label: 'Narrow', value: 'Arial Narrow' },
      ],
    },
    {
      key: 'Times New Roman',
      label: 'Times New Roman',
      variants: [
        { key: 'regular', label: 'Regular', value: 'Times New Roman' },
      ],
    },
    {
      key: 'Calibri',
      label: 'Calibri',
      variants: [
        { key: 'regular', label: 'Regular', value: 'Calibri' },
      ],
    },
    {
      key: 'Georgia',
      label: 'Georgia',
      variants: [
        { key: 'regular', label: 'Regular', value: 'Georgia' },
      ],
    },
    {
      key: 'Verdana',
      label: 'Verdana',
      variants: [
        { key: 'regular', label: 'Regular', value: 'Verdana' },
      ],
    },
    {
      key: 'Tahoma',
      label: 'Tahoma',
      variants: [
        { key: 'regular', label: 'Regular', value: 'Tahoma' },
      ],
    },
    {
      key: 'Helvetica',
      label: 'Helvetica',
      variants: [
        { key: 'regular', label: 'Regular', value: 'Helvetica' },
      ],
    },
    {
      key: 'Comic Sans MS',
      label: 'Comic Sans MS',
      variants: [
        { key: 'regular', label: 'Regular', value: 'Comic Sans MS' },
      ],
    },
    {
      key: 'Courier New',
      label: 'Courier New',
      variants: [
        { key: 'regular', label: 'Regular', value: 'Courier New' },
      ],
    },
    {
      key: 'Impact',
      label: 'Impact',
      variants: [
        { key: 'regular', label: 'Regular', value: 'Impact' },
      ],
    },
  ];

  const fontSizes = [
    '8pt', '9pt', '10pt', '11pt', '12pt', '14pt', '16pt', '18pt',
    '20pt', '24pt', '28pt', '32pt', '36pt', '48pt', '72pt'
  ];
  const paragraphSpacings = [0, 2, 3, 4, 6, 8, 10, 12, 16, 20, 24];
  const lineHeights = [1, 1.15, 1.5, 2];

  // Функции для работы с размером шрифта
  const normalizeFontFamily = (value) => {
    if (!value) return null;
    return String(value).split(',')[0].replace(/["']/g, '').trim() || null;
  };

  const normalizeFontSizeValue = (raw, fallback = '12pt') => {
    const source = String(raw || '').trim().toLowerCase();
    if (!source) return fallback;
    const numeric = parseFloat(source);
    if (!Number.isFinite(numeric) || numeric <= 0) return fallback;
    if (source.endsWith('pt')) return `${Math.round(numeric)}pt`;
    if (source.endsWith('px')) return `${Math.round(numeric * 0.75)}pt`;
    return `${Math.round(numeric)}pt`;
  };

  const toPtNumber = (raw, fallback = 12) => {
    const normalized = normalizeFontSizeValue(raw, `${fallback}pt`);
    const numeric = parseFloat(normalized);
    return Number.isFinite(numeric) ? numeric : fallback;
  };

  const getCurrentFontSize = () => {
    if (!editor) return '12pt';
    const textStyleSize = editor.getAttributes('textStyle').fontSize;
    const paragraphSize = editor.getAttributes('paragraph').fontSize;
    const headingSize = editor.getAttributes('heading').fontSize;
    const fontSize = textStyleSize || paragraphSize || headingSize;
    return normalizeFontSizeValue(fontSize, '12pt');
  };
  const getCurrentFontFamily = () => {
    if (!editor) return 'default';
    const attrs = editor.getAttributes('textStyle');
    const attrFont = normalizeFontFamily(attrs?.fontFamily);
    if (attrFont) return attrFont;

    const { state } = editor.view;
    // Проверяем marks под курсором
    const cursorMarks = state.selection.$from.marks();
    const markFont = cursorMarks.find((m) => m.type.name === 'textStyle' && m.attrs?.fontFamily);
    const markName = normalizeFontFamily(markFont?.attrs?.fontFamily);
    if (markName) return markName;

    // Проверяем отложенные storedMarks (когда курсор только что поставлен)
    const storedFont = state.storedMarks?.find((m) => m.type.name === 'textStyle' && m.attrs?.fontFamily);
    const storedName = normalizeFontFamily(storedFont?.attrs?.fontFamily);
    if (storedName) return storedName;

    // Фолбек: читаем реальный computed style узла курсора
    try {
      const sel = window.getSelection && window.getSelection();
      const anchor = sel?.anchorNode;
      const element = anchor?.nodeType === Node.ELEMENT_NODE ? anchor : anchor?.parentElement;
      if (element && window.getComputedStyle) {
        const computedName = normalizeFontFamily(window.getComputedStyle(element).fontFamily);
        if (computedName) return computedName;
      }
    } catch (_) { /* no-op */ }

    return 'default';
  };

  const getCurrentParagraphSpacing = () => {
    if (!editor) return 'default';
    const paragraphAttrs = editor.getAttributes('paragraph');
    const headingAttrs = editor.getAttributes('heading');
    const raw = paragraphAttrs?.paragraphSpacing ?? headingAttrs?.paragraphSpacing;
    if (raw === null || raw === undefined || raw === '') return 'default';
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return 'default';
    return String(Math.max(0, Math.round(numeric)));
  };

  const getCurrentLineHeight = () => {
    if (!editor) return 'default';
    const paragraphAttrs = editor.getAttributes('paragraph');
    const headingAttrs = editor.getAttributes('heading');
    const raw = paragraphAttrs?.lineHeight ?? headingAttrs?.lineHeight;
    if (raw === null || raw === undefined || raw === '') return 'default';
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return 'default';
    return String(Math.round(numeric * 100) / 100);
  };

  const increaseFontSize = () => {
    if (externalToolbarActive && typeof onExternalToolbarCommand === 'function') {
      if (onExternalToolbarCommand('increaseFontSize')) return;
    }
    if (!editor) return;
    const currentSize = toPtNumber(getCurrentFontSize(), 12);
    const newSize = Math.min(currentSize + 1, 72);
    editor.chain().focus(undefined, { scrollIntoView: false }).setFontSize(`${newSize}pt`).run();
  };

  const decreaseFontSize = () => {
    if (externalToolbarActive && typeof onExternalToolbarCommand === 'function') {
      if (onExternalToolbarCommand('decreaseFontSize')) return;
    }
    if (!editor) return;
    const currentSize = toPtNumber(getCurrentFontSize(), 12);
    const newSize = Math.max(currentSize - 1, 8);
    editor.chain().focus(undefined, { scrollIntoView: false }).setFontSize(`${newSize}pt`).run();
  };

	  const wrapSelectionWithCondition = (condition) => {
	    if (!editor) return;
	    const { state, view } = editor;
	    const { from, to } = state.selection;
    if (from === to) {
      alert('Выделите текст, который нужно показывать по условию');
      return;
    }
    const cond = (condition || '').trim();
    if (!cond) return;

    const { schema, doc } = state;
    const inlineType = schema.nodes.conditionalBlock;
    const blockType = schema.nodes.conditionalSection;
    if (!inlineType || !blockType) return;

    const groupId = `cg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

    const segments = [];
    doc.nodesBetween(from, to, (node, pos) => {
      if (!node.isTextblock) return;
      const blockFrom = pos + 1;
      const blockTo = pos + node.nodeSize - 1;
      const segFrom = Math.max(from, blockFrom);
      const segTo = Math.min(to, blockTo);
      if (segFrom < segTo) segments.push({ from: segFrom, to: segTo });
      return false;
    });

    // Если не нашли textblock (редко, но бывает в таблицах/нестандартных узлах) — fallback на обычную замену
    if (segments.length === 0) {
      const fragment = protectFragmentEdgeSpaces(doc.slice(from, to).content, schema);
      const node = inlineType.create({ condition: cond, groupId }, fragment);
      view.dispatch(state.tr.replaceRangeWith(from, to, node).scrollIntoView());
      view.focus();
      return;
    }

    // Оборачиваем каждый сегмент inline-блоком внутри соответствующего абзаца/текстблока.
    // Это НЕ добавляет переводов строк и сохраняет форматирование.
    let tr = state.tr;
    for (let i = segments.length - 1; i >= 0; i--) {
      const seg = segments[i];
      const segFrom = tr.mapping.map(seg.from);
      const segTo = tr.mapping.map(seg.to);
      if (segFrom >= segTo) continue;
      const fragment = protectFragmentEdgeSpaces(tr.doc.slice(segFrom, segTo).content, schema);
      const node = inlineType.create({ condition: cond, groupId }, fragment);
      tr = tr.replaceRangeWith(segFrom, segTo, node);
    }
    tr = tr.scrollIntoView();
    view.dispatch(tr);
	    view.focus();
	  };

			  const wrapRangeWithCondition = React.useCallback(({ tr, schema, doc, from, to, condition, groupId }) => {
			    const inlineType = schema.nodes.conditionalBlock;
			    const blockType = schema.nodes.conditionalSection;
			    if (!inlineType || !blockType) return tr;
			    const cond = String(condition || '').trim();
			    if (!cond) return tr;
			    const a = Math.min(from, to);
			    const b = Math.max(from, to);
			    if (a >= b) return tr;

			    const segments = [];
			    doc.nodesBetween(a, b, (node, pos) => {
			      if (!node.isTextblock) return;
			      const blockFrom = pos + 1;
			      const blockTo = pos + node.nodeSize - 1;
			      const segFrom = Math.max(a, blockFrom);
			      const segTo = Math.min(b, blockTo);
			      if (segFrom < segTo) segments.push({ from: segFrom, to: segTo, blockFrom, blockTo });
			      return false;
			    });

	    if (segments.length === 0) {
	      const fragment = protectFragmentEdgeSpaces(doc.slice(a, b).content, schema);
	      const node = inlineType.create({ condition: cond, groupId }, fragment);
	      return tr.replaceRangeWith(a, b, node);
	    }

			    let nextTr = tr;
			    for (let i = segments.length - 1; i >= 0; i--) {
			      const seg = segments[i];
			      // Позиции `seg.*` уже рассчитаны в координатах `doc` (который равен текущему `tr.doc`).
			      // Здесь нельзя применять `tr.mapping` повторно — иначе диапазоны "съедут" и уменьшение
			      // начнёт визуально/фактически откатываться назад.
			      // Так как мы идём с конца, дополнительные шаги replace не требуют маппинга.
			      const blockFrom = seg.blockFrom;
			      const blockTo = seg.blockTo;
			      let segFrom = seg.from;
			      let segTo = seg.to;

			      // Клампим внутрь текущего textblock.
			      segFrom = clamp(segFrom, blockFrom, blockTo);
			      segTo = clamp(segTo, blockFrom, blockTo);

			      // Если диапазон схлопнулся на границе (часто при уменьшении), расширим его на 1 символ
			      // внутри того же textblock, чтобы обёртка могла создаться.
			      if (segFrom >= segTo) {
			        if (segTo < blockTo) segTo = Math.min(blockTo, segTo + 1);
			        else if (segFrom > blockFrom) segFrom = Math.max(blockFrom, segFrom - 1);
			      }
			      if (segFrom >= segTo) continue;

		      const fragment = protectFragmentEdgeSpaces(nextTr.doc.slice(segFrom, segTo).content, schema);
		      const node = inlineType.create({ condition: cond, groupId }, fragment);
		      nextTr = nextTr.replaceRangeWith(segFrom, segTo, node);
		    }
			    return nextTr;
			  }, []);

  const computeOuterConditionalBounds = React.useCallback((doc, gid) => {
    // Ограничиваем ресайз внешним родительским IF и ближайшими соседними IF,
    // чтобы запретить пересечение блоков.
    let min = 1;
    let max = doc.content.size - 1;

    const groupNodes = [];
    const foreignNodes = [];
    doc.descendants((node, pos) => {
      if (node.type?.name !== 'conditionalBlock' && node.type?.name !== 'conditionalSection') return;
      const effective = String(node.attrs?.groupId || `__pos_${pos}`);
      const entry = { pos, node, effective };
      if (effective === String(gid)) groupNodes.push(entry);
      else foreignNodes.push(entry);
    });

    let groupMin = null;
    let groupMax = null;
    groupNodes.forEach(({ pos, node }) => {
      const start = pos + 1;
      const end = pos + node.nodeSize - 1;
      groupMin = groupMin == null ? start : Math.min(groupMin, start);
      groupMax = groupMax == null ? end : Math.max(groupMax, end);
      try {
        const inside = Math.min(pos + 1, pos + node.nodeSize - 1);
        const $p = doc.resolve(inside);
        for (let d = $p.depth; d > 0; d--) {
          const n = $p.node(d);
          const name = n?.type?.name;
          if (name !== 'conditionalBlock' && name !== 'conditionalSection') continue;
          const nid = String(n.attrs?.groupId || '');
          const eff = nid ? nid : `__pos_${$p.before(d)}`;
          if (String(eff) === String(gid)) continue;
          min = Math.max(min, $p.start(d));
          max = Math.min(max, $p.end(d));
        }
      } catch (_) { }
    });

    if (groupMin == null || groupMax == null) {
      return { min, max };
    }

    // Жёстко запрещаем пересекать соседние IF (не даём границам перескочить их).
    let nearestBefore = null;
    let nearestAfter = null;
    foreignNodes.forEach(({ pos, node }) => {
      const start = pos + 1;
      const end = pos + node.nodeSize - 1;
      if (end <= groupMin) {
        nearestBefore = nearestBefore == null ? end : Math.max(nearestBefore, end);
      } else if (start >= groupMax) {
        nearestAfter = nearestAfter == null ? start : Math.min(nearestAfter, start);
      }
    });

    if (nearestBefore != null) min = Math.max(min, nearestBefore);
    if (nearestAfter != null) max = Math.min(max, nearestAfter);

    if (min >= max) return { min: 1, max: doc.content.size - 1 };
    return { min, max };
  }, []);

		  const resizeConditionalGroup = React.useCallback(({ groupId, from, to }) => {
		    if (!editor) return;
		    const view = editor.view;
		    const state = view.state;
		    const doc = state.doc;
		    const gid = String(groupId || '');
		    if (!gid) return;

	    // Находим все узлы группы по effective id
	    const nodes = [];
	    doc.descendants((node, pos) => {
	      if (node.type?.name !== 'conditionalBlock' && node.type?.name !== 'conditionalSection') return;
	      const effective = String(node.attrs?.groupId || `__pos_${pos}`);
	      if (effective !== gid) return;
	      nodes.push({ pos, node });
	    });
	    if (!nodes.length) return;

		    nodes.sort((a, b) => a.pos - b.pos);
		    const first = nodes[0];
		    const condition = String(first.node.attrs?.condition || '').trim();
		    if (!condition) return;
	    const stableGroupId = String(first.node.attrs?.groupId || '') || `cg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

		    // Ограничение: не выходим за пределы внешнего IF
		    const outer = computeOuterConditionalBounds(doc, gid);
		    let nextFrom = clamp(Math.min(from, to), outer.min, outer.max);
		    let nextTo = clamp(Math.max(from, to), outer.min, outer.max);
		    if (nextTo <= nextFrom) nextTo = Math.min(outer.max, nextFrom + 1);

		    let tr = state.tr;

	    // Разворачиваем текущие узлы группы в "обычный" текст (сохраняя содержимое)
	    const positions = nodes.slice().sort((a, b) => b.pos - a.pos);
	    positions.forEach(({ pos, node }) => {
	      tr = tr.replace(pos, pos + node.nodeSize, new Slice(node.content, 0, 0));
	    });

	    // ВАЖНО: mapping.map() не сохраняет смещение для позиции, попадающей внутрь replace-диапазона:
	    // она может "схлопываться" к одному из краёв и тогда уменьшение визуально не применяется.
	    // Поэтому маппим вручную, сохраняя относительное смещение внутри узла:
	    // - внутри узла: pos -> pos - 1 (убрали открывающую границу)
	    // - после узла: pos -> pos - 2 (убрали и открывающую, и закрывающую)
	    const mapPosAfterUnwrap = (pos) => {
	      let mapped = pos;
	      for (let i = 0; i < nodes.length; i++) {
	        const start = nodes[i].pos;
	        const end = start + nodes[i].node.nodeSize;
	        if (pos <= start) continue;
	        if (pos >= end) {
	          mapped -= 2;
	          continue;
	        }
	        // pos внутри узла (между start и end): убираем только "открывающую" границу
	        mapped -= 1;
	      }
	      return mapped;
	    };

	    const mappedFrom = clamp(mapPosAfterUnwrap(nextFrom), 1, tr.doc.content.size - 1);
	    const mappedTo = clamp(mapPosAfterUnwrap(nextTo), 1, tr.doc.content.size - 1);
	    let a = Math.min(mappedFrom, mappedTo);
	    let b = Math.max(mappedFrom, mappedTo);
	    // Если диапазон схлопнулся (часто при сильном уменьшении края) — не удаляем блок,
	    // а оставляем минимально возможный диапазон в 1 символ.
	    if (a >= b) {
	      b = Math.min(tr.doc.content.size - 1, a + 1);
	    }
	    if (a >= b) return;

	    // Если выбранный диапазон не содержит контента (попали на границы узлов),
	    // слегка "раздвинем" его, чтобы блок не выглядел как исчезнувший.
	    try {
	      const slice = tr.doc.slice(a, b);
	      if (!slice?.content || slice.content.size === 0) {
	        if (b < tr.doc.content.size - 1) b = Math.min(tr.doc.content.size - 1, b + 1);
	        else if (a > 1) a = Math.max(1, a - 1);
	      }
	    } catch (_) { }
	    if (a >= b) return;

		    tr = wrapRangeWithCondition({
		      tr,
		      schema: state.schema,
		      doc: tr.doc,
		      from: a,
		      to: b,
		      condition,
		      groupId: stableGroupId,
		    });

	    // Защита: если по какой-то причине обёртки не создались — не применяем изменения,
	    // чтобы не оставлять пользователя без блока (когда узел "пропадает").
	    try {
	      let created = false;
	      let newFrom = null;
	      let newTo = null;
		      tr.doc.descendants((node, pos) => {
		        if (node.type?.name !== 'conditionalBlock' && node.type?.name !== 'conditionalSection') return;
		        if (String(node.attrs?.groupId || '') !== String(stableGroupId)) return;
		        created = true;
		        if (newFrom == null) newFrom = pos + 1;
		        const end = pos + node.nodeSize - 1;
		        newTo = Math.max(newTo == null ? -1 : newTo, end);
		      });
		      if (!created) return;
		    } catch (_) { }

	    tr = tr.scrollIntoView();
	    view.dispatch(tr);
	    view.focus();
	    // Добиваем пересчёт ручек/гуттера даже если изменения минимальные.
	    try { window.requestAnimationFrame(() => updateConditionalGutterRef.current?.()); } catch (_) { }

	    // Если был синтетический id — обновим selection на реальный groupId
	    if (gid.startsWith('__pos_')) {
	      try {
	        setSelectedConditionalGroupId(stableGroupId);
	        view.dispatch(view.state.tr.setMeta(conditionalMarkersPluginKey, { type: 'select', groupId: stableGroupId }));
	      } catch (_) { }
	    }
	  }, [editor, computeOuterConditionalBounds, wrapRangeWithCondition, setSelectedConditionalGroupId]);

  const openConditionEditor = React.useCallback(({ title, initial, onApply, restoreGutter = null }) => {
    // Поповер условий во время редактирования не нужен — но после закрытия по Esc/Отмена
    // возвращаем пользователя обратно в него, если он был открыт.
    restoreConditionalGutterRef.current = restoreGutter;
    setActiveConditionalGutter(null);
    setConditionEditorTitle(title || 'Условие показа блока');
    setConditionEditorState(parseConditionToBuilder(initial || "debtor_type_code == 'legal'"));
    conditionEditorApplyRef.current = typeof onApply === 'function' ? onApply : null;
    const tab = isAdmin ? 'global' : 'mine';
    setConditionPresetsTab(tab);
    setPresetDraft({ id: null, name: '', is_global: tab === 'global' && isAdmin });
    loadConditionPresets();
    setConditionEditorOpen(true);
  }, [loadConditionPresets, isAdmin]);

  const getConditionForConditionalNode = React.useCallback((doc, from, groupId) => {
    if (!doc) return '';
    const gid = String(groupId || '');
    const isSyntheticGid = gid.startsWith('__pos_');
    let found = '';
    if (gid && !isSyntheticGid) {
      doc.descendants((node) => {
        if (found) return false;
        if (node.type?.name !== 'conditionalBlock' && node.type?.name !== 'conditionalSection') return;
        if (String(node.attrs?.groupId || '') === gid) {
          found = String(node.attrs?.condition || '');
          return false;
        }
      });
    }
    if (!found && typeof from === 'number') {
      try {
        const $pos = doc.resolve(from);
        const node = $pos.nodeAfter;
        if (node && (node.type?.name === 'conditionalBlock' || node.type?.name === 'conditionalSection')) {
          found = String(node.attrs?.condition || '');
        }
      } catch (_) { }
    }
    return found;
  }, []);

  React.useEffect(() => {
    if (conditionEditorOpen) return;
    if (!restoreConditionalGutterOnCloseRef.current) return;
    restoreConditionalGutterOnCloseRef.current = false;

    const snap = restoreConditionalGutterRef.current;
    restoreConditionalGutterRef.current = null;
    if (!snap || !editor?.view?.state?.doc) return;

    const doc = editor.view.state.doc;
    const items = Array.isArray(snap.items) ? snap.items : [];
    const refreshedItems = items.map((it) => ({
      ...it,
      condition: getConditionForConditionalNode(doc, it.from, it.groupId) || it.condition,
    }));
    const first = snap.first ? {
      ...snap.first,
      condition: getConditionForConditionalNode(doc, snap.first.from, snap.first.groupId) || snap.first.condition,
    } : (refreshedItems[0] || null);

    setActiveConditionalGutter({
      ...snap,
      items: refreshedItems,
      first,
      count: refreshedItems.length || snap.count || 0,
    });
  }, [conditionEditorOpen, editor, getConditionForConditionalNode]);

  const promptCondition = () => {
    openConditionEditor({
      title: 'Условие показа выделенного блока',
      initial: "debtor_type_code == 'legal'",
      onApply: (expr) => wrapSelectionWithCondition(expr),
    });
  };

  const createLoopFromSelection = React.useCallback(() => {
    if (!editor) return;
    const defaultList = DEFAULT_LOOP_LIST || 'pledge_single_creditor_solo_lots';
    const defaultExpr = `item in ${defaultList}`;
    const { state } = editor;
    const { selection } = state;
    let created = false;
    if (!selection.empty) {
      created = editor.chain().focus().wrapIn('loopSection', { loop: defaultExpr }).run();
    }
    if (!created) {
      editor.chain().focus().insertContent({
        type: 'loopSection',
        attrs: { loop: defaultExpr },
        content: [{ type: 'paragraph' }],
      }).run();
    }
    window.setTimeout(() => {
      openLoopEditorFromPos(editor.state.selection.from);
    }, 0);
  }, [editor, openLoopEditorFromPos]);

  // Закрытие модалки цикла по Esc (не даём Esc закрыть внешние модалки)
  React.useEffect(() => {
    if (!loopEditorOpen) return;
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      closeLoopEditor();
    };
    document.addEventListener('keydown', handleEscape, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: true });
    };
  }, [loopEditorOpen, closeLoopEditor]);

  React.useEffect(() => {
    const handleExternalClose = () => {
      if (loopEditorOpen) {
        closeLoopEditor();
      }
    };
    document.addEventListener('aotCloseLoopEditor', handleExternalClose, { capture: true });
    return () => {
      document.removeEventListener('aotCloseLoopEditor', handleExternalClose, { capture: true });
    };
  }, [loopEditorOpen, closeLoopEditor]);

  const updateConditionalGutter = React.useCallback(() => {
    if (!enableConditionTools) {
      setConditionalGutterItems((prev) => (prev.length ? [] : prev));
      setConditionalResizeUI(null);
      setActiveConditionalGutter(null);
      return;
    }
    if (!editor) return;
    const view = editor.view;
    const a4 = a4ContainerRef.current;
    if (!view || !a4) return;

    const isScrollable = (el) => {
      if (!el) return false;
      if (!(el instanceof HTMLElement)) return false;
      try {
        const s = window.getComputedStyle(el);
        const oy = (s.overflowY || '').toLowerCase();
        const canScroll = (oy === 'auto' || oy === 'scroll');
        return canScroll && el.scrollHeight > el.clientHeight + 1;
      } catch (_) {
        return false;
      }
    };
    const findPrimaryScroller = () => {
      // 1) Сначала ищем ближайший скроллер от ProseMirror вверх до wrapper
      const wrapper = wrapperRef.current;
      let el = view.dom;
      while (el && el !== wrapper && el instanceof HTMLElement) {
        if (isScrollable(el)) return el;
        el = el.parentElement;
      }
      // 2) Потом — основной контейнер редактора
      const main = mainContainerRef.current;
      if (isScrollable(main)) return main;
      // 3) В крайнем случае — window (document.scrollingElement)
      const docEl = document.scrollingElement;
      if (docEl && docEl instanceof HTMLElement && docEl.scrollHeight > docEl.clientHeight + 1) return docEl;
      return null;
    };

    // Запоминаем базовый scrollTop того скроллера,
    // относительно которого посчитаны координаты (это нужно, чтобы при скролле можно было
    // двигать гуттер transform'ом без пересчёта позиций и без лагов).
    scrollBasesRef.current = new WeakMap();
    scrollPrimaryRef.current = findPrimaryScroller();
    try {
      const primary = scrollPrimaryRef.current;
      if (primary && primary instanceof HTMLElement) {
        scrollBasesRef.current.set(primary, Number(primary.scrollTop || 0));
      }
    } catch (_) { }
    // Отложенно сбросим transform гуттера в layout-эффекте после обновления позиций,
    // чтобы не было визуального "мигания" при клике после скролла.
    pendingGutterResetRef.current = true;

    const a4Rect = a4.getBoundingClientRect();
    const mainRect = mainContainerRef.current?.getBoundingClientRect?.();
    // ВАЖНО: в стандартном режиме используем CSS zoom (см. --editor-zoom),
    // а в fullscreen — transform: scale(). Для корректной привязки бейджей/линий
    // нормализуем координаты на фактический масштаб.
    let scaleX = 1;
    let scaleY = 1;
    try {
      const wrapper = wrapperRef.current;
      const inFullscreen = !!wrapper?.classList?.contains?.('fullscreen-mode');
      if (inFullscreen) {
        scaleX = a4.offsetWidth ? a4Rect.width / a4.offsetWidth : 1;
        scaleY = a4.offsetHeight ? a4Rect.height / a4.offsetHeight : 1;
      } else {
        const z = wrapper ? parseFloat(getComputedStyle(wrapper).getPropertyValue('--editor-zoom') || '1') : 1;
        scaleX = Number.isFinite(z) && z > 0 ? z : 1;
        scaleY = scaleX;
      }
	    } catch (_) {
	      scaleX = 1;
	      scaleY = 1;
	    }
	    conditionalResizeLayoutRef.current = { a4Rect, scaleX, scaleY };
	    const prose = a4.querySelector('.tiptap-editor .ProseMirror');
	    const proseRect = prose?.getBoundingClientRect();
    let paddingLeft = 0;
    try {
      paddingLeft = prose ? parseFloat(window.getComputedStyle(prose).paddingLeft || '0') : 0;
    } catch (_) { paddingLeft = 0; }
    const contentStartX = proseRect
      ? ((proseRect.left - a4Rect.left) / (scaleX || 1)) + (Number.isFinite(paddingLeft) ? paddingLeft : 0)
      : 0;
    // Позиция бейджа: хотим, чтобы он жил в левом "поле" редактора (между красной рамкой и синим контейнером),
    // а не зависел от пользовательских полей страницы. Поэтому привязываем его к левому краю
    // основного скроллера редактора.
    const badgeAnchorX = (() => {
      try {
        // Ставим бейдж в левое поле редактора (внутри красного контейнера),
        // чтобы он не клиппился родительскими overflow/трансформациями.
        const desiredScreenLeft = Number(mainRect?.left || 0) + 8;
        return (desiredScreenLeft - a4Rect.left) / (scaleX || 1);
      } catch (_) {
        return -(24 + 8);
      }
    })();

    const groups = new Map();
    view.state.doc.descendants((node, pos) => {
      if (node.type.name !== 'conditionalBlock' && node.type.name !== 'conditionalSection') return;
      const groupId = String(node.attrs?.groupId || '');
      const gid = groupId || `__pos_${pos}`;
      const entry = groups.get(gid) || {
        groupId: groupId || '',
        condition: String(node.attrs?.condition || ''),
        nodes: [],
      };
      entry.nodes.push({ pos, node });
      // Берём условие из первого узла группы (все должны совпадать)
      if (!entry.condition) entry.condition = String(node.attrs?.condition || '');
      groups.set(gid, entry);
    });

    const raw = [];
    groups.forEach((g, gid) => {
      const list = g.nodes.slice().sort((a, b) => a.pos - b.pos);
      const first = list[0];
      const last = list[list.length - 1];
      const from = first.pos;
      const to = last.pos + last.node.nodeSize;

      const safeStart = Math.min(from + 1, to - 1);
      const safeEnd = Math.max(from + 1, to - 1);
      const start = view.coordsAtPos(safeStart);
      const end = view.coordsAtPos(safeEnd);

      const top = (start.top - a4Rect.top) / (scaleY || 1);
      const bottom = (end.bottom - a4Rect.top) / (scaleY || 1);
      const height = Math.max(12, bottom - top);

      raw.push({
        key: `condg-${gid}-${from}-${to}`,
        groupId: g.groupId || gid,
        from,
        to,
        condition: String(g.condition || ''),
        top,
        height,
        contentStartX,
        badgeAnchorX,
        pageWidth: a4.offsetWidth || a4Rect.width,
      });
    });

    // Укладка бейджей: если несколько стартуют на одной строке — раскладываем влево колонками.
    const byLine = new Map();
    raw.forEach((item) => {
      const lineKey = Math.round(item.top / 4) * 4;
      const list = byLine.get(lineKey) || [];
      list.push(item);
      byLine.set(lineKey, list);
    });

	    byLine.forEach((list) => {
	      list.sort((a, b) => a.from - b.from);
	      list.forEach((item, idx) => {
	        item.lane = idx;
	      });
	    });

		    setConditionalGutterItems(raw);

		    // Ручки изменения границ IF: показываем только для выбранного блока.
		    const pluginSelectedId = (() => {
		      try {
		        const ps = conditionalMarkersPluginKey.getState(view.state);
		        return ps?.selectedGroupId ? String(ps.selectedGroupId) : '';
		      } catch (_) { return ''; }
		    })();
		    const selectedId = pluginSelectedId || (selectedConditionalGroupId ? String(selectedConditionalGroupId) : '');

		    if (mode === 'edit' && selectedId) {
		      const selected = raw.find((r) => String(r.groupId) === String(selectedId));
		      if (!selected) {
		        setConditionalResizeUI(null);
		      } else {
		        try {
		          const contentFrom = Math.min(selected.from + 1, selected.to - 1);
		          const contentTo = Math.max(selected.from + 1, selected.to - 1);
		          const start = view.coordsAtPos(contentFrom);
		          const end = view.coordsAtPos(contentTo);

	          const relStartX = (start.left - a4Rect.left) / (scaleX || 1);
	          const relStartY = (start.top - a4Rect.top) / (scaleY || 1);
	          const relStartH = (start.bottom - start.top) / (scaleY || 1);

	          const relEndX = (end.right - a4Rect.left) / (scaleX || 1);
	          const relEndY = (end.top - a4Rect.top) / (scaleY || 1);
	          const relEndH = (end.bottom - end.top) / (scaleY || 1);

		          setConditionalResizeUI({
		            groupId: selectedId,
		            from: contentFrom,
		            to: contentTo,
		            start: { x: relStartX, y: relStartY + relStartH / 2, h: relStartH },
		            end: { x: relEndX, y: relEndY + relEndH / 2, h: relEndH },
		          });
		        } catch (_) {
		          setConditionalResizeUI(null);
		        }
		      }
		    } else {
		      setConditionalResizeUI(null);
		    }
  }, [editor, mode, selectedConditionalGroupId, enableConditionTools]);

  // Держим актуальную версию пересчёта в ref, чтобы можно было вызывать из эффектов,
  // объявленных выше по файлу, без ESLint no-use-before-define.
  React.useEffect(() => {
    if (!enableConditionTools) {
      updateConditionalGutterRef.current = null;
      return undefined;
    }
    updateConditionalGutterRef.current = updateConditionalGutter;
    return () => {
      if (updateConditionalGutterRef.current === updateConditionalGutter) {
        updateConditionalGutterRef.current = null;
      }
    };
  }, [updateConditionalGutter, enableConditionTools]);

  // Сбрасываем transform гуттера синхронно с обновлением DOM (до paint),
  // чтобы не было краткого скачка позиции (фликера) при пересчёте после клика/selectionUpdate.
  React.useLayoutEffect(() => {
    if (!pendingGutterResetRef.current) return;
    const gutterEl = conditionalGutterRef.current;
    if (gutterEl) {
      gutterEl.style.transform = '';
    }
    pendingGutterResetRef.current = false;
  }, [conditionalGutterItems]);

  React.useEffect(() => {
    if (!enableConditionTools) return undefined;
    if (!editor) return;
    let raf = 0;
    const handler = () => updateConditionalGutter();
    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        handler();
      });
    };
    editor.on('update', schedule);
    editor.on('selectionUpdate', schedule);
    window.addEventListener('resize', schedule);
    schedule();
    return () => {
      editor.off('update', schedule);
      editor.off('selectionUpdate', schedule);
      window.removeEventListener('resize', schedule);
      if (raf) {
        window.cancelAnimationFrame(raf);
        raf = 0;
      }
    };
  }, [editor, updateConditionalGutter, enableConditionTools]);

  // После открытия/изменения масштаба/выхода из fullscreen пересчитываем гуттер отложенно,
  // иначе при первой отрисовке (до применения zoom/шрифтов) coordsAtPos может дать некорректные координаты,
  // и бейджи появляются только после первого скролла.
  React.useEffect(() => {
    if (!enableConditionTools) return undefined;
    if (!editor) return;
    if (fullscreen) return;
    let raf1 = 0;
    let raf2 = 0;
    let t = 0;
    const run = () => {
      try { updateConditionalGutter(); } catch (_) { }
    };
    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(run);
    });
    t = window.setTimeout(run, 80);
    try {
      // Если доступны шрифты — дождёмся, чтобы метрики строк были стабильны
      if (document?.fonts?.ready) {
        document.fonts.ready.then(() => {
          run();
        }).catch(() => {});
      }
    } catch (_) { }
    return () => {
      if (raf1) window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
      if (t) window.clearTimeout(t);
    };
  }, [editor, fullscreen, editorZoom, updateConditionalGutter, enableConditionTools]);

  // Аналогично для fullscreen: при первом входе transform:scale может примениться уже ПОСЛЕ первого пересчёта,
  // поэтому линии/бейджи на долю секунды оказываются «выше», и встают правильно только после первого скролла.
  // Делаем отложенный пересчёт сразу после входа в fullscreen.
  React.useEffect(() => {
    if (!enableConditionTools) return undefined;
    if (!editor) return;
    if (!fullscreen) return;
    let raf1 = 0;
    let raf2 = 0;
    let t1 = 0;
    let t2 = 0;
    const run = () => {
      try { updateConditionalGutter(); } catch (_) { }
    };
    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(run);
    });
    t1 = window.setTimeout(run, 80);
    t2 = window.setTimeout(run, 220);
    try {
      if (document?.fonts?.ready) {
        document.fonts.ready.then(run).catch(() => {});
      }
    } catch (_) { }
    return () => {
      if (raf1) window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
      if (t1) window.clearTimeout(t1);
      if (t2) window.clearTimeout(t2);
    };
  }, [editor, fullscreen, updateConditionalGutter, enableConditionTools]);

  // При смене fullscreen/масштаба пересчитываем позиции один раз, чтобы бейджи не "уезжали".
  React.useEffect(() => {
    if (!enableConditionTools) return undefined;
    if (!editor) return;
    const id = window.requestAnimationFrame(() => updateConditionalGutter());
    return () => window.cancelAnimationFrame(id);
  }, [editor, fullscreen, updateConditionalGutter, enableConditionTools]);

  // Синхронизация гуттера со скроллом.
  // scroll не всплывает, поэтому слушаем CAPTURE на wrapperRef — так мы ловим любой скролл внутри редактора.
  // Если скролл происходит в контейнере, который НЕ содержит гуттер (например, внутри .tiptap-editor),
  // то сдвигаем гуттер transform'ом синхронно, чтобы не было «догонялок» и не требовался клик.
  React.useEffect(() => {
    if (!enableConditionTools) return undefined;
    if (!editor) return;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const mainScroller = mainContainerRef.current;

    const isScrollable = (el) => {
      if (!el) return false;
      if (!(el instanceof HTMLElement)) return false;
      try {
        const s = window.getComputedStyle(el);
        const oy = (s.overflowY || '').toLowerCase();
        const canScroll = (oy === 'auto' || oy === 'scroll');
        return canScroll && el.scrollHeight > el.clientHeight + 1;
      } catch (_) {
        return false;
      }
    };
    const resolveScrollerFromTarget = (target) => {
      // В стандартном режиме внутри модалки нам важно всегда считать основным скроллером
      // именно контейнер редактора. Иначе гуттер может начать синхронизироваться с прокруткой
      // страницы/оверлея и визуально «отрываться» от текста.
      if (!fullscreen && mainScroller && isScrollable(mainScroller)) {
        try {
          if (target instanceof Node && wrapper.contains(target)) return mainScroller;
        } catch (_) { }
      }

      if (target instanceof HTMLElement && isScrollable(target)) return target;
      if (target instanceof HTMLElement) {
        let el = target.parentElement;
        while (el && el !== wrapper) {
          if (isScrollable(el)) return el;
          el = el.parentElement;
        }
      }
      const primary = scrollPrimaryRef.current;
      if (primary && primary instanceof HTMLElement) return primary;
      if (mainScroller && isScrollable(mainScroller)) return mainScroller;
      // В стандартном режиме не используем document.scrollingElement как fallback,
      // иначе возможна синхронизация гуттера с прокруткой страницы позади модалки.
      if (fullscreen) {
        const docEl = document.scrollingElement;
        if (docEl && docEl instanceof HTMLElement && docEl.scrollHeight > docEl.clientHeight + 1) return docEl;
      }
      return null;
    };

    let raf = 0;
    const scheduleRecalc = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        updateConditionalGutter();
      });
    };

    const syncForScroller = (scroller) => {
      const gutterEl = conditionalGutterRef.current;
      if (!gutterEl) {
        scheduleRecalc();
        return;
      }
      if (!scroller) {
        gutterEl.style.transform = '';
        scheduleRecalc();
        return;
      }

      // Не синхронизируем гуттер с прокруткой элементов вне редактора,
      // иначе при скролле в модалке гуттер может двигаться отдельно от текста.
      try {
        if (scroller instanceof HTMLElement && !wrapper.contains(scroller) && scroller !== mainScroller) {
          gutterEl.style.transform = '';
          scheduleRecalc();
          return;
        }
      } catch (_) { }

      // Если скролл в контейнере, который содержит гуттер — гуттер и так едет вместе с DOM.
      if (scroller.contains(gutterEl)) {
        gutterEl.style.transform = '';
        scheduleRecalc();
        return;
      }

      const bases = scrollBasesRef.current;
      if (!bases.has(scroller)) bases.set(scroller, Number(scroller.scrollTop || 0));
      const baseTop = Number(bases.get(scroller) || 0);
      const delta = Number(scroller.scrollTop || 0) - baseTop;
      gutterEl.style.transform = delta ? `translate3d(0, ${-delta}px, 0)` : '';
    };

    const onScrollCapture = (e) => {
      const target = e?.target;
      const scroller = resolveScrollerFromTarget(target);
      syncForScroller(scroller);
    };

    wrapper.addEventListener('scroll', onScrollCapture, true);
    // На трекпаде/колесе иногда скролл идёт «мимо» scroll-события для нужного контейнера,
    // но wheel при этом всегда приходит — используем его как страховку.
    const onWheel = (e) => {
      const target = e?.target;
      const scroller = resolveScrollerFromTarget(target);
      // wheel может прийти раньше обновления scrollTop, поэтому делаем sync в rAF
      window.requestAnimationFrame(() => syncForScroller(scroller));
    };
    wrapper.addEventListener('wheel', onWheel, { passive: true });
    wrapper.addEventListener('touchmove', onWheel, { passive: true });

    return () => {
      try { wrapper.removeEventListener('scroll', onScrollCapture, true); } catch (_) { }
      try { wrapper.removeEventListener('wheel', onWheel); } catch (_) { }
      try { wrapper.removeEventListener('touchmove', onWheel); } catch (_) { }
      if (raf) {
        window.cancelAnimationFrame(raf);
        raf = 0;
      }
    };
  }, [editor, fullscreen, updateConditionalGutter, enableConditionTools]);

  // В стандартном режиме колесо мыши/трекпад иногда начинает скроллить "не тот" контейнер
  // (вплоть до прокрутки внешнего модального окна/страницы). В итоге A4‑страница с текстом
  // «уезжает», появляется пустота, а IF-гуттер продолжает двигаться.
  // Принудительно направляем прокрутку в основной скроллер редактора.
  React.useEffect(() => {
    if (fullscreen) return;

    let mounted = true;
    let cleanup = null;

    const attach = () => {
      if (!mounted) return;
      const wrapper = wrapperRef.current;
      const mainScroller = mainContainerRef.current;
      const a4 = a4ContainerRef.current;
      if (!wrapper || !mainScroller || !a4) {
        window.requestAnimationFrame(attach);
        return;
      }

      const isInModal = () => {
        try {
          return !!wrapper.closest?.('.template-editor-modal, .document-editor-modal, .document-editor-overlay');
        } catch (_) {
          return false;
        }
      };
      const modalRoot = (() => {
        try {
          return wrapper.closest?.('.template-editor-modal, .document-editor-modal, .document-editor-overlay') || null;
        } catch (_) {
          return null;
        }
      })();
      const modalSidebar = (() => {
        try {
          return modalRoot?.querySelector?.('.editor-sidebar') || null;
        } catch (_) {
          return null;
        }
      })();

      const isScrollable = (el) => {
        if (!el || !(el instanceof HTMLElement)) return false;
        return (el.scrollHeight || 0) > (el.clientHeight || 0) + 1;
      };

      const shouldLetNativeScroll = (targetEl) => {
        // Поповер условий/прочие всплывашки должны уметь скроллиться сами.
        if (targetEl?.closest?.('.conditional-gutter-popover')) return true;
        if (targetEl?.closest?.('.condition-editor-modal')) return true;
        if (targetEl?.closest?.('.react-datepicker, .react-datepicker-popper')) return true;
        if (targetEl?.closest?.('.preview-modal')) return true;
        return false;
      };

      const scrollMain = (delta) => {
        if (!isScrollable(mainScroller)) return false;
        const maxTop = Math.max(0, (mainScroller.scrollHeight || 0) - (mainScroller.clientHeight || 0));
        const nextTop = Math.min(maxTop, Math.max(0, Number(mainScroller.scrollTop || 0) + Number(delta || 0)));
        mainScroller.scrollTop = nextTop;
        return true;
      };

      const onWheelCapture = (e) => {
        const target = e?.target;
        if (!(target instanceof Node)) return;
        if (!wrapper.contains(target)) return;
        const targetEl = (target instanceof HTMLElement) ? target : null;
        if (shouldLetNativeScroll(targetEl)) return;

        const delta = Number(e.deltaY || 0);
        const inModal = !!modalRoot && isInModal();
        const didScrollMain = scrollMain(delta);

        // В модалке всегда блокируем wheel-скролл наружу (иначе «утекает» в html/body и даёт пустоту).
        // Вне модалки — блокируем только если реально крутим основной скроллер.
        if (inModal || didScrollMain) {
          e.preventDefault();
          e.stopPropagation();
          try { if (e.stopImmediatePropagation) e.stopImmediatePropagation(); } catch (_) { }
        }
      };

      const onWindowWheelCapture = (e) => {
        const target = e?.target;
        if (!(target instanceof Node)) return;
        const targetEl = (target instanceof HTMLElement) ? target : null;
        if (!modalRoot) return;
        if (!modalRoot.contains(target)) return;
        // Это обработчик только для текущего экземпляра редактора (в модалке может быть несколько оверлеев).
        if (!modalRoot.contains(wrapper)) return;
        // Не мешаем скроллу сайдбара с переменными.
        if (modalSidebar && modalSidebar.contains(target)) return;
        if (wrapper.contains(target)) return;
        if (shouldLetNativeScroll(targetEl)) return;

        const delta = Number(e.deltaY || 0);
        scrollMain(delta);

        // В модалке всегда блокируем wheel, даже если основной скроллер не скроллится (иначе «утекает» наружу).
        e.preventDefault();
        e.stopPropagation();
        try { if (e.stopImmediatePropagation) e.stopImmediatePropagation(); } catch (_) { }
      };

      wrapper.addEventListener('wheel', onWheelCapture, { passive: false, capture: true });
      // Вешаем обработчик не на window, а на корень модалки: это уменьшает шум в консоли DevTools
      // и не трогает события вне модального окна.
      if (modalRoot) {
        modalRoot.addEventListener('wheel', onWindowWheelCapture, { passive: false, capture: true });
      }

      cleanup = () => {
        try { wrapper.removeEventListener('wheel', onWheelCapture, { capture: true }); } catch (_) { }
        try { modalRoot?.removeEventListener?.('wheel', onWindowWheelCapture, { capture: true }); } catch (_) { }
      };
    };

    attach();
    return () => {
      mounted = false;
      try { cleanup?.(); } catch (_) { }
    };
  }, [fullscreen]);

	  // Поправляем позиционирование поповера IF, чтобы он всегда был видимым (не прятался под тулбаром).
	  React.useLayoutEffect(() => {
	    if (!activeConditionalGutter || !conditionalPopoverRef.current) return;
	    const pop = conditionalPopoverRef.current;
	    const rect = pop.getBoundingClientRect();

	    const vw = window.innerWidth || 0;
	    const vh = window.innerHeight || 0;
	    const pad = 8;

    const toolbarEl = wrapperRef.current?.querySelector?.('.tiptap-toolbar');
    const toolbarRect = toolbarEl?.getBoundingClientRect?.();
    const minTop = Math.max(pad, (toolbarRect?.bottom || 0) + pad);

	    let nextLeft = Number(activeConditionalGutter.position?.left || 0);
	    let nextTop = Number(activeConditionalGutter.position?.top || 0);

	    // Стараемся держать поповер в пределах viewport и, по возможности, вне области текста (A4),
	    // чтобы он не перекрывал выбранный блок.
	    const a4Rect = a4ContainerRef.current?.getBoundingClientRect?.();
	    const boundsLeft = pad;
	    const boundsRight = vw - pad;
	    const maxLeft = Math.max(boundsLeft, boundsRight - rect.width);

	    // Если поповер оказался над областью A4 и есть место справа/слева — вытолкнем его наружу.
	    const a4Left = Number(a4Rect?.left ?? pad);
	    const a4Right = Number(a4Rect?.right ?? (vw - pad));
	    const a4Top = Number(a4Rect?.top ?? 0);
	    const a4Bottom = Number(a4Rect?.bottom ?? vh);
	    const overlapsA4 = !(
	      (nextLeft + rect.width) <= a4Left ||
	      nextLeft >= a4Right ||
	      (nextTop + rect.height) <= a4Top ||
	      nextTop >= a4Bottom
	    );
	    if (overlapsA4) {
	      const spaceRight = Math.max(0, boundsRight - (a4Right + pad));
	      const spaceLeft = Math.max(0, (a4Left - pad) - boundsLeft);
	      if (spaceRight >= rect.width) {
	        nextLeft = a4Right + pad;
	      } else if (spaceLeft >= rect.width) {
	        nextLeft = (a4Left - pad) - rect.width;
	      }
	    }

	    if (nextLeft + rect.width > boundsRight) nextLeft = maxLeft;
	    if (nextLeft < boundsLeft) nextLeft = boundsLeft;

	    if (nextTop + rect.height > vh - pad) nextTop = Math.max(minTop, vh - rect.height - pad);
	    if (nextTop < minTop) nextTop = minTop;

    const prevLeft = Number(activeConditionalGutter.position?.left || 0);
    const prevTop = Number(activeConditionalGutter.position?.top || 0);
    if (nextLeft !== prevLeft || nextTop !== prevTop) {
      setActiveConditionalGutter((prev) => prev ? ({ ...prev, position: { left: nextLeft, top: nextTop } }) : prev);
    }
	  }, [activeConditionalGutter]);

	  const setHoveredConditionalRange = React.useCallback((groupId) => {
	    if (!editor) return;
	    const view = editor.view;
	    if (!view) return;
	    if (!groupId) {
	      view.dispatch(view.state.tr.setMeta(conditionalMarkersPluginKey, { type: 'clearHover' }));
	      return;
	    }
	    view.dispatch(view.state.tr.setMeta(conditionalMarkersPluginKey, { type: 'hover', groupId: String(groupId) }));
	  }, [editor]);

	  const setSelectedConditionalRange = React.useCallback((groupId) => {
	    if (!editor) return;
	    const view = editor.view;
	    if (!view) return;
	    if (!groupId) {
	      setSelectedConditionalGroupId(null);
	      try {
	        const tr = view.state.tr.setMeta(conditionalMarkersPluginKey, { type: 'clearPreviewRange' });
	        tr.setMeta('addToHistory', false);
	        view.dispatch(tr);
	      } catch (_) { }
	      view.dispatch(view.state.tr.setMeta(conditionalMarkersPluginKey, { type: 'clearSelect' }));
	      return;
	    }
	    const gid = String(groupId);
	    setSelectedConditionalGroupId(gid);
	    try {
	      const tr = view.state.tr.setMeta(conditionalMarkersPluginKey, { type: 'clearPreviewRange' });
	      tr.setMeta('addToHistory', false);
	      view.dispatch(tr);
	    } catch (_) { }
	    view.dispatch(view.state.tr.setMeta(conditionalMarkersPluginKey, { type: 'select', groupId: gid }));
	  }, [editor]);

  // Закрытие поповера: клик вне/ESC
  React.useEffect(() => {
    if (!enableConditionTools) return undefined;
    if (!activeConditionalGutter) return;
    const onDocDown = (e) => {
      const target = e?.target;
      if (target?.closest?.('.conditional-gutter-popover')) return;
      if (target?.closest?.('.conditional-gutter-badge')) return;
      // При перетаскивании границ IF не закрываем поповер и не снимаем выделение.
      if (target?.closest?.('.conditional-resize-layer')) return;
      if (target?.closest?.('.conditional-resize-handle')) return;
      setSelectedConditionalRange(null);
      setActiveConditionalGutter(null);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Если открыто модальное окно редактирования условий — Esc должен закрыть только его.
      if (conditionEditorOpen) return;
      // Важно: Esc должен закрывать поповер IF и НЕ закрывать модальное окно редактора шаблона.
      try {
        e.preventDefault();
        // Перехватываем максимально рано: у некоторых модалок обработчик висит на window в capture
        // и срабатывает раньше document. Поэтому вешаемся на window и используем stopImmediatePropagation.
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        e.stopPropagation();
      } catch (_) { }
      setSelectedConditionalRange(null);
      setActiveConditionalGutter(null);
    };
    document.addEventListener('mousedown', onDocDown, true);
    // Используем window + capture, чтобы перехватить Esc раньше внешних модалок/фуллскрина.
    window.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDocDown, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [activeConditionalGutter, setSelectedConditionalRange, conditionEditorOpen, enableConditionTools]);

  // Появление ручек изменения границ IF только при наведении на край (чтобы не мешали видеть границу).
  React.useEffect(() => {
    if (!enableConditionTools) return undefined;
    const a4 = a4ContainerRef.current;
    if (!a4) return;
    if (mode !== 'edit') return;
    if (!conditionalResizeUI || !selectedConditionalGroupId) {
      setConditionalResizeHandlesVisible({ start: false, end: false });
      return;
    }
    if (String(conditionalResizeUI.groupId) !== String(selectedConditionalGroupId)) {
      setConditionalResizeHandlesVisible({ start: false, end: false });
      return;
    }

    let raf = 0;
    const THRESH_X = 10; // px
    const PAD_Y = 10; // px

    const onMove = (e) => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const drag = conditionalResizeDragRef.current;
        if (drag) {
          setConditionalResizeHandlesVisible({ start: true, end: true });
          return;
        }

        const { a4Rect, scaleX, scaleY } = conditionalResizeLayoutRef.current || {};
        if (!a4Rect) return;
        const x = (e.clientX - a4Rect.left) / (scaleX || 1);
        const y = (e.clientY - a4Rect.top) / (scaleY || 1);

        const s = conditionalResizeUI.start || {};
        const en = conditionalResizeUI.end || {};
        const sH = Math.max(8, Number(s.h || 0));
        const eH = Math.max(8, Number(en.h || 0));

        const isNearStart = Math.abs(x - Number(s.x || 0)) <= THRESH_X && Math.abs(y - Number(s.y || 0)) <= (sH / 2 + PAD_Y);
        const isNearEnd = Math.abs(x - Number(en.x || 0)) <= THRESH_X && Math.abs(y - Number(en.y || 0)) <= (eH / 2 + PAD_Y);

        if (isNearStart || isNearEnd) {
          setConditionalResizeHandlesVisible({ start: isNearStart, end: isNearEnd });
        } else {
          setConditionalResizeHandlesVisible({ start: false, end: false });
        }
      });
    };

    const onLeave = () => {
      const drag = conditionalResizeDragRef.current;
      if (drag) return;
      setConditionalResizeHandlesVisible({ start: false, end: false });
    };

    a4.addEventListener('mousemove', onMove, true);
    a4.addEventListener('mouseleave', onLeave, true);
    return () => {
      a4.removeEventListener('mousemove', onMove, true);
      a4.removeEventListener('mouseleave', onLeave, true);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [mode, conditionalResizeUI, selectedConditionalGroupId, enableConditionTools]);

  // Закрытие редактора условий по Esc (и не даём Esc закрывать внешние модалки/выходить из fullscreen)
	  React.useEffect(() => {
	    if (!conditionEditorOpen) return;
	    const onKey = (e) => {
	      if (e.key !== 'Escape') return;
	      try {
	        e.preventDefault();
	        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
	        e.stopPropagation();
	      } catch (_) { }
	      restoreConditionalGutterOnCloseRef.current = true;
	      setConditionEditorOpen(false);
	    };
	    window.addEventListener('keydown', onKey, true);
	    return () => window.removeEventListener('keydown', onKey, true);
	  }, [conditionEditorOpen]);

		  const editConditional = React.useCallback((from, groupId) => {
		    if (!editor) return;
			    const view = editor.view;
			    const doc = view.state.doc;
			    const gid = String(groupId || '');
			    let current = '';
    // groupId может быть «синтетическим» (__pos_...) для старых блоков без data-cgid.
    // В этом случае искать по attrs.groupId бессмысленно — нужно брать узел по позиции.
    const isSyntheticGid = gid.startsWith('__pos_');
    if (gid && !isSyntheticGid) {
      doc.descendants((node) => {
        if (current) return false;
        if (node.type?.name !== 'conditionalBlock' && node.type?.name !== 'conditionalSection') return;
        if (String(node.attrs?.groupId || '') === gid) {
          current = String(node.attrs?.condition || '');
          return false;
        }
      });
    }
    if (!current) {
      const $pos = doc.resolve(from);
      const node = $pos.nodeAfter;
      if (!node || (node.type?.name !== 'conditionalBlock' && node.type?.name !== 'conditionalSection')) return;
      current = String(node.attrs?.condition || '');
    }
		    openConditionEditor({
		      title: 'Изменить условие показа блока',
		      initial: current || "debtor_type_code == 'legal'",
		      restoreGutter: activeConditionalGutter,
		      onApply: (nextExpr) => {
		        const trimmed = String(nextExpr || '').trim();
		        if (!trimmed) return;
		        let tr = view.state.tr;
	        if (gid && !isSyntheticGid) {
	          doc.descendants((node, pos) => {
	            if (node.type?.name !== 'conditionalBlock' && node.type?.name !== 'conditionalSection') return;
	            if (String(node.attrs?.groupId || '') !== gid) return;
	            tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, condition: trimmed });
	          });
	        } else {
	          const $pos = doc.resolve(from);
	          const node = $pos.nodeAfter;
	          if (!node || (node.type?.name !== 'conditionalBlock' && node.type?.name !== 'conditionalSection')) return;
	          tr = tr.setNodeMarkup(from, undefined, { ...node.attrs, condition: trimmed });
		        }
		        view.dispatch(tr);
		        view.focus();
		        restoreConditionalGutterOnCloseRef.current = true;
		      },
		    });
		  }, [editor, openConditionEditor, activeConditionalGutter]);

  const deleteConditional = React.useCallback((from, groupId) => {
    if (!editor) return;
    const view = editor.view;
    const doc = view.state.doc;
    const gid = String(groupId || '');
    const isSyntheticGid = gid.startsWith('__pos_');
    let tr = view.state.tr;

    if (gid && !isSyntheticGid) {
      const positions = [];
      doc.descendants((node, pos) => {
        if (node.type?.name !== 'conditionalBlock' && node.type?.name !== 'conditionalSection') return;
        if (String(node.attrs?.groupId || '') !== gid) return;
        positions.push({ pos, node });
      });
      positions.sort((a, b) => b.pos - a.pos);
      positions.forEach(({ pos, node }) => {
        tr = tr.replace(pos, pos + node.nodeSize, new Slice(node.content, 0, 0));
      });
    } else {
      const $pos = doc.resolve(from);
      const node = $pos.nodeAfter;
      if (!node || (node.type?.name !== 'conditionalBlock' && node.type?.name !== 'conditionalSection')) return;
      tr = tr.replace(from, from + node.nodeSize, new Slice(node.content, 0, 0));
    }

    view.dispatch(tr);
    view.focus();
    setActiveConditionalGutter(null);
  }, [editor]);

  React.useLayoutEffect(() => {
    const el = topInlineSlotRef.current;
    if (!el) {
      setTopInlineMeasuredHeight(0);
      return undefined;
    }
    const measure = () => {
      try {
        const rect = Number(el.getBoundingClientRect?.().height) || 0;
        const h = Math.max(rect, Number(el.offsetHeight) || 0, Number(el.clientHeight) || 0);
        setTopInlineMeasuredHeight(Math.max(0, Math.ceil(h)));
      } catch (_) {
        setTopInlineMeasuredHeight(0);
      }
    };
    measure();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => {
      if (ro) ro.disconnect();
    };
  }, [topInlineSlot]);

  const topInlineGapValue = Math.max(
    0,
    Number(topInlineGapPx ?? bodyGuide?.topGapPx) || 0
  );
  const topInlinePropContentHeightValue = Math.max(
    0,
    Number(topInlineContentHeightPx ?? bodyGuide?.topContentHeightPx) || 0
  );
  const topInlineContentHeightValue = Math.max(
    0,
    topInlineSlot
      ? (Number(topInlineMeasuredHeight) || 0)
      : topInlinePropContentHeightValue
  );
  const topInlineEffectiveGapValue = Math.max(topInlineGapValue, topInlineContentHeightValue);
  const topInlineExtraGapValue = Math.max(0, topInlineEffectiveGapValue - topInlineContentHeightValue);
  const resolvedContentTopPaddingPx = Math.max(
    0,
    Number(contentTopPaddingPx ?? margins.top) || 0
  );

  const bodyGuideLines = (() => {
    const lines = [];
    if (!bodyGuide || typeof bodyGuide !== 'object') return lines;

    const pageHeightPx = Math.max(
      1123,
      Number(a4ContainerRef.current?.scrollHeight) || 0,
      Number(a4ContainerRef.current?.offsetHeight) || 0
    );
    const clampY = (y) => Math.max(0, Math.min(pageHeightPx, y));
    const topMargin = Math.max(0, Number(margins.top || 0));
    const bottomMargin = Math.max(0, Number(margins.bottom || 0));

    if (bodyGuide.type === 'content') {
      const topGapPx = topInlineGapValue;
      const topContentHeightPx = topInlineContentHeightValue;
      const effectiveTopGapPx = Math.max(topGapPx, topContentHeightPx);
      const bottomGapPx = Math.max(0, Number(bodyGuide.bottomGapPx) || 0);
      let footerTextStartY = null;
      if (bottomInlineSlot && a4ContainerRef.current) {
        const containerEl = a4ContainerRef.current;
        const anchorEl = containerEl.querySelector('.a4-inline-slot--bottom .hf-inline-editor')
          || containerEl.querySelector('.a4-inline-slot--bottom');
        if (anchorEl) {
          let top = 0;
          let node = anchorEl;
          while (node && node !== containerEl) {
            top += Number(node.offsetTop) || 0;
            node = node.offsetParent;
          }
          if (node === containerEl) {
            footerTextStartY = Math.max(0, Math.round(top));
          }
        }
      }
      const desiredFooterGuideY = pageHeightPx - (bottomMargin + bottomGapPx);
      const finalFooterGuideY = Number.isFinite(footerTextStartY)
        ? Math.min(desiredFooterGuideY, footerTextStartY)
        : desiredFooterGuideY;
      lines.push({
        type: 'header',
        y: clampY(topMargin + effectiveTopGapPx),
        label: 'Граница начала текста',
      });
      lines.push({
        type: 'footer',
        y: clampY(finalFooterGuideY),
        label: 'Граница конца текста',
      });
      return lines;
    }

    const guideType = bodyGuide.type === 'footer'
      ? 'footer'
      : (bodyGuide.type === 'header' ? 'header' : null);
    if (!guideType) return lines;

    const guideGapPx = Math.max(0, Number(bodyGuide.gapPx) || 0);
    const guideContentHeightPx = Math.max(0, Number(bodyGuide.contentHeightPx) || 0);
    const yRaw = guideType === 'footer'
      ? (pageHeightPx - (bottomMargin + guideContentHeightPx + guideGapPx))
      : (topMargin + guideContentHeightPx + guideGapPx);
    lines.push({
      type: guideType,
      y: clampY(yRaw),
      label: guideType === 'footer' ? 'Граница тела (низ)' : 'Граница тела (верх)',
    });
    return lines;
  })();

  if (!editor) {
    return null;
  }

  return (
    <div
      ref={wrapperRef}
      className={`tiptap-editor-wrapper a4-format ${fullscreen ? 'fullscreen-mode' : ''} ${!fullscreen && !zoomReady ? 'zoom-pending' : ''} ${showHiddenChars ? 'show-hidden-chars' : ''}`}
      style={{
        '--a4-padding-top': `${resolvedContentTopPaddingPx}px`,
        '--a4-padding-right': `${margins.right}px`,
        '--a4-padding-bottom': `${margins.bottom}px`,
        '--a4-padding-left': `${margins.left}px`,
        '--pm-margin-left': `${margins.left}px`,
        '--pm-margin-right': `${margins.right}px`,
        '--pm-margin-top': `${margins.top}px`,
        '--pm-margin-bottom': `${margins.bottom}px`,
        '--a4-top-inline-extra-gap': `${Math.max(0, topInlineExtraGapValue)}px`,
        '--a4-content-height': `${1123 - margins.top - margins.bottom}px`,
        '--editor-zoom': String(editorZoom),
        '--footer-visual-spacer': `${Math.max(0, footerVisualSpacer)}px`,
      }}
      data-editor-section-mode={String(sectionMode || 'content')}
    >
      {mode === 'edit' && (
        <div className={`tiptap-toolbar${externalToolbarActive ? ' has-external-target' : ''}`} aria-disabled="false">
          {/* Кнопки Отменить/Вернуть */}
          <button
            onClick={() => {
              if (externalToolbarActive && typeof onExternalToolbarCommand === 'function') {
                if (onExternalToolbarCommand('undo')) return;
              }
              editor.chain().focus().undo().run();
            }}
            disabled={!editor.can().undo()}
            title="Отменить (Ctrl+Z)"
            type="button"
            className="undo-btn"
          >
            ↶
          </button>
          <button
            onClick={() => {
              if (externalToolbarActive && typeof onExternalToolbarCommand === 'function') {
                if (onExternalToolbarCommand('redo')) return;
              }
              editor.chain().focus().redo().run();
            }}
            disabled={!editor.can().redo()}
            title="Вернуть (Ctrl+Y)"
            type="button"
            className="redo-btn"
          >
            ↷
          </button>

          <div className="toolbar-separator" />
          {/* Поля A4 */}
          <button
            onClick={() => setShowMarginsPanel(!showMarginsPanel)}
            title="Поля страницы (в миллиметрах)"
            type="button"
          >
            Поля
          </button>
          {showMarginsPanel && (
            <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', marginLeft: 8 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>Сверху
                <input type="text" inputMode="decimal" value={marginInputsMm.top}
                  onChange={(e) => handleMarginInputChange('top', e.target.value)}
                  onBlur={() => commitMarginInput('top')}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitMarginInput('top'); }}
                  style={{ width: 64 }} />
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>Справа
                <input type="text" inputMode="decimal" value={marginInputsMm.right}
                  onChange={(e) => handleMarginInputChange('right', e.target.value)}
                  onBlur={() => commitMarginInput('right')}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitMarginInput('right'); }}
                  style={{ width: 64 }} />
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>Снизу
                <input type="text" inputMode="decimal" value={marginInputsMm.bottom}
                  onChange={(e) => handleMarginInputChange('bottom', e.target.value)}
                  onBlur={() => commitMarginInput('bottom')}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitMarginInput('bottom'); }}
                  style={{ width: 64 }} />
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>Слева
                <input type="text" inputMode="decimal" value={marginInputsMm.left}
                  onChange={(e) => handleMarginInputChange('left', e.target.value)}
                  onBlur={() => commitMarginInput('left')}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitMarginInput('left'); }}
                  style={{ width: 64 }} />
              </label>
            </div>
          )}

          {/* Выбор шрифта (один список с группами и вариантами) */}
          <select
            onChange={(e) => {
              const nextFontValue = e.target.value;
              if (externalToolbarActive && typeof onExternalToolbarCommand === 'function') {
                if (onExternalToolbarCommand('setFontFamily', nextFontValue)) return;
              }
              if (nextFontValue === 'default') {
                editor.chain().focus(undefined, { scrollIntoView: false }).unsetFontFamily().run();
                return;
              }
              editor.chain().focus(undefined, { scrollIntoView: false }).setFontFamily(nextFontValue).run();
            }}
            className="font-family-select"
            title="Шрифт"
            value={(() => {
              if (externalToolbarActive) {
                return externalToolbarState?.fontFamily || 'default';
              }
              const currentFont = getCurrentFontFamily();
              if (currentFont === 'default') return 'default';
              const normalizedCurrent = normalizeFontFamily(currentFont);
              for (const group of fontFamilyGroups) {
                for (const variant of group.variants) {
                  if (normalizeFontFamily(variant.value) === normalizedCurrent) {
                    return variant.value;
                  }
                }
              }
              return 'default';
            })()}
          >
            <option value="default">Шрифт по умолчанию</option>
            {fontFamilyGroups.map((group) => (
              <optgroup key={group.key} label={group.label}>
                {group.variants.map((variant) => (
                  <option key={`${group.key}-${variant.key}`} value={variant.value} style={{ fontFamily: variant.value }}>
                    {group.variants.length > 1 ? `${variant.label}` : group.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>

          {/* Выбор размера шрифта */}
          <select
            onChange={(e) => {
              if (externalToolbarActive && typeof onExternalToolbarCommand === 'function') {
                if (onExternalToolbarCommand('setFontSize', e.target.value)) return;
              }
              if (e.target.value === 'default') {
                editor.chain().focus(undefined, { scrollIntoView: false }).unsetFontSize().run();
              } else {
                editor.chain().focus(undefined, { scrollIntoView: false }).setFontSize(e.target.value).run();
              }
            }}
            className="font-size-select"
            title="Размер шрифта"
            value={externalToolbarActive ? (externalToolbarState?.fontSize || 'default') : getCurrentFontSize()}
          >
            <option value="default">Размер</option>
            {fontSizes.map(size => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <select
            onChange={(e) => {
              if (externalToolbarActive && typeof onExternalToolbarCommand === 'function') {
                if (onExternalToolbarCommand('setLineHeight', e.target.value)) return;
              }
              if (e.target.value === 'default') {
                editor.chain().focus(undefined, { scrollIntoView: false }).unsetLineHeight().run();
              } else {
                editor.chain().focus(undefined, { scrollIntoView: false }).setLineHeight(Number(e.target.value)).run();
              }
            }}
            className="font-size-select"
            title="Межстрочный интервал"
            value={externalToolbarActive ? (externalToolbarState?.lineHeight || 'default') : getCurrentLineHeight()}
          >
            <option value="default">Межстрочный</option>
            {lineHeights.map((value) => (
              <option key={String(value)} value={String(value)}>
                {String(value)}
              </option>
            ))}
          </select>
          <select
            onChange={(e) => {
              if (externalToolbarActive && typeof onExternalToolbarCommand === 'function') {
                if (onExternalToolbarCommand('setParagraphSpacing', e.target.value)) return;
              }
              if (e.target.value === 'default') {
                editor.chain().focus(undefined, { scrollIntoView: false }).unsetParagraphSpacing().run();
              } else {
                editor.chain().focus(undefined, { scrollIntoView: false }).setParagraphSpacing(Number(e.target.value)).run();
              }
            }}
            className="font-size-select"
            title="Интервал после абзаца"
            value={externalToolbarActive ? (externalToolbarState?.paragraphSpacing || 'default') : getCurrentParagraphSpacing()}
          >
            <option value="default">Интервал абзаца</option>
            {paragraphSpacings.map((size) => (
              <option key={size} value={String(size)}>
                {size}pt
              </option>
            ))}
          </select>

          {/* Кнопки увеличения/уменьшения размера */}
          <button
            onClick={increaseFontSize}
            title="Увеличить размер шрифта"
            type="button"
            className="font-size-btn"
          >
            A+
          </button>
          <button
            onClick={decreaseFontSize}
            title="Уменьшить размер шрифта"
            type="button"
            className="font-size-btn"
          >
            A-
          </button>
          <button
            onClick={() => setShowHiddenChars((prev) => !prev)}
            title="Показать/скрыть непечатаемые символы"
            type="button"
            className={showHiddenChars ? 'is-active' : ''}
          >
            ¶
          </button>

          {showConditions && (
            <>
              <div className="toolbar-separator" />
              <button
                type="button"
                onClick={promptCondition}
                title="Обёрнуть выделенный текст условием [[if ...]]"
                className="btn-secondary"
              >
                IF
              </button>
              <button
                type="button"
                onClick={createLoopFromSelection}
                title="Создать цикл [[for ...]]"
                className="btn-secondary"
              >
                Цикл
              </button>
            </>
          )}

          {/* Форматирование текста */}
          <button
            onClick={() => {
              if (externalToolbarActive && typeof onExternalToolbarCommand === 'function') {
                if (onExternalToolbarCommand('toggleBold')) return;
              }
              editor.chain().focus().toggleBold().run();
            }}
            className={(externalToolbarActive ? !!externalToolbarState?.bold : editor.isActive('bold')) ? 'is-active' : ''}
            title="Жирный"
            type="button"
          >
            <strong>B</strong>
          </button>
          <button
            onClick={() => {
              if (externalToolbarActive && typeof onExternalToolbarCommand === 'function') {
                if (onExternalToolbarCommand('toggleItalic')) return;
              }
              editor.chain().focus().toggleItalic().run();
            }}
            className={(externalToolbarActive ? !!externalToolbarState?.italic : editor.isActive('italic')) ? 'is-active' : ''}
            title="Курсив"
            type="button"
          >
            <em>I</em>
          </button>
          <button
            onClick={() => {
              if (externalToolbarActive && typeof onExternalToolbarCommand === 'function') {
                if (onExternalToolbarCommand('toggleUnderline')) return;
              }
              editor.chain().focus().toggleUnderline().run();
            }}
            className={(externalToolbarActive ? !!externalToolbarState?.underline : editor.isActive('underline')) ? 'is-active' : ''}
            title="Подчеркнутый"
            type="button"
          >
            <u>U</u>
          </button>

          <div className="toolbar-separator" />

          {/* Выравнивание текста */}
          <button
            onClick={() => {
              if (externalToolbarActive && typeof onExternalToolbarCommand === 'function') {
                if (onExternalToolbarCommand('setTextAlign', 'left')) return;
              }
              editor.chain().focus().setTextAlign('left').run();
            }}
            className={(externalToolbarActive ? externalToolbarState?.textAlign === 'left' : editor.isActive({ textAlign: 'left' })) ? 'is-active' : ''}
            title="По левому краю"
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <rect x="1" y="3" width="10" height="1.5" />
              <rect x="1" y="5.5" width="14" height="1.5" />
              <rect x="1" y="8" width="8" height="1.5" />
              <rect x="1" y="10.5" width="12" height="1.5" />
            </svg>
          </button>
          <button
            onClick={() => {
              if (externalToolbarActive && typeof onExternalToolbarCommand === 'function') {
                if (onExternalToolbarCommand('setTextAlign', 'center')) return;
              }
              editor.chain().focus().setTextAlign('center').run();
            }}
            className={(externalToolbarActive ? externalToolbarState?.textAlign === 'center' : editor.isActive({ textAlign: 'center' })) ? 'is-active' : ''}
            title="По центру"
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <rect x="3" y="3" width="10" height="1.5" />
              <rect x="1" y="5.5" width="14" height="1.5" />
              <rect x="4" y="8" width="8" height="1.5" />
              <rect x="2" y="10.5" width="12" height="1.5" />
            </svg>
          </button>
          <button
            onClick={() => {
              if (externalToolbarActive && typeof onExternalToolbarCommand === 'function') {
                if (onExternalToolbarCommand('setTextAlign', 'right')) return;
              }
              editor.chain().focus().setTextAlign('right').run();
            }}
            className={(externalToolbarActive ? externalToolbarState?.textAlign === 'right' : editor.isActive({ textAlign: 'right' })) ? 'is-active' : ''}
            title="По правому краю"
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <rect x="5" y="3" width="10" height="1.5" />
              <rect x="1" y="5.5" width="14" height="1.5" />
              <rect x="7" y="8" width="8" height="1.5" />
              <rect x="3" y="10.5" width="12" height="1.5" />
            </svg>
          </button>
          <button
            onClick={() => {
              if (externalToolbarActive && typeof onExternalToolbarCommand === 'function') {
                if (onExternalToolbarCommand('setTextAlign', 'justify')) return;
              }
              editor.chain().focus().setTextAlign('justify').run();
            }}
            className={(externalToolbarActive ? externalToolbarState?.textAlign === 'justify' : editor.isActive({ textAlign: 'justify' })) ? 'is-active' : ''}
            title="По ширине"
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <rect x="1" y="3" width="14" height="1.5" />
              <rect x="1" y="5.5" width="14" height="1.5" />
              <rect x="1" y="8" width="14" height="1.5" />
              <rect x="1" y="10.5" width="14" height="1.5" />
            </svg>
          </button>

          <div className="toolbar-separator" />

          {/* Списки */}
          <button
            onClick={() => {
              if (externalToolbarActive && typeof onExternalToolbarCommand === 'function') {
                if (onExternalToolbarCommand('toggleBulletList')) return;
              }
              editor.chain().focus().toggleBulletList().run();
            }}
            className={(externalToolbarActive ? !!externalToolbarState?.bulletList : editor.isActive('bulletList')) ? 'is-active' : ''}
            title="Маркированный список"
            type="button"
          >
            •
          </button>
          <button
            onClick={() => {
              if (externalToolbarActive && typeof onExternalToolbarCommand === 'function') {
                if (onExternalToolbarCommand('toggleOrderedList')) return;
              }
              editor.chain().focus().toggleOrderedList().run();
            }}
            className={(externalToolbarActive ? !!externalToolbarState?.orderedList : editor.isActive('orderedList')) ? 'is-active' : ''}
            title="Нумерованный список"
            type="button"
          >
            1.
          </button>

          <div className="toolbar-separator" />

          {/* Таблицы */}
          <button
            onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: false }).run()}
            title="Вставить таблицу"
            type="button"
            className="table-action-btn"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
              <rect x="2.5" y="3.5" width="9" height="9" rx="0.8" />
              <path d="M5.5 3.5v9M8.5 3.5v9M2.5 6.5h9M2.5 9.5h9" />
              <path d="M13 3.2v4.2M10.9 5.3h4.2" />
            </svg>
          </button>
          {editor.isActive('table') && (
            <>
              <button
                onClick={() => editor.chain().focus().addColumnBefore().run()}
                title="Добавить колонку слева"
                type="button"
                className="table-action-btn"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
                  <rect x="3.5" y="3.5" width="9" height="9" rx="0.8" />
                  <path d="M6.5 3.5v9M9.5 3.5v9M3.5 6.5h9M3.5 9.5h9" />
                  <path d="M1.4 8h2.6M2.2 6.8 1 8l1.2 1.2" />
                  <path d="M1.6 3.2v3.6M0 5h3.2" />
                </svg>
              </button>
              <button
                onClick={() => editor.chain().focus().addColumnAfter().run()}
                title="Добавить колонку справа"
                type="button"
                className="table-action-btn"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
                  <rect x="3.5" y="3.5" width="9" height="9" rx="0.8" />
                  <path d="M6.5 3.5v9M9.5 3.5v9M3.5 6.5h9M3.5 9.5h9" />
                  <path d="M14.6 8H12M13.8 6.8 15 8l-1.2 1.2" />
                  <path d="M12.8 3.2v3.6M11.2 5h3.2" />
                </svg>
              </button>
              <button
                onClick={() => editor.chain().focus().deleteColumn().run()}
                title="Удалить колонку"
                type="button"
                className="table-action-btn"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
                  <rect x="3.5" y="3.5" width="9" height="9" rx="0.8" />
                  <path d="M8 3.5v9M3.5 6.5h9M3.5 9.5h9" />
                  <path d="M1.3 8h2.6" />
                </svg>
              </button>
              <button
                onClick={() => editor.chain().focus().addRowBefore().run()}
                title="Добавить строку сверху"
                type="button"
                className="table-action-btn"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
                  <rect x="3.5" y="3.5" width="9" height="9" rx="0.8" />
                  <path d="M6.5 3.5v9M9.5 3.5v9M3.5 6.5h9M3.5 9.5h9" />
                  <path d="M8 1.3v2.6M6.8 2.2 8 1l1.2 1.2" />
                  <path d="M12.8 1.2h2.4M14 0v2.4" />
                </svg>
              </button>
              <button
                onClick={() => editor.chain().focus().addRowAfter().run()}
                title="Добавить строку снизу"
                type="button"
                className="table-action-btn"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
                  <rect x="3.5" y="3.5" width="9" height="9" rx="0.8" />
                  <path d="M6.5 3.5v9M9.5 3.5v9M3.5 6.5h9M3.5 9.5h9" />
                  <path d="M8 14.7v-2.6M6.8 13.8 8 15l1.2-1.2" />
                  <path d="M12.8 14.8h2.4M14 13.6v2.4" />
                </svg>
              </button>
              <button
                onClick={() => editor.chain().focus().deleteRow().run()}
                title="Удалить строку"
                type="button"
                className="table-action-btn"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
                  <rect x="3.5" y="3.5" width="9" height="9" rx="0.8" />
                  <path d="M6.5 3.5v9M9.5 3.5v9M3.5 8h9" />
                  <path d="M12.9 14.7H15.5" />
                </svg>
              </button>
              <button
                onClick={() => editor.chain().focus().deleteTable().run()}
                title="Удалить таблицу"
                type="button"
                className="table-action-btn"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
                  <rect x="2.5" y="2.5" width="11" height="11" rx="0.8" />
                  <path d="M6.1 2.5v11M9.9 2.5v11M2.5 6.1h11M2.5 9.9h11" />
                  <path d="M1.4 1.4l13.2 13.2" />
                </svg>
              </button>
              <button
                onClick={() => editor.chain().focus().mergeCells().run()}
                title="Объединить ячейки"
                type="button"
                disabled={!editor.can().mergeCells()}
                className="table-action-btn"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
                  <rect x="2.5" y="3.5" width="11" height="9" rx="0.8" />
                  <path d="M8 3.5v9" />
                  <path d="M5.8 8H2.9M4.1 6.3 2.4 8l1.7 1.7" />
                  <path d="M10.2 8h2.9M11.9 6.3 13.6 8l-1.7 1.7" />
                </svg>
              </button>
              <button
                onClick={() => editor.chain().focus().setCellAttribute('verticalAlign', 'top').run()}
                title="Выравнивание по верхнему краю"
                type="button"
                className={`table-action-btn ${editor.getAttributes('tableCell')?.verticalAlign === 'top' ? 'is-active' : ''}`}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
                  <path d="M2 3h12" />
                  <path d="M8 12V5.5M6.8 6.7 8 5.5l1.2 1.2" />
                </svg>
              </button>
              <button
                onClick={() => editor.chain().focus().setCellAttribute('verticalAlign', 'middle').run()}
                title="Выравнивание по центру"
                type="button"
                className={`table-action-btn ${editor.getAttributes('tableCell')?.verticalAlign === 'middle' ? 'is-active' : ''}`}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
                  <path d="M2 8h12" />
                  <path d="M8 5.2v5.6" />
                  <path d="M6.8 6.4 8 5.2l1.2 1.2M6.8 9.6 8 10.8l1.2-1.2" />
                </svg>
              </button>
              <button
                onClick={() => editor.chain().focus().setCellAttribute('verticalAlign', 'bottom').run()}
                title="Выравнивание по нижнему краю"
                type="button"
                className={`table-action-btn ${editor.getAttributes('tableCell')?.verticalAlign === 'bottom' ? 'is-active' : ''}`}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
                  <path d="M2 13h12" />
                  <path d="M8 4v6.5M6.8 9.3 8 10.5l1.2-1.2" />
                </svg>
              </button>

              {/* Настройка границ таблицы */}
              <select
                onChange={(e) => {
                  const val = e.target.value;
                  const bw = `${val}px`;
                  try {
                    editor.chain().focus().updateAttributes('table', { tableBorderWidth: bw }).run();
                    window.requestAnimationFrame(() => {
                      syncTableDisplayStyles();
                    });
                  } catch (_) { }
                }}
                className="border-width-select"
                title="Толщина границ таблицы"
                value={(() => {
                  const raw = String(editor.getAttributes('table')?.tableBorderWidth || '1px').trim();
                  const m = raw.match(/^([0-9]+(?:\.[0-9]+)?)px$/);
                  return m ? m[1] : '1';
                })()}
                style={{ width: '150px' }}
              >
                <option value="0">Граница: нет</option>
                <option value="0.5">Граница: 0.5px</option>
                <option value="1">Граница: 1px</option>
                <option value="2">Граница: 2px</option>
                <option value="3">Граница: 3px</option>
              </select>

              {/* Высота строк таблицы */}
              <select
                onChange={(e) => {
                  const val = e.target.value; // в пикселях или 0
                  try {
                    const mh = val === '0' ? '0px' : `${val}px`;
                    editor.chain().focus().updateAttributes('table', { tableRowMinHeight: mh }).run();
                    window.requestAnimationFrame(() => {
                      syncTableDisplayStyles();
                    });
                  } catch (_) { }
                }}
                className="border-width-select"
                title="Минимальная высота строки"
                value={(() => {
                  const raw = String(editor.getAttributes('table')?.tableRowMinHeight || '0px').trim();
                  const m = raw.match(/^([0-9]+(?:\.[0-9]+)?)px$/);
                  if (!m) return '0';
                  const n = Number(m[1]);
                  return Number.isFinite(n) ? String(n) : '0';
                })()}
                style={{ marginLeft: 4, width: '156px' }}
              >
                <option value="0">Строка: авто</option>
                <option value="20">Строка: 20px</option>
                <option value="24">Строка: 24px</option>
                <option value="28">Строка: 28px</option>
                <option value="32">Строка: 32px</option>
                <option value="40">Строка: 40px</option>
              </select>

              {/* Внутренние отступы ячеек таблицы */}
              <select
                onChange={(e) => {
                  const val = e.target.value;
                  try {
                    const cp = `${Math.max(0, Number(val) || 0)}px`;
                    editor.chain().focus().updateAttributes('table', { tableCellPadding: cp }).run();
                    window.requestAnimationFrame(() => {
                      syncTableDisplayStyles();
                    });
                  } catch (_) { }
                }}
                className="border-width-select"
                title="Отступ текста от границ ячейки"
                value={(() => {
                  const raw = String(editor.getAttributes('table')?.tableCellPadding || '8px').trim();
                  const m = raw.match(/^([0-9]+(?:\.[0-9]+)?)px$/);
                  if (!m) return '8';
                  const n = Number(m[1]);
                  return Number.isFinite(n) ? String(Math.max(0, Math.round(n))) : '8';
                })()}
                style={{ marginLeft: 4, width: '150px' }}
              >
                <option value="0">Поля: 0px</option>
                <option value="2">Поля: 2px</option>
                <option value="4">Поля: 4px</option>
                <option value="6">Поля: 6px</option>
                <option value="8">Поля: 8px</option>
                <option value="10">Поля: 10px</option>
                <option value="12">Поля: 12px</option>
                <option value="16">Поля: 16px</option>
              </select>

              {/* Отступ таблицы до/после текста */}
              <select
                onChange={(e) => {
                  const val = e.target.value;
                  try {
                    const mv = `${Math.max(0, Number(val) || 0)}px`;
                    editor.chain().focus().updateAttributes('table', { tableMarginVert: mv }).run();
                    window.requestAnimationFrame(() => {
                      syncTableDisplayStyles();
                    });
                  } catch (_) { }
                }}
                className="border-width-select"
                title="Отступ таблицы сверху и снизу"
                value={(() => {
                  const raw = String(editor.getAttributes('table')?.tableMarginVert || '15px').trim();
                  const m = raw.match(/^([0-9]+(?:\.[0-9]+)?)px$/);
                  if (!m) return '15';
                  const n = Number(m[1]);
                  return Number.isFinite(n) ? String(Math.max(0, Math.round(n))) : '15';
                })()}
                style={{ marginLeft: 4, width: '172px' }}
              >
                <option value="0">Отступ до/после: 0px</option>
                <option value="4">Отступ до/после: 4px</option>
                <option value="8">Отступ до/после: 8px</option>
                <option value="12">Отступ до/после: 12px</option>
                <option value="15">Отступ до/после: 15px</option>
                <option value="20">Отступ до/после: 20px</option>
                <option value="24">Отступ до/после: 24px</option>
                <option value="32">Отступ до/после: 32px</option>
              </select>
            </>
          )}

          <div className="toolbar-separator" />

          {/* Кнопка разрыва страницы */}
          <button
            onClick={() => editor.chain().focus().insertPageBreak().run()}
            title="Разрыв страницы (Ctrl+Enter)"
            type="button"
            className="page-break-btn"
          >
            ⎵
          </button>

          {/* Кнопка полноэкранного режима */}
          <div className="toolbar-separator" />
          <button
            onClick={() => setFullscreen(!fullscreen)}
            className={fullscreen ? 'is-active' : ''}
            title={fullscreen ? 'Выход из полноэкранного режима' : 'Полноэкранный режим'}
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              {fullscreen ? (
                <g>
                  <path d="M6 0v2H4v2H2V2a2 2 0 0 1 2-2h2zM16 4V2a2 2 0 0 0-2-2h-2v2h2v2h2zM4 14v-2H2v2a2 2 0 0 0 2 2h2v-2H4zM14 12v2h-2v2h2a2 2 0 0 0 2-2v-2h-2z" />
                  <path d="M8 3L6 5h1.5v3H6l2 2 2-2H8.5V5H10L8 3z" opacity="0.7" />
                </g>
              ) : (
                <path d="M3 1a2 2 0 0 0-2 2v2h2V3h2V1H3zM13 1a2 2 0 0 1 2 2v2h-2V3h-2V1h2zM1 11v2a2 2 0 0 0 2 2h2v-2H3v-2H1zM15 11v2a2 2 0 0 1-2 2h-2v-2h2v-2h2zM6 6h4v4H6V6z" />
              )}
            </svg>
          </button>

        </div>
      )}

	      <div className="editor-main-container" ref={mainContainerRef}>
	        <div className="a4-zoom-wrapper" ref={zoomWrapperRef}>
	          <div className="a4-container" ref={a4ContainerRef}>
              {topInlineSlot ? (
                <div className="a4-inline-slot a4-inline-slot--top" ref={topInlineSlotRef}>
                  {topInlineSlot}
                </div>
              ) : null}
              {bodyGuideLines.map((line, idx) => (
                <div
                  key={`${line.type}-${idx}`}
                  className={`a4-body-guide a4-body-guide--${line.type}`}
                  style={{
                    top: `${line.y}px`,
                    left: `${Math.max(0, Number(margins.left || 0))}px`,
                    right: `${Math.max(0, Number(margins.right || 0))}px`,
                  }}
                  aria-hidden="true"
                >
                  <span className="a4-body-guide-label">{line.label}</span>
                </div>
              ))}
		            {enableConditionTools && conditionalResizeUI && String(conditionalResizeUI.groupId) === String(selectedConditionalGroupId) && (
	              <div className="conditional-resize-layer" aria-hidden="true">
	                <div
	                  className={`conditional-resize-handle conditional-resize-handle--start ${conditionalResizeHandlesVisible.start ? 'is-visible' : ''}`}
	                  style={{ transform: `translate3d(${Math.max(0, (conditionalResizeUI.start?.x || 0) - 14)}px, ${Math.max(0, (conditionalResizeUI.start?.y || 0) - 6)}px, 0)` }}
	                  title="Потяните, чтобы изменить начало блока"
	                  onMouseDown={(e) => {
	                    if (!editor) return;
	                    e.preventDefault();
	                    e.stopPropagation();
	                    const view = editor.view;
	                    const doc = view.state.doc;
	                    const gid = String(conditionalResizeUI.groupId || '');
	                    if (!gid) return;
	                    const startFrom = Number(conditionalResizeUI.from);
	                    const startTo = Number(conditionalResizeUI.to);
	                    const outer = computeOuterConditionalBounds(doc, gid);
	                    conditionalResizeDragRef.current = {
	                      gid,
	                      side: 'start',
	                      from: startFrom,
	                      to: startTo,
	                      outer,
	                      raf: 0,
	                      lastClient: null,
	                    };
	                    setConditionalResizeHandlesVisible({ start: true, end: true });

	                    const onMove = (ev) => {
	                      const drag = conditionalResizeDragRef.current;
	                      if (!drag || !editor) return;
	                      drag.lastClient = { x: ev.clientX, y: ev.clientY };
	                      if (drag.raf) return;
	                      drag.raf = window.requestAnimationFrame(() => {
	                        drag.raf = 0;
	                        const v = editor.view;
	                        const resolved = resolveConditionalEdgePosFromCoords(v, ev.clientX, ev.clientY, 'start', drag.outer.min, drag.outer.max);
	                        if (typeof resolved !== 'number') return;
	                        let nextFrom = clamp(resolved, drag.outer.min, drag.outer.max);
	                        nextFrom = snapConditionalEdgeOutsideVariable(v.state, nextFrom);
	                        nextFrom = snapConditionalEdgeOutsideForeignConditionals(v.state, nextFrom, drag.gid);
	                        nextFrom = clamp(nextFrom, drag.outer.min, drag.outer.max);
	                        let nextTo = drag.to;
	                        if (nextTo <= nextFrom) nextFrom = Math.max(drag.outer.min, nextTo - 1);

	                        // preview подсветки
                        try {
                          const tr = v.state.tr.setMeta(conditionalMarkersPluginKey, { type: 'previewRange', range: { from: nextFrom, to: nextTo } });
                          tr.setMeta('addToHistory', false);
                          v.dispatch(tr);
                        } catch (_) { }
                        try {
                          const blocked = findForeignConditionalNearPos(v.state, nextFrom, drag.gid, 6);
                          const tr = v.state.tr.setMeta(conditionalMarkersPluginKey, { type: blocked ? 'blockedRange' : 'clearBlockedRange', range: blocked || null });
                          tr.setMeta('addToHistory', false);
                          v.dispatch(tr);
                        } catch (_) { }

	                        // preview ручек
	                        try {
	                          const { a4Rect, scaleX, scaleY } = conditionalResizeLayoutRef.current || {};
	                          if (!a4Rect) return;
	                          const start = v.coordsAtPos(nextFrom);
	                          const end = v.coordsAtPos(nextTo);
	                          const relStartX = (start.left - a4Rect.left) / (scaleX || 1);
	                          const relStartY = (start.top - a4Rect.top) / (scaleY || 1);
	                          const relStartH = (start.bottom - start.top) / (scaleY || 1);
	                          const relEndX = (end.right - a4Rect.left) / (scaleX || 1);
	                          const relEndY = (end.top - a4Rect.top) / (scaleY || 1);
	                          const relEndH = (end.bottom - end.top) / (scaleY || 1);
	                          setConditionalResizeUI((prev) => prev ? ({
	                            ...prev,
	                            from: nextFrom,
	                            to: nextTo,
	                            start: { x: relStartX, y: relStartY + relStartH / 2, h: relStartH },
	                            end: { x: relEndX, y: relEndY + relEndH / 2, h: relEndH },
	                          }) : prev);
	                        } catch (_) { }

	                        drag.from = nextFrom;
	                      });
	                    };

		                    const onUp = (ev) => {
		                      const drag = conditionalResizeDragRef.current;
		                      conditionalResizeDragRef.current = null;
		                      window.removeEventListener('mousemove', onMove, true);
		                      window.removeEventListener('mouseup', onUp, true);
		                      if (!drag || !editor) return;
		                      // Если отпускание произошло до того, как отработал последний RAF,
		                      // добиваем финальную позицию синхронно, чтобы диапазон применился.
	                      try {
	                        const v = editor.view;
	                        const c = drag.lastClient || (ev ? { x: ev.clientX, y: ev.clientY } : null);
	                        const resolved = c ? resolveConditionalEdgePosFromCoords(v, c.x, c.y, 'start', drag.outer.min, drag.outer.max) : null;
	                        if (typeof resolved === 'number') {
	                          let nextFrom = clamp(resolved, drag.outer.min, drag.outer.max);
	                          nextFrom = snapConditionalEdgeOutsideVariableHard(v.state, nextFrom);
	                          nextFrom = snapConditionalEdgeOutsideForeignConditionals(v.state, nextFrom, drag.gid);
	                          nextFrom = clamp(nextFrom, drag.outer.min, drag.outer.max);
	                          if (drag.to <= nextFrom) nextFrom = Math.max(drag.outer.min, drag.to - 1);
	                          drag.from = nextFrom;
	                        }
	                      } catch (_) { }
	                      try {
	                        const tr = editor.view.state.tr.setMeta(conditionalMarkersPluginKey, { type: 'clearPreviewRange' });
	                        tr.setMeta('addToHistory', false);
	                        editor.view.dispatch(tr);
	                      } catch (_) { }
	                      try {
	                        const tr = editor.view.state.tr.setMeta(conditionalMarkersPluginKey, { type: 'clearBlockedRange' });
	                        tr.setMeta('addToHistory', false);
	                        editor.view.dispatch(tr);
	                      } catch (_) { }
	                      resizeConditionalGroup({ groupId: drag.gid, from: drag.from, to: drag.to });
	                      // На случай, если транзакция не применилась (например, из‑за валидации),
	                      // синхронизируем UI ручек с реальным документом.
	                      try { window.requestAnimationFrame(() => updateConditionalGutterRef.current?.()); } catch (_) { }
	                    };

	                    window.addEventListener('mousemove', onMove, true);
	                    window.addEventListener('mouseup', onUp, true);
	                  }}
	                />

	                <div
	                  className={`conditional-resize-handle conditional-resize-handle--end ${conditionalResizeHandlesVisible.end ? 'is-visible' : ''}`}
	                  style={{ transform: `translate3d(${Math.max(0, (conditionalResizeUI.end?.x || 0) + 2)}px, ${Math.max(0, (conditionalResizeUI.end?.y || 0) - 6)}px, 0)` }}
	                  title="Потяните, чтобы изменить конец блока"
	                  onMouseDown={(e) => {
	                    if (!editor) return;
	                    e.preventDefault();
	                    e.stopPropagation();
	                    const view = editor.view;
	                    const doc = view.state.doc;
	                    const gid = String(conditionalResizeUI.groupId || '');
	                    if (!gid) return;
	                    const startFrom = Number(conditionalResizeUI.from);
	                    const startTo = Number(conditionalResizeUI.to);
	                    const outer = computeOuterConditionalBounds(doc, gid);
	                    conditionalResizeDragRef.current = {
	                      gid,
	                      side: 'end',
	                      from: startFrom,
	                      to: startTo,
	                      outer,
	                      raf: 0,
	                      lastClient: null,
	                    };
	                    setConditionalResizeHandlesVisible({ start: true, end: true });

	                    const onMove = (ev) => {
	                      const drag = conditionalResizeDragRef.current;
	                      if (!drag || !editor) return;
	                      drag.lastClient = { x: ev.clientX, y: ev.clientY };
	                      if (drag.raf) return;
	                      drag.raf = window.requestAnimationFrame(() => {
	                        drag.raf = 0;
	                        const v = editor.view;
	                        const resolved = resolveConditionalEdgePosFromCoords(v, ev.clientX, ev.clientY, 'end', drag.outer.min, drag.outer.max);
	                        if (typeof resolved !== 'number') return;
	                        let nextTo = clamp(resolved, drag.outer.min, drag.outer.max);
	                        nextTo = snapConditionalEdgeOutsideVariable(v.state, nextTo);
	                        nextTo = snapConditionalEdgeOutsideForeignConditionals(v.state, nextTo, drag.gid);
	                        nextTo = clamp(nextTo, drag.outer.min, drag.outer.max);
	                        let nextFrom = drag.from;
	                        if (nextTo <= nextFrom) nextTo = Math.min(drag.outer.max, nextFrom + 1);

                      try {
                        const tr = v.state.tr.setMeta(conditionalMarkersPluginKey, { type: 'previewRange', range: { from: nextFrom, to: nextTo } });
                        tr.setMeta('addToHistory', false);
                        v.dispatch(tr);
                      } catch (_) { }
                      try {
                        const blocked = findForeignConditionalNearPos(v.state, nextTo, drag.gid, 6);
                        const tr = v.state.tr.setMeta(conditionalMarkersPluginKey, { type: blocked ? 'blockedRange' : 'clearBlockedRange', range: blocked || null });
                        tr.setMeta('addToHistory', false);
                        v.dispatch(tr);
                      } catch (_) { }

	                        try {
	                          const { a4Rect, scaleX, scaleY } = conditionalResizeLayoutRef.current || {};
	                          if (!a4Rect) return;
	                          const start = v.coordsAtPos(nextFrom);
	                          const end = v.coordsAtPos(nextTo);
	                          const relStartX = (start.left - a4Rect.left) / (scaleX || 1);
	                          const relStartY = (start.top - a4Rect.top) / (scaleY || 1);
	                          const relStartH = (start.bottom - start.top) / (scaleY || 1);
	                          const relEndX = (end.right - a4Rect.left) / (scaleX || 1);
	                          const relEndY = (end.top - a4Rect.top) / (scaleY || 1);
	                          const relEndH = (end.bottom - end.top) / (scaleY || 1);
	                          setConditionalResizeUI((prev) => prev ? ({
	                            ...prev,
	                            from: nextFrom,
	                            to: nextTo,
	                            start: { x: relStartX, y: relStartY + relStartH / 2, h: relStartH },
	                            end: { x: relEndX, y: relEndY + relEndH / 2, h: relEndH },
	                          }) : prev);
	                        } catch (_) { }

	                        drag.to = nextTo;
	                      });
	                    };

		                    const onUp = (ev) => {
		                      const drag = conditionalResizeDragRef.current;
		                      conditionalResizeDragRef.current = null;
		                      window.removeEventListener('mousemove', onMove, true);
		                      window.removeEventListener('mouseup', onUp, true);
		                      if (!drag || !editor) return;
		                      // Финальный "добив" позиции на mouseup (см. комментарий в start-handle).
	                      try {
	                        const v = editor.view;
	                        const c = drag.lastClient || (ev ? { x: ev.clientX, y: ev.clientY } : null);
	                        const resolved = c ? resolveConditionalEdgePosFromCoords(v, c.x, c.y, 'end', drag.outer.min, drag.outer.max) : null;
	                        if (typeof resolved === 'number') {
	                          let nextTo = clamp(resolved, drag.outer.min, drag.outer.max);
	                          nextTo = snapConditionalEdgeOutsideVariableHard(v.state, nextTo);
	                          nextTo = snapConditionalEdgeOutsideForeignConditionals(v.state, nextTo, drag.gid);
	                          nextTo = clamp(nextTo, drag.outer.min, drag.outer.max);
	                          if (nextTo <= drag.from) nextTo = Math.min(drag.outer.max, drag.from + 1);
	                          drag.to = nextTo;
	                        }
	                      } catch (_) { }
	                      try {
	                        const tr = editor.view.state.tr.setMeta(conditionalMarkersPluginKey, { type: 'clearPreviewRange' });
	                        tr.setMeta('addToHistory', false);
	                        editor.view.dispatch(tr);
	                      } catch (_) { }
	                      try {
	                        const tr = editor.view.state.tr.setMeta(conditionalMarkersPluginKey, { type: 'clearBlockedRange' });
	                        tr.setMeta('addToHistory', false);
	                        editor.view.dispatch(tr);
	                      } catch (_) { }
	                      resizeConditionalGroup({ groupId: drag.gid, from: drag.from, to: drag.to });
	                      try { window.requestAnimationFrame(() => updateConditionalGutterRef.current?.()); } catch (_) { }
	                    };

	                    window.addEventListener('mousemove', onMove, true);
	                    window.addEventListener('mouseup', onUp, true);
	                  }}
	                />
	              </div>
	            )}
		            {enableConditionTools && conditionalGutterItems.length > 0 && (
	              <div className="conditional-gutter" aria-hidden="true" ref={conditionalGutterRef}>
                {(() => {
                  const lineKeyFor = (top) => Math.round(Number(top || 0) / 4) * 4;
                  const byLine = new Map();
                  (conditionalGutterItems || []).forEach((item) => {
                    const lk = lineKeyFor(item.top);
                    const list = byLine.get(lk) || [];
                    list.push(item);
                    byLine.set(lk, list);
                  });

                  // 1) Линии — для каждого блока отдельно.
                  const lines = (conditionalGutterItems || []).map((o) => {
                    const lineLeft = Math.max(0, (o.contentStartX || 0) - 4);
                    return (
                      <div
                        key={`line-${o.key}`}
                        className="conditional-gutter-item"
                        style={{ transform: `translate3d(0, ${o.top}px, 0)`, height: o.height }}
                      >
                        <div
                          className="conditional-gutter-line"
                          style={{ left: lineLeft }}
                          data-cond-range="1"
                          data-cond-from={o.from}
                          data-cond-to={o.to}
                          onMouseEnter={() => setHoveredConditionalRange(o.groupId)}
                          onMouseLeave={() => setHoveredConditionalRange(null)}
                        />
                      </div>
                    );
                  });

                  // 2) Бейдж — один на строку (IF^N), чтобы не слипались несколько бейджей.
                  const BADGE_HEIGHT = 16;
                  const popoverWidth = 520;
                  const rawBadges = Array.from(byLine.entries())
                    .sort((a, b) => a[0] - b[0])
                    .map(([lineTop, list]) => {
                      const items = list.slice().sort((a, b) => a.from - b.from);
                      const first = items[0];
                      const groupTop = items.reduce((min, it) => Math.min(min, Number(it.top || 0)), Number(first.top || 0));
                      const groupBottom = items.reduce((max, it) => Math.max(max, Number(it.top || 0) + Number(it.height || 0)), Number(first.top || 0) + Number(first.height || 0));
                      // Высота "строки" равна высоте линии (без искусственного минимума),
                      // иначе центр бейджа может визуально уехать ниже центра строки.
                      const groupHeight = Math.max(1, groupBottom - groupTop);
                      const count = items.length;
                      // Бейдж ставим в левое "поле" редактора (якорь рассчитывается в updateConditionalGutter)
                      const left = Number(first.badgeAnchorX || 0);
                      const lineLeft = Math.max(0, (first.contentStartX || 0) - 4);
                      const popoverLeft = Math.min(
                        Math.max(8, lineLeft + 10),
                        Math.max(8, (first.pageWidth || 0) - popoverWidth - 8)
                      );
                      const groupKey = `condline-${items.map((i) => i.groupId || i.key).join('|')}`;

                      const badgeTop = groupTop + (groupHeight / 2) - (BADGE_HEIGHT / 2);

                      return {
                        key: groupKey,
                        items,
                        first,
                        count,
                        left,
                        badgeTop,
                        popoverLeft,
                        lineLeft,
                      };
                    });

                  const mergedBadges = [];
                  rawBadges.forEach((badge) => {
                    const last = mergedBadges[mergedBadges.length - 1];
                    if (!last) {
                      mergedBadges.push(badge);
                      return;
                    }

                    const overlap = (last.badgeTop + BADGE_HEIGHT) - badge.badgeTop;
                    if (overlap > (BADGE_HEIGHT / 2)) {
                      const mergedItems = [...last.items, ...badge.items].sort((a, b) => a.from - b.from);
                      mergedBadges[mergedBadges.length - 1] = {
                        ...last,
                        items: mergedItems,
                        count: (last.count || 0) + (badge.count || 0),
                        key: `condline-${mergedItems.map((i) => i.groupId || i.key).join('|')}`,
                      };
                      return;
                    }

                    mergedBadges.push(badge);
                  });

                  const badges = mergedBadges.map((badge) => {
                      const { key: groupKey, items, first, count, left, badgeTop, popoverLeft } = badge;

                      return (
                        <div
                          key={groupKey}
                          className="conditional-gutter-item"
                          style={{ transform: `translate3d(0, ${badgeTop}px, 0)`, height: BADGE_HEIGHT }}
                        >
                          <div
	                            className="conditional-gutter-badge"
	                            style={{ left, top: 0, transform: 'none' }}
	                            onClick={(e) => {
	                              e.preventDefault();
	                              e.stopPropagation();
	                              // Подсвечиваем выбранный блок, чтобы были видны границы в тексте
	                              // (особенно важно, когда на одной строке несколько IF).
	                              setSelectedConditionalRange(first.groupId);
	                              setActiveConditionalGutter((prev) => {
	                                if (prev?.key === groupKey) return null;

	                                const badgeRect = e.currentTarget?.getBoundingClientRect?.();
	                                const a4Rect = a4ContainerRef.current?.getBoundingClientRect?.();
	                                const vw = window.innerWidth || 0;
	                                const vh = window.innerHeight || 0;
	                                const pad = 8;

                                const toolbarEl = wrapperRef.current?.querySelector?.('.tiptap-toolbar');
                                const toolbarRect = toolbarEl?.getBoundingClientRect?.();
                                const minTop = Math.max(pad, (toolbarRect?.bottom || 0) + pad);

	                                const a4Left = Number(a4Rect?.left ?? pad);
	                                const a4Right = Number(a4Rect?.right ?? (vw - pad));
	                                const boundsLeft = pad;
	                                const boundsRight = vw - pad;
	                                const maxLeft = Math.max(boundsLeft, boundsRight - popoverWidth);

	                                // По умолчанию показываем ПОПОВЕР справа от бейджа.
	                                const preferredRight = badgeRect ? (badgeRect.right + 10) : popoverLeft;
	                                const preferredLeft = badgeRect ? (badgeRect.left - popoverWidth - 10) : (popoverLeft - popoverWidth - 10);

	                                let initialLeft;
	                                // Сначала пробуем разместить поповер ВНЕ области текста (справа/слева от A4),
	                                // чтобы он не перекрывал выбранный блок.
	                                const spaceRight = Math.max(0, boundsRight - (a4Right + pad));
	                                const spaceLeft = Math.max(0, (a4Left - pad) - boundsLeft);
	                                if (spaceRight >= popoverWidth) {
	                                  initialLeft = a4Right + pad;
	                                } else if (spaceLeft >= popoverWidth) {
	                                  initialLeft = (a4Left - pad) - popoverWidth;
	                                } else if (preferredRight + popoverWidth <= boundsRight) {
	                                  initialLeft = preferredRight;
	                                } else if (preferredLeft >= boundsLeft) {
	                                  initialLeft = preferredLeft;
	                                } else {
	                                  initialLeft = Math.min(Math.max(boundsLeft, preferredRight), maxLeft);
	                                }

	                                const initialTopFromRect = badgeRect
	                                  ? (badgeRect.top - 6)
	                                  : (minTop + 8);
                                const initialTop = Math.min(
                                  Math.max(minTop, initialTopFromRect),
                                  Math.max(minTop, vh - 160)
                                );

                                return {
                                  key: groupKey,
                                  items,
                                  first,
                                  count,
                                  position: { left: initialLeft, top: initialTop },
                                };
                              });
                            }}
                            role="button"
                            tabIndex={0}
                            title={count > 1 ? `IF: ${count} условий` : (first?.condition ? `IF: ${formatConditionForTooltip(first.condition)}` : 'IF')}
                          >
                            IF{count > 1 ? <sup className="conditional-gutter-badge-count">{count}</sup> : null}
                          </div>
                        </div>
                      );
                    });

                  return (
                    <>
                      {lines}
                      {badges}
                    </>
                  );
                })()}
              </div>
            )}
            <div className="docx-content-wrapper">
              <EditorContent
                editor={editor}
                className={`tiptap-editor ${mode === 'preview' ? 'tiptap-preview' : ''}`}
              />
            </div>
            {bottomInlineSlot ? (
              <div className="a4-inline-slot a4-inline-slot--bottom">
                {bottomInlineSlot}
              </div>
            ) : null}
          </div>
        </div>

      </div>

	      {enableConditionTools && activeConditionalGutter && typeof document !== 'undefined' ? createPortal(
        <div
          ref={conditionalPopoverRef}
          className="conditional-gutter-popover"
          style={{
            left: activeConditionalGutter.position?.left || 8,
            top: activeConditionalGutter.position?.top || 8,
          }}
          onMouseLeave={() => setHoveredConditionalRange(null)}
        >
          <div className="conditional-gutter-popover-header">
            <div className="conditional-gutter-popover-title">
              {activeConditionalGutter.count > 1 ? 'Условия на строке:' : 'Условие:'}
            </div>
	            <button
	              type="button"
	              className="conditional-gutter-popover-close"
	              onClick={() => {
	                setSelectedConditionalRange(null);
	                setActiveConditionalGutter(null);
	              }}
	              title="Закрыть"
	            >
	              ×
	            </button>
          </div>

          {activeConditionalGutter.count === 1 ? (
            <>
              <div className="conditional-gutter-popover-condition">{formatConditionForPopover(activeConditionalGutter.first?.condition) || '(не задано)'}</div>
              <div className="conditional-gutter-popover-actions">
                <button type="button" onClick={() => editConditional(activeConditionalGutter.first.from, activeConditionalGutter.first.groupId)}>Изменить</button>
                <button type="button" className="danger" onClick={() => deleteConditional(activeConditionalGutter.first.from, activeConditionalGutter.first.groupId)}>Удалить</button>
              </div>
            </>
          ) : (
            <div className="conditional-gutter-popover-list">
              {(activeConditionalGutter.items || []).map((it, idx) => (
                <div
                  key={`${activeConditionalGutter.key}-${it.key}`}
                  className={`conditional-gutter-popover-row ${selectedConditionalGroupId && String(it.groupId) === String(selectedConditionalGroupId) ? 'is-selected' : ''}`}
                  onMouseEnter={() => setHoveredConditionalRange(it.groupId)}
                  onMouseLeave={() => setHoveredConditionalRange(null)}
                  onClick={() => setSelectedConditionalRange(it.groupId)}
                >
                  <div className="conditional-gutter-popover-row-index">{idx + 1}.</div>
                  <div className="conditional-gutter-popover-row-condition">{formatConditionForPopover(it.condition) || '(не задано)'}</div>
                  <div className="conditional-gutter-popover-row-actions">
                    <button type="button" onClick={() => editConditional(it.from, it.groupId)}>Изменить</button>
                    <button type="button" className="danger" onClick={() => deleteConditional(it.from, it.groupId)}>Удалить</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>,
        document.body
      ) : null}

      {loopEditorOpen && (
        <div
          className="condition-editor-overlay"
          onMouseDown={() => setLoopEditorOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div className="condition-editor-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="condition-editor-header">
              <div className="condition-editor-title">Цикл</div>
              <button
                type="button"
                className="condition-editor-close"
                onClick={closeLoopEditor}
                title="Закрыть"
              >
                ×
              </button>
            </div>
            <div className="condition-editor-body">
              <div className="loop-editor-field">
                <label className="loop-editor-label">Список для перебора</label>
                <select
                  className="loop-editor-select"
                  value={loopEditorState.listCode || ''}
                  onChange={(e) => updateLoopList(e.target.value)}
                  ref={loopEditorSelectRef}
                >
                  <option value="">Выберите список...</option>
                  {LOOP_LIST_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="loop-editor-field">
                <label className="loop-editor-label">Итоговое выражение</label>
                <input
                  className="loop-editor-input"
                  type="text"
                  value={loopEditorState.loop || ''}
                  readOnly
                  placeholder="item in ..."
                  ref={loopEditorInputRef}
                />
              </div>
              <div className="loop-editor-actions">
                <button type="button" onClick={() => moveLoopBoundary('expandUp')} disabled={!loopEditorState.actions?.canExpandUp}>Расширить вверх</button>
                <button type="button" onClick={() => moveLoopBoundary('expandDown')} disabled={!loopEditorState.actions?.canExpandDown}>Расширить вниз</button>
                <button type="button" onClick={() => moveLoopBoundary('shrinkUp')} disabled={!loopEditorState.actions?.canShrinkUp}>Сузить сверху</button>
                <button type="button" onClick={() => moveLoopBoundary('shrinkDown')} disabled={!loopEditorState.actions?.canShrinkDown}>Сузить снизу</button>
              </div>
              <div className="loop-editor-footer">
                <button type="button" className="danger" onClick={unwrapLoop}>Удалить цикл</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {conditionEditorOpen && (
        <div
          className="condition-editor-overlay"
          onMouseDown={() => {
            restoreConditionalGutterOnCloseRef.current = true;
            setConditionEditorOpen(false);
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="condition-editor-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="condition-editor-header">
              <div className="condition-editor-title">{conditionEditorTitle}</div>
              <button
                type="button"
                className="condition-editor-close"
                onClick={() => {
                  restoreConditionalGutterOnCloseRef.current = true;
                  setConditionEditorOpen(false);
                }}
                title="Закрыть"
              >
                ×
              </button>
            </div>

            <div className="condition-editor-body">
              <div className="condition-editor-presets">
                <div className="condition-editor-presets-title">Пресеты (быстрые варианты):</div>
                <div className="condition-editor-presets-tabs">
                  <button
                    type="button"
                    className={`condition-presets-tab ${conditionPresetsTab === 'global' ? 'active' : ''}`}
                    onClick={() => {
                      setConditionPresetsTab('global');
                      setPresetDraft({ id: null, name: '', is_global: isAdmin });
                    }}
                  >
                    Общие
                  </button>
                  <button
                    type="button"
                    className={`condition-presets-tab ${conditionPresetsTab === 'mine' ? 'active' : ''}`}
                    onClick={() => {
                      setConditionPresetsTab('mine');
                      setPresetDraft({ id: null, name: '', is_global: false });
                    }}
                  >
                    Мои
                  </button>
                </div>

                {(conditionPresetsTab === 'mine' || isAdmin) ? (
                  <div className="condition-editor-presets-form">
                    <input
                      type="text"
                      className="condition-presets-input"
                      placeholder={presetDraft.id ? 'Название пресета (редактирование)' : 'Название нового пресета'}
                      value={presetDraft.name}
                      onChange={(e) => setPresetDraft((s) => ({ ...s, name: e.target.value }))}
                    />
                    <div className="condition-presets-kind">
                      {conditionPresetsTab === 'global' ? 'Общий' : 'Мой'}
                    </div>
                    <button type="button" className="condition-presets-save" onClick={savePresetFromCurrentCondition}>
                      {presetDraft.id ? 'Сохранить' : 'Добавить'}
                    </button>
                    {presetDraft.id && (
                      <button
                        type="button"
                        className="condition-presets-cancel"
                        onClick={() => setPresetDraft({ id: null, name: '', is_global: conditionPresetsTab === 'global' && isAdmin })}
                      >
                        Отмена
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="condition-presets-status">
                    Общие пресеты может создавать и изменять только администратор
                  </div>
                )}

                {conditionPresetsLoading && (
                  <div className="condition-presets-status">Загрузка пресетов…</div>
                )}
                {!!conditionPresetsError && (
                  <div className="condition-presets-status error">{conditionPresetsError}</div>
                )}

                {!conditionPresetsLoading && (
                  <div className="condition-editor-presets-list">
                    {(conditionPresetsTab === 'global'
                      ? conditionPresets.filter((p) => !!p.is_global)
                      : conditionPresets.filter((p) => !p.is_global)
                    ).map((p) => {
                      const canManage = isAdmin || (!p.is_global);
                      return (
                        <div key={p.id} className="condition-preset-item">
                          <button
                            type="button"
                            className={`condition-chip ${canManage ? 'condition-chip--manageable' : ''}`}
                            onClick={() => {
                              setConditionEditorState(parseConditionToBuilder(p.expression));
                              setPresetDraft((s) => ({ ...s, id: null })); // не мешаем редактированию условия
                            }}
                            title={p.expression}
                          >
                            {p.name}
                          </button>
                          {canManage && (
                            <div className="condition-preset-icons" aria-hidden="false">
                              <button
                                type="button"
                                className="card-action-btn card-action-btn--edit condition-preset-action"
                                title="Изменить"
                                aria-label="Изменить пресет"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  editPreset(p);
                                }}
                              >
                                ✎
                              </button>
                              <button
                                type="button"
                                className="card-action-btn card-action-btn--delete condition-preset-action"
                                title="Удалить"
                                aria-label="Удалить пресет"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  deletePreset(p);
                                }}
                              >
                                🗑️
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {(conditionPresetsTab === 'global'
                      ? conditionPresets.filter((p) => !!p.is_global)
                      : conditionPresets.filter((p) => !p.is_global)
                    ).length === 0 && !conditionPresetsError && (
                      <div className="condition-presets-status">
                        {conditionPresetsTab === 'global'
                          ? 'Общие пресеты не настроены'
                          : 'У вас пока нет пресетов'}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="condition-editor-mode">
                <div className="condition-editor-toggle">
                  <span>Расширенный режим (для программиста)</span>
                  <input
                    type="checkbox"
                    aria-label="Расширенный режим (для программиста)"
                    checked={conditionEditorState.mode === 'advanced'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setConditionEditorState((s) => ({
                          ...s,
                          mode: 'advanced',
                          raw: sanitizeConditionText(s.raw || buildConditionFromBuilder(s)),
                        }));
                      } else {
                        setConditionEditorState((s) => parseConditionToBuilder(s.raw));
                      }
                    }}
                  />
                </div>
              </div>

              {conditionEditorState.mode === 'advanced' ? (
                <div className="condition-editor-advanced">
                  <textarea
                    className="condition-editor-textarea"
                    value={conditionEditorState.raw || ''}
                    onChange={(e) => setConditionEditorState((s) => ({ ...s, raw: e.target.value }))}
                    placeholder="Например: debtor_type_code == 'legal' && not_empty(debtor_case_act_date)"
                    rows={3}
                  />
                  <div className="condition-editor-help">
                    Примеры: <code>empty(x)</code>, <code>not_empty(x)</code>, <code>contains(x, 'текст')</code>, <code>x == 'значение'</code>, <code>x == y</code>
                  </div>
                </div>
              ) : (
                <div className="condition-editor-builder">
	                  <div className="condition-editor-join">
	                    <span>Показывать блок, если</span>
	                    <select
	                      value={conditionEditorState.join === '||' ? 'any' : 'all'}
	                      onChange={(e) => setConditionEditorState((s) => {
	                        const nextJoin = e.target.value === 'any' ? '||' : '&&';
	                        const hasGroups = normalizeBuilderClauses(s.clauses).some((c) => c.kind === 'group');
	                        // Для сложных условий (с группами ИЛИ) переводим в advanced, чтобы не терять смысл.
	                        if (hasGroups && nextJoin === '||') {
	                          return {
	                            ...s,
	                            mode: 'advanced',
	                            raw: sanitizeConditionText(buildConditionFromBuilder(s)),
	                            join: '||',
	                            clauses: [],
	                          };
	                        }
	                        return { ...s, join: nextJoin };
	                      })}
	                    >
	                      <option value="all">все условия выполняются</option>
	                      <option value="any">выполняется любое условие</option>
	                    </select>
	                  </div>

	                  <div className="condition-editor-rows">
	                    {(() => {
	                      const items = normalizeBuilderClauses(conditionEditorState.clauses);

	                      const renderClauseRow = (clause, idxPath, key) => {
	                        const varMeta = CONDITION_VARIABLES.find((v) => v.code === clause.variable);
	                        const op = clause.op || 'eq';
	                        const isKnownVar = CONDITION_VARIABLES.some((v) => v.code === clause.variable);
	                        const showValue = op === 'eq' || op === 'neq' || op === 'contains' || op === 'eq_var' || op === 'neq_var';
	                        const isEnum = varMeta?.type === 'enum' && (op === 'eq' || op === 'neq');
	                        const valuePlaceholder = op === 'eq_var' || op === 'neq_var' ? 'Другая переменная (например: debtor_case_act_date)' : 'Значение';

	                        const updateAtPath = (updater) => {
	                          setConditionEditorState((s) => {
	                            const nextItems = normalizeBuilderClauses(s.clauses);
	                            const [rootIdx, innerIdx] = idxPath;
	                            if (innerIdx === null || innerIdx === undefined) {
	                              const prev = nextItems[rootIdx] || {};
	                              nextItems[rootIdx] = updater(prev);
	                            } else {
	                              const grp = nextItems[rootIdx];
	                              if (!grp || grp.kind !== 'group') return s;
	                              const inner = Array.isArray(grp.clauses) ? grp.clauses.slice() : [];
	                              const prev = inner[innerIdx] || {};
	                              inner[innerIdx] = updater(prev);
	                              nextItems[rootIdx] = { ...grp, clauses: inner.length ? inner : [{ kind: 'clause', variable: 'debtor_type_code', op: 'eq', value: 'legal' }] };
	                            }
	                            return { ...s, clauses: nextItems.length ? nextItems : [{ kind: 'clause', variable: 'debtor_type_code', op: 'eq', value: 'legal' }] };
	                          });
	                        };

	                        const removeAtPath = () => {
	                          setConditionEditorState((s) => {
	                            const nextItems = normalizeBuilderClauses(s.clauses);
	                            const [rootIdx, innerIdx] = idxPath;
	                            if (innerIdx === null || innerIdx === undefined) {
	                              nextItems.splice(rootIdx, 1);
	                              return { ...s, clauses: nextItems.length ? nextItems : [{ kind: 'clause', variable: 'debtor_type_code', op: 'eq', value: 'legal' }] };
	                            }
	                            const grp = nextItems[rootIdx];
	                            if (!grp || grp.kind !== 'group') return s;
	                            const inner = Array.isArray(grp.clauses) ? grp.clauses.slice() : [];
	                            inner.splice(innerIdx, 1);
	                            if (!inner.length) {
	                              // удаляем пустую группу целиком
	                              nextItems.splice(rootIdx, 1);
	                            } else {
	                              nextItems[rootIdx] = { ...grp, clauses: inner };
	                            }
	                            return { ...s, clauses: nextItems.length ? nextItems : [{ kind: 'clause', variable: 'debtor_type_code', op: 'eq', value: 'legal' }] };
	                          });
	                        };

	                        return (
	                          <div key={key} className="condition-editor-row">
	                            <select
	                              value={clause.variable}
	                              onChange={(e) => {
	                                const nextVar = e.target.value;
	                                updateAtPath((prev) => {
	                                  const nextOp = String(prev.op || 'eq');
	                                  const nextValRaw = String(prev.value || '');
	                                  const shouldFixEnum = (nextOp === 'eq' || nextOp === 'neq') && !isEnumValueAllowed(nextVar, nextValRaw);
	                                  return { ...prev, kind: 'clause', variable: nextVar, value: shouldFixEnum ? getDefaultEnumValue(nextVar) : prev.value };
	                                });
	                              }}
	                            >
	                              {CONDITION_VARIABLES.map((v) => (
	                                <option key={v.code} value={v.code}>{v.label}</option>
	                              ))}
	                              {!isKnownVar && <option value={clause.variable}>{getConditionVariableLabel(clause.variable)}</option>}
	                            </select>

	                            <select
	                              value={op}
	                              onChange={(e) => {
	                                const nextOp = e.target.value;
	                                updateAtPath((prev) => {
	                                  const varCode = String(prev.variable || '');
	                                  let nextVal = prev.value;
	                                  if (nextOp === 'empty' || nextOp === 'not_empty') nextVal = '';
	                                  if ((nextOp === 'eq' || nextOp === 'neq') && !isEnumValueAllowed(varCode, String(nextVal || ''))) {
	                                    nextVal = getDefaultEnumValue(varCode);
	                                  }
	                                  return { ...prev, kind: 'clause', op: nextOp, value: nextVal };
	                                });
	                              }}
	                            >
	                              <option value="eq">равно</option>
	                              <option value="neq">не равно</option>
	                              <option value="empty">не заполнено</option>
	                              <option value="not_empty">заполнено</option>
	                              <option value="contains">содержит</option>
	                              <option value="eq_var">равно переменной</option>
	                              <option value="neq_var">не равно переменной</option>
	                            </select>

	                            {showValue && (
	                              isEnum ? (
	                                <select
	                                  value={clause.value || ''}
	                                  onChange={(e) => {
	                                    const nextVal = e.target.value;
	                                    updateAtPath((prev) => ({ ...prev, kind: 'clause', value: nextVal }));
	                                  }}
	                                >
	                                  {(varMeta?.values || []).map((v) => (
	                                    <option key={v.value} value={v.value}>{v.label}</option>
	                                  ))}
	                                </select>
	                              ) : (
	                                <input
	                                  type="text"
	                                  value={clause.value || ''}
	                                  placeholder={valuePlaceholder}
	                                  onChange={(e) => {
	                                    const nextVal = e.target.value;
	                                    updateAtPath((prev) => ({ ...prev, kind: 'clause', value: nextVal }));
	                                  }}
	                                />
	                              )
	                            )}

	                            <button
	                              type="button"
	                              className="condition-editor-remove"
	                              title="Удалить условие"
	                              onClick={removeAtPath}
	                            >
	                              ×
	                            </button>
	                          </div>
	                        );
	                      };

	                      return (
	                        <>
	                          {items.map((it, idx) => {
	                            if (it.kind === 'group') {
	                              return (
	                                <div key={`grp-${idx}`} className="condition-editor-group">
	                                  <div className="condition-editor-group-header">
	                                    <span>Группа ИЛИ</span>
	                                    <button
	                                      type="button"
	                                      className="condition-editor-remove"
	                                      title="Удалить группу"
	                                      onClick={() => setConditionEditorState((s) => {
	                                        const nextItems = normalizeBuilderClauses(s.clauses);
	                                        nextItems.splice(idx, 1);
	                                        return { ...s, clauses: nextItems.length ? nextItems : [{ kind: 'clause', variable: 'debtor_type_code', op: 'eq', value: 'legal' }] };
	                                      })}
	                                    >
	                                      ×
	                                    </button>
	                                  </div>
	                                  <div className="condition-editor-group-rows">
	                                    {(it.clauses || []).map((c, j) => renderClauseRow(c, [idx, j], `grp-${idx}-${j}`))}
	                                  </div>
	                                  <button
	                                    type="button"
	                                    className="condition-editor-add condition-editor-add--inline"
	                                    onClick={() => setConditionEditorState((s) => {
	                                      const nextItems = normalizeBuilderClauses(s.clauses);
	                                      const grp = nextItems[idx];
	                                      if (!grp || grp.kind !== 'group') return s;
	                                      const inner = Array.isArray(grp.clauses) ? grp.clauses.slice() : [];
	                                      inner.push({ kind: 'clause', variable: 'debtor_type_code', op: 'eq', value: 'legal' });
	                                      nextItems[idx] = { ...grp, clauses: inner };
	                                      return { ...s, clauses: nextItems };
	                                    })}
	                                  >
	                                    + Добавить в группу
	                                  </button>
	                                </div>
	                              );
	                            }
	                            return renderClauseRow(it, [idx, null], `row-${idx}`);
	                          })}
	                        </>
	                      );
	                    })()}
	                  </div>

	                  <button
	                    type="button"
	                    className="condition-editor-add"
	                    onClick={() => setConditionEditorState((s) => ({
	                      ...s,
	                      clauses: [...normalizeBuilderClauses(s.clauses), { kind: 'clause', variable: 'debtor_type_code', op: 'eq', value: 'legal' }],
	                    }))}
	                  >
	                    + Добавить условие
	                  </button>

	                  <button
	                    type="button"
	                    className="condition-editor-add"
	                    onClick={() => setConditionEditorState((s) => ({
	                      ...s,
	                      join: '&&',
	                      clauses: [...normalizeBuilderClauses(s.clauses), { kind: 'group', join: '||', clauses: [{ kind: 'clause', variable: 'debtor_type_code', op: 'eq', value: 'legal' }] }],
	                    }))}
	                  >
	                    + Добавить группу ИЛИ
	                  </button>
	                </div>
	              )}

              <div className="condition-editor-preview">
                <div className="condition-editor-preview-title">Итоговое условие:</div>
                <code className="condition-editor-preview-code">
                  {conditionEditorState.mode === 'advanced'
                    ? sanitizeConditionText(conditionEditorState.raw)
                    : buildConditionFromBuilder(conditionEditorState) || '(пусто)'}
                </code>
              </div>
            </div>

              <div className="condition-editor-footer">
              <button type="button" className="btn-secondary" onClick={() => {
                restoreConditionalGutterOnCloseRef.current = true;
                setConditionEditorOpen(false);
              }}>
                Отмена
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  const expr = conditionEditorState.mode === 'advanced'
                    ? sanitizeConditionText(conditionEditorState.raw)
                    : buildConditionFromBuilder(conditionEditorState);
                  const trimmed = String(expr || '').trim();
                  if (!trimmed) return;
                  restoreConditionalGutterOnCloseRef.current = true;
                  setConditionEditorOpen(false);
                  const apply = conditionEditorApplyRef.current;
                  if (apply) apply(trimmed);
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Кнопка показа переменных в полноэкранном режиме - вынесена из скролл-контейнера */}
      {fullscreen && (
        <>
          {showVariables && (
            <button
              className={`variables-toggle ${shouldShowVariables ? 'is-active' : ''}`}
              onClick={() => setShowVariablesLocal(!showVariablesLocal)}
              title={shouldShowVariables ? 'Скрыть переменные' : 'Показать переменные'}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                {shouldShowVariables ? (
                  // Простая стрелочка вправо (закрыть панель)
                  <path d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
                ) : (
                  // Простая стрелочка влево (открыть панель)
                  <path d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z" />
                )}
              </svg>
            </button>
          )}

          {/* Панель переменных */}
          {showVariables && (
            <div className={`variables-panel-fullscreen ${shouldShowVariables ? 'show' : ''} ${showVariableSearch ? 'with-search' : ''}`}>
              <div className="panel-header">
                <h4>Переменные шаблона</h4>
                <div className="panel-header-actions">
                  <button
                    type="button"
                    className={`icon-button ${showVariableSearch ? 'is-active' : ''}`}
                    onClick={() => {
                      setShowVariableSearch(prev => {
                        const next = !prev;
                        if (!next) {
                          setVariableSearch('');
                        }
                        return next;
                      });
                    }}
                    title={showVariableSearch ? 'Скрыть поиск' : 'Искать переменные'}
                    aria-label={showVariableSearch ? 'Скрыть поиск переменных' : 'Открыть поиск переменных'}
                  >
                    🔍
                  </button>
                  <button
                    onClick={() => {
                      setShowVariablesLocal(false);
                      setShowVariableSearch(false);
                      setVariableSearch('');
                    }}
                    className="close-panel-btn"
                    title="Закрыть"
                  >
                    &times;
                  </button>
                </div>
              </div>

              {showVariableSearch && (
                <div className="variables-search is-open">
                  <input
                    ref={variableSearchInputRef}
                    type="text"
                    className="variables-search-input"
                    placeholder="Поиск..."
                    value={variableSearch}
                    onChange={(e) => setVariableSearch(e.target.value)}
                  />
                </div>
              )}

              <div className="variables-groups">
                {filteredVariableGroups.length === 0 ? (
                  <div className="variables-empty">Ничего не найдено</div>
                ) : (
                  filteredVariableGroups.map(({ name: groupName, variables }) => (
                    <div key={groupName} className="variable-group">
                      <div className="group-title">{groupName}</div>
                      <div className="variables-grid">
                        {variables.map(variable => {
                          const description = availableVariables?.[variable] || variable;
                          const variableCode = `{{${variable}}}`;
                          return (
                            <div
                              key={variable}
                              className="variable-chip"
                              onClick={(e) => handleVariableClick(variable, e)}
                              title={`${variableCode} — кликните для вставки`}
                            >
                              <span className="variable-description">
                                {highlightVariableMatch(description)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div
                className="variables-hint"
                ref={(el) => {
                  if (el) {
                    // Удаляем старый обработчик если есть
                    el.removeEventListener('wheel', el._wheelHandler);

                    // Создаём новый обработчик
                    el._wheelHandler = (e) => {
                      // Перенаправляем прокрутку на контейнер с переменными
                      e.preventDefault();
                      e.stopPropagation();

                      // Находим контейнер с переменными
                      const variablesContainer = document.querySelector('.variables-panel-fullscreen .variables-groups');
                      if (variablesContainer) {
                        // Прокручиваем контейнер с переменными
                        variablesContainer.scrollTop += e.deltaY;
                      }
                    };

                    // Добавляем обработчик с passive: false
                    el.addEventListener('wheel', el._wheelHandler, { passive: false });
                  }
                }}
              >
                <p><strong>Как работать с переменными:</strong></p>
                <ul>
                  <li>Поставьте курсор и кликните по переменной — она вставится в текст</li>
                  <li>ФИО — появится меню выбора падежа</li>
                  <li>Даты — можно выбрать формат отображения</li>
                  <li>Подписи — подсказка по переменной видна при наведении</li>
                </ul>
                <p><strong>Согласование по роду:</strong></p>
                <ul>
                  <li>Базово: <span className="gender-agreement-highlight"><span className="gender-agreement-brace">{'{'}</span><span className="gender-agreement-part gender-male">муж</span><span className="gender-agreement-sep">|</span><span className="gender-agreement-part gender-female">жен</span><span className="gender-agreement-brace">{'}'}</span></span>, пример <span className="gender-agreement-highlight"><span className="gender-agreement-brace">{'{'}</span><span className="gender-agreement-part gender-male">действующего</span><span className="gender-agreement-sep">|</span><span className="gender-agreement-part gender-female">действующей</span><span className="gender-agreement-brace">{'}'}</span></span></li>
                  <li>Средний род для юрлиц: <span className="gender-agreement-highlight"><span className="gender-agreement-brace">{'{'}</span><span className="gender-agreement-part gender-male">именуемый</span><span className="gender-agreement-sep">|</span><span className="gender-agreement-part gender-female">именуемая</span><span className="gender-agreement-sep">|</span><span className="gender-agreement-part gender-neutral">именуемое</span><span className="gender-agreement-sep">:</span><span className="gender-agreement-suffix">organizer_name</span><span className="gender-agreement-brace">{'}'}</span></span></li>
                  <li>Явная привязка: <span className="gender-agreement-highlight"><span className="gender-agreement-brace">{'{'}</span><span className="gender-agreement-part gender-male">его</span><span className="gender-agreement-sep">|</span><span className="gender-agreement-part gender-female">её</span><span className="gender-agreement-sep">|</span><span className="gender-agreement-part gender-neutral">его</span><span className="gender-agreement-sep">:</span><span className="gender-agreement-suffix">organizer_signatory_r</span><span className="gender-agreement-brace">{'}'}</span></span></li>
                </ul>
              </div>
            </div>
          )}
        </>
      )}

    </div>
  );
});

TiptapEditor.displayName = 'TiptapEditor';

export default TiptapEditor;
